import { SUBJECTS, subjectLabel, quranSubjectIds, arabicSubjectIds } from "./shared/subjects";
import { iconSvg } from "./shared/icons";
import { COUNTRY_TIMEZONES, findZone } from "./shared/timezones";

// ── Types ─────────────────────────────────────────────────────────────────────

interface User { id: string; name: string; email: string; role: "student" | "tutor" | "moderator" | "admin"; avatarUrl?: string | null; emailVerified?: boolean; theme?: string; language?: string; timeZone?: string }

interface TutorCard {
  userId: string; displayName: string; shortBio: string; country: string;
  profilePhotoUrl: string | null; subjectsTaught: string; isHafiz: boolean;
  hourlyRateUsd: number; averageRating: number | null; totalSessions: number;
  yearsExperience: number; teachingLanguages: string;
  ijazahs: Array<{ riwayah: string; verifiedByAdmin: boolean }>;
  availabilitySlots: Array<{ dayOfWeek: number }>;
  score?: number; matchReason?: string;
}

const parseMaybeJson = <T,>(value: unknown, fallback: T): T => {
  if (Array.isArray(value) || (value && typeof value === "object")) return value as T;
  if (typeof value !== "string") return fallback;
  try { return JSON.parse(value) as T; } catch { return fallback; }
};

// ── API helpers ───────────────────────────────────────────────────────────────

const token = (): string | null => null;

const api = async (method: string, path: string, body?: unknown): Promise<unknown> => {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const t = token();
  if (t) headers["Authorization"] = `Bearer ${t}`;
  const res = await fetch(path, { method, headers, credentials: "same-origin", body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
  return data;
};

const get = (path: string) => api("GET", path);
const post = (path: string, body: unknown) => api("POST", path, body);
const put = (path: string, body: unknown) => api("PUT", path, body);
const patch = (path: string, body: unknown) => api("PATCH", path, body);

// ── Auth state ────────────────────────────────────────────────────────────────

let currentUser: User | null = null;

const loadCurrentUser = async (): Promise<User | null> => {
  try {
    const data = await get("/api/auth/me") as { user: User };
    currentUser = data.user;
    return currentUser;
  } catch {
    currentUser = null;
    return null;
  }
};

const storeToken = (t: string, user: User) => {
  void t;
  currentUser = user;
};

const logout = async () => {
  try { await post("/api/auth/logout", {}); } catch { /* ignore */ }
  currentUser = null;
  window.location.href = "/";
};

// ── Nav auth area ─────────────────────────────────────────────────────────────

const updateNavAuth = (user: User | null) => {
  const area = document.getElementById("nav-auth");
  const mobileArea = document.getElementById("nav-mobile-auth");
  if (!area) return;
  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";

  if (!user) {
    area.innerHTML = `
      <a href="/login${isAr ? "?lang=ar" : ""}" class="btn btn-ghost btn-sm">${isAr ? "تسجيل الدخول" : "Login"}</a>
      <a href="/signup${isAr ? "?lang=ar" : ""}" class="btn btn-primary btn-sm">${isAr ? "إنشاء حساب" : "Sign Up"}</a>
    `;
    if (mobileArea) {
      mobileArea.innerHTML = `
        <a href="/login${isAr ? "?lang=ar" : ""}" class="nav-link">${isAr ? "تسجيل الدخول" : "Login"}</a>
        <a href="/signup${isAr ? "?lang=ar" : ""}" class="nav-link nav-mobile-signup">${isAr ? "إنشاء حساب" : "Sign Up"}</a>
      `;
    }
    return;
  }

  const dashUrl = user.role === "student" ? "/dashboard/student"
    : user.role === "tutor" ? "/dashboard/tutor"
    : "/dashboard/admin";
  const roleLabel = isAr
    ? (user.role === "admin" ? "مدير" : user.role === "moderator" ? "مشرف" : user.role === "tutor" ? "معلم" : "طالب")
    : user.role;

  area.innerHTML = `
    <a href="${dashUrl}" class="btn btn-ghost btn-sm nav-user-link">
      <span class="nav-user-avatar">${user.avatarUrl ? `<img src="${esc(user.avatarUrl)}" alt="" />` : esc(user.name.slice(0, 1).toUpperCase())}</span>
      <span class="nav-user-name">${esc(user.name)}</span>
      <span class="nav-user-role">${roleLabel}</span>
    </a>
    <button class="btn btn-outline btn-sm" id="nav-logout">${isAr ? "خروج" : "Logout"}</button>
  `;
  document.getElementById("nav-logout")?.addEventListener("click", logout);

  if (mobileArea) {
    mobileArea.innerHTML = `
      <a href="${dashUrl}" class="nav-link">
        <span class="nav-user-avatar" style="display:inline-flex;margin-inline-end:0.5rem">${user.avatarUrl ? `<img src="${esc(user.avatarUrl)}" alt="" />` : esc(user.name.slice(0, 1).toUpperCase())}</span>
        ${esc(user.name)} <small style="opacity:0.6;margin-inline-start:0.25rem">${roleLabel}</small>
      </a>
      <button class="nav-link" id="nav-mobile-logout" style="text-align:start;background:none;border:none;cursor:pointer;color:var(--danger)">${isAr ? "تسجيل الخروج" : "Logout"}</button>
    `;
    document.getElementById("nav-mobile-logout")?.addEventListener("click", logout);
  }
};

const initHome = async () => {
  const statsEl = document.getElementById("hero-stat-tutors");
  if (!statsEl) return;
  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";

  if (currentUser) {
    const dashUrl = currentUser.role === "student" ? "/dashboard/student"
      : currentUser.role === "tutor" ? "/dashboard/tutor"
      : "/dashboard/admin";
    const welcomeEl = document.getElementById("hero-welcome");
    const actionsEl = document.getElementById("hero-actions");
    if (welcomeEl && actionsEl) {
      actionsEl.classList.add("hidden");
      welcomeEl.classList.remove("hidden");
      welcomeEl.innerHTML = `
        <div class="hero-welcome-box">
          <p class="hero-welcome-greeting">${isAr ? `مرحباً بعودتك، ${esc(currentUser.name)}!` : `Welcome back, ${esc(currentUser.name)}!`}</p>
          <a href="${dashUrl}" class="btn btn-primary btn-lg">${isAr ? "الذهاب إلى لوحة التحكم" : "Go to dashboard"}</a>
        </div>
      `;
    }
  }

  try {
    type TopTutor = {
      userId: string; displayName: string; shortBio: string; country?: string;
      profilePhotoUrl: string | null; averageRating: number | null; totalSessions?: number;
      yearsExperience?: number; isHafiz?: boolean; hasTachingSanad?: boolean;
      ijazahs?: Array<{ riwayah: string; verifiedByAdmin: boolean }>;
      hourlyRateUsd?: number; subjectsTaught: Array<{ subjectId: string }>;
    };
    const [stats, top] = await Promise.all([
      get("/api/platform/stats") as Promise<{ verifiedTutors: number; subjects: number; happyStudents: number }>,
      get("/api/tutors/top") as Promise<{ tutors: TopTutor[] }>
    ]);
    setText("hero-stat-tutors", String(stats.verifiedTutors));
    setText("hero-stat-subjects", String(stats.subjects));
    setText("hero-stat-students", String(stats.happyStudents));
    document.querySelectorAll<HTMLElement>("[data-auth-hide]").forEach(el => el.classList.toggle("hidden", !!currentUser));
    const stack = document.getElementById("hero-top-tutors");
    if (stack) {
      stack.innerHTML = top.tutors.length > 0 ? top.tutors.map((t, i) => `
        <button class="hero-card${i ? " hero-card--offset" : ""}" data-tutor-index="${i}" title="${isAr ? "عرض أوراق الاعتماد" : "View credentials"}">
          ${t.profilePhotoUrl ? `<img class="hero-card-photo" src="${esc(t.profilePhotoUrl)}" alt="" />` : iconSvg("graduation", "hero-card-avatar")}
          <div class="hero-card-info">
            <strong>${esc(t.displayName)}</strong>
            <span>${esc(t.shortBio || subjectLabel(t.subjectsTaught[0]?.subjectId ?? "quran_recitation", lang))}</span>
          </div>
          <div class="hero-card-rating">${iconSvg("star", "inline-icon")} ${t.averageRating?.toFixed(1) ?? (isAr ? "جديد" : "New")}</div>
        </button>
      `).join("") : `<div class="hero-card"><div class="hero-card-info"><strong>${isAr ? "لم يتم اعتماد معلمين بعد" : "No approved tutors yet"}</strong><span>${isAr ? "سيظهر أفضل المعلمين هنا بعد الاعتماد" : "Top tutors will appear here after approval"}</span></div></div>`;

      // Clicking a tutor opens their credibility card.
      stack.querySelectorAll<HTMLButtonElement>("[data-tutor-index]").forEach(btn => btn.addEventListener("click", () => {
        const t = top.tutors[Number(btn.dataset.tutorIndex)];
        if (t) openCredibilityCard(t, lang);
      }));
    }
  } catch { /* non-fatal */ }
};

// Credibility card: the trust signals for one tutor, in a compact modal.
const openCredibilityCard = (t: {
  userId: string; displayName: string; shortBio?: string; country?: string;
  profilePhotoUrl?: string | null; averageRating?: number | null; totalSessions?: number;
  yearsExperience?: number; isHafiz?: boolean; hasTachingSanad?: boolean;
  ijazahs?: Array<{ riwayah: string; verifiedByAdmin: boolean }>;
  hourlyRateUsd?: number; subjectsTaught?: Array<{ subjectId: string }>;
}, lang: string) => {
  const isAr = lang === "ar";
  const verifiedIjazahs = (t.ijazahs ?? []).filter(i => i.verifiedByAdmin);
  const badges: string[] = [
    `<span class="cred-badge cred-badge--verified">${iconSvg("check", "btn-icon")} ${isAr ? "معلم مُعتمد" : "Verified tutor"}</span>`
  ];
  if (t.isHafiz) badges.push(`<span class="cred-badge cred-badge--hafiz">${iconSvg("star", "btn-icon")} ${isAr ? "حافظ للقرآن" : "Hafiz"}</span>`);
  if ((t.ijazahs ?? []).length) badges.push(`<span class="cred-badge cred-badge--ijazah">${iconSvg("scroll", "btn-icon")} ${isAr ? `إجازة (${t.ijazahs!.length})` : `Ijazah (${t.ijazahs!.length})`}${verifiedIjazahs.length ? ` ✓` : ""}</span>`);
  if (t.hasTachingSanad) badges.push(`<span class="cred-badge">${iconSvg("book-open", "btn-icon")} ${isAr ? "سند تدريس" : "Teaching sanad"}</span>`);
  if ((t.yearsExperience ?? 0) > 0) badges.push(`<span class="cred-badge">${iconSvg("graduation", "btn-icon")} ${t.yearsExperience} ${isAr ? "سنوات خبرة" : "yrs experience"}</span>`);

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal cred-modal" role="dialog" aria-modal="true">
      <div class="modal-head">
        <h2>${isAr ? "أوراق الاعتماد" : "Credentials"}</h2>
        <button class="modal-close" aria-label="${isAr ? "إغلاق" : "Close"}">${iconSvg("x", "btn-icon")}</button>
      </div>
      <div class="cred-hero">
        <div class="cred-photo">${t.profilePhotoUrl ? `<img src="${esc(t.profilePhotoUrl)}" alt="" />` : iconSvg("graduation", "cred-photo-icon")}</div>
        <div>
          <strong class="cred-name">${esc(t.displayName)}</strong>
          ${t.country ? `<span class="cred-country">${iconSvg("map-pin", "btn-icon")} ${esc(t.country)}</span>` : ""}
          ${t.shortBio ? `<p class="cred-bio">${esc(t.shortBio)}</p>` : ""}
        </div>
      </div>
      <div class="cred-badges">${badges.join("")}</div>
      <div class="cred-stats">
        <div><strong>${t.averageRating?.toFixed(1) ?? "—"}</strong><span>${isAr ? "التقييم" : "Rating"}</span></div>
        <div><strong>${t.totalSessions ?? 0}</strong><span>${isAr ? "درس مكتمل" : "Lessons taught"}</span></div>
        <div><strong>$${t.hourlyRateUsd ?? 0}</strong><span>${isAr ? "بالساعة" : "per hour"}</span></div>
      </div>
      <div class="modal-actions">
        <a class="btn btn-outline" href="/tutors/${esc(t.userId)}${isAr ? "?lang=ar" : ""}">${isAr ? "الملف الكامل" : "Full profile"}</a>
        <a class="btn btn-primary" href="/book/${esc(t.userId)}${isAr ? "?lang=ar" : ""}">${isAr ? "احجز درساً" : "Book a lesson"}</a>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  const close = () => backdrop.remove();
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
  backdrop.querySelector(".modal-close")?.addEventListener("click", close);
  document.addEventListener("keydown", function onKey(e) {
    if (e.key === "Escape") { close(); document.removeEventListener("keydown", onKey); }
  });
};

const esc = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
   .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// ── Hamburger nav ─────────────────────────────────────────────────────────────

const initNav = () => {
  const btn = document.getElementById("nav-hamburger");
  const menu = document.getElementById("nav-mobile-menu");
  if (!btn || !menu) return;

  const closeMenu = () => {
    menu.hidden = true;
    btn.setAttribute("aria-expanded", "false");
  };

  btn.addEventListener("click", () => {
    const open = menu.hidden;
    menu.hidden = !open;
    btn.setAttribute("aria-expanded", String(open));
  });

  menu.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (target.closest("a[href]")) closeMenu();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !menu.hidden) closeMenu();
  });
};

// ── Forgot password page ──────────────────────────────────────────────────────

const initForgotPassword = () => {
  const form = document.getElementById("forgot-form") as HTMLFormElement | null;
  if (!form) return;
  const emailEl = document.getElementById("forgot-email") as HTMLInputElement;
  const errEl   = document.getElementById("forgot-error") as HTMLElement;
  const submitEl = document.getElementById("forgot-submit") as HTMLButtonElement;
  const successEl = document.getElementById("forgot-success") as HTMLElement;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errEl.textContent = ""; errEl.classList.add("hidden");
    submitEl.disabled = true;
    try {
      await post("/api/auth/forgot-password", { email: emailEl.value.trim() });
      form.classList.add("hidden");
      successEl.classList.remove("hidden");
    } catch {
      errEl.textContent = document.documentElement.lang === "ar"
        ? "حدث خطأ. حاول مجدداً."
        : "Something went wrong. Please try again.";
      errEl.classList.remove("hidden");
      submitEl.disabled = false;
    }
  });
};

// ── Reset password page ───────────────────────────────────────────────────────

const initResetPassword = () => {
  const subtitleEl  = document.getElementById("reset-subtitle") as HTMLElement;
  const formEl      = document.getElementById("reset-form") as HTMLFormElement;
  const passEl      = document.getElementById("reset-password") as HTMLInputElement;
  const toggleEl    = document.getElementById("reset-pw-toggle") as HTMLButtonElement;
  const errEl       = document.getElementById("reset-error") as HTMLElement;
  const submitEl    = document.getElementById("reset-submit") as HTMLButtonElement;
  const successEl   = document.getElementById("reset-success") as HTMLElement;
  const invalidEl   = document.getElementById("reset-invalid") as HTMLElement;
  const isAr = document.documentElement.lang === "ar";

  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  if (!token) {
    subtitleEl.textContent = isAr ? "رابط غير صالح." : "Invalid link.";
    invalidEl.classList.remove("hidden");
    return;
  }

  // Show the form immediately — we only validate on submit
  subtitleEl.textContent = isAr ? "أدخل كلمة المرور الجديدة" : "Enter your new password";
  formEl.classList.remove("hidden");

  toggleEl?.addEventListener("click", () => {
    passEl.type = passEl.type === "password" ? "text" : "password";
  });

  formEl.addEventListener("submit", async (e) => {
    e.preventDefault();
    errEl.textContent = ""; errEl.classList.add("hidden");
    if (passEl.value.length < 8) {
      errEl.textContent = isAr ? "كلمة المرور يجب أن تكون 8 أحرف على الأقل." : "Password must be at least 8 characters.";
      errEl.classList.remove("hidden"); return;
    }
    submitEl.disabled = true;
    try {
      await post("/api/auth/reset-password", { token, password: passEl.value });
      formEl.classList.add("hidden");
      subtitleEl.textContent = "";
      successEl.classList.remove("hidden");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : (isAr ? "الرابط غير صالح أو انتهت صلاحيته." : "Link is invalid or expired.");
      errEl.textContent = msg;
      errEl.classList.remove("hidden");
      formEl.classList.add("hidden");
      invalidEl.classList.remove("hidden");
    }
  });
};

// ── Login page ────────────────────────────────────────────────────────────────

const initLogin = () => {
  const form = document.getElementById("login-form") as HTMLFormElement | null;
  if (!form) return;

  const emailEl = document.getElementById("login-email") as HTMLInputElement;
  const passEl = document.getElementById("login-password") as HTMLInputElement;
  const errEl = document.getElementById("login-error") as HTMLElement;
  const submitEl = document.getElementById("login-submit") as HTMLButtonElement;
  const toggleEl = document.getElementById("login-pw-toggle") as HTMLButtonElement;
  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";
  const showErr = (msg: string) => { errEl.textContent = msg; errEl.classList.remove("hidden"); };
  const hideErr = () => errEl.classList.add("hidden");
  const params = new URLSearchParams(window.location.search);
  const oauthError = params.get("oauth_error");
  if (oauthError) showErr(oauthError);

  toggleEl?.addEventListener("click", () => {
    passEl.type = passEl.type === "password" ? "text" : "password";
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    hideErr();
    submitEl.disabled = true;
    submitEl.textContent = isAr ? "جاري الدخول..." : "Logging in…";
    try {
      const data = await post("/api/auth/login", {
        email: emailEl.value.trim(),
        password: passEl.value
      }) as { token: string; user: User };
      storeToken(data.token, data.user);
      if (data.user.role === "student") {
        window.location.href = "/onboarding";
      } else if (data.user.role === "tutor") {
        window.location.href = "/dashboard/tutor";
      } else {
        window.location.href = "/dashboard/admin";
      }
    } catch (err) {
      showErr(err instanceof Error ? err.message : (isAr ? "حدث خطأ" : "An error occurred"));
      submitEl.disabled = false;
      submitEl.textContent = isAr ? "دخول" : "Login";
    }
  });
};

// ── Signup page ───────────────────────────────────────────────────────────────

const initSignup = () => {
  const form = document.getElementById("signup-form") as HTMLFormElement | null;
  if (!form) return;

  const nameEl = document.getElementById("signup-name") as HTMLInputElement;
  const emailEl = document.getElementById("signup-email") as HTMLInputElement;
  const passEl = document.getElementById("signup-password") as HTMLInputElement;
  const roleEl = document.getElementById("signup-role") as HTMLInputElement;
  const subjectsEl = document.getElementById("signup-subjects") as HTMLInputElement | null;
  const errEl = document.getElementById("signup-error") as HTMLElement;
  const submitEl = document.getElementById("signup-submit") as HTMLButtonElement;
  const toggleEl = document.getElementById("signup-pw-toggle") as HTMLButtonElement;
  const applicationEl = document.getElementById("signup-application") as HTMLElement | null;
  const accountEl = document.getElementById("signup-account") as HTMLElement | null;
  const continueEl = document.getElementById("signup-application-continue") as HTMLButtonElement | null;
  const skipEl = document.getElementById("signup-application-skip") as HTMLButtonElement | null;
  const selectedSubjects = new Set<string>();
  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";

  toggleEl?.addEventListener("click", () => {
    passEl.type = passEl.type === "password" ? "text" : "password";
  });

  const showAccountForm = () => {
    subjectsEl && (subjectsEl.value = JSON.stringify([...selectedSubjects]));
    applicationEl?.classList.add("hidden");
    accountEl?.classList.remove("hidden");
    accountEl?.classList.add("flow-in");
    nameEl.focus();
  };

  document.querySelectorAll<HTMLButtonElement>(".signup-subject-chip").forEach(btn => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.subject ?? "";
      if (!id) return;
      const selected = selectedSubjects.has(id);
      if (selected) selectedSubjects.delete(id);
      else selectedSubjects.add(id);
      btn.classList.toggle("signup-subject-chip--selected", !selected);
      btn.setAttribute("aria-pressed", String(!selected));
      subjectsEl && (subjectsEl.value = JSON.stringify([...selectedSubjects]));
    });
  });

  continueEl?.addEventListener("click", showAccountForm);
  skipEl?.addEventListener("click", () => {
    selectedSubjects.clear();
    document.querySelectorAll(".signup-subject-chip--selected").forEach(btn => {
      btn.classList.remove("signup-subject-chip--selected");
      btn.setAttribute("aria-pressed", "false");
    });
    showAccountForm();
  });

  const updateOauthLinks = () => {
    const role = roleEl.value === "tutor" ? "tutor" : "student";
    const returnTo = role === "tutor" ? "/dashboard/tutor" : "/onboarding";
    document.querySelectorAll<HTMLAnchorElement>(".signup-oauth").forEach(link => {
      const langQ = isAr ? "&lang=ar" : "";
      link.href = `/api/auth/oauth/google/start?role=${role}&returnTo=${encodeURIComponent(returnTo)}${langQ}`;
    });
  };

  document.querySelectorAll<HTMLButtonElement>(".role-btn").forEach(btn => {
    btn.addEventListener("click", () => {
      document.querySelectorAll(".role-btn").forEach(b => b.classList.remove("role-btn--active"));
      btn.classList.add("role-btn--active");
      roleEl.value = btn.dataset.role ?? "student";
      updateOauthLinks();
    });
  });
  updateOauthLinks();

  const showErr = (msg: string) => { errEl.textContent = msg; errEl.classList.remove("hidden"); };
  const hideErr = () => errEl.classList.add("hidden");
  const params = new URLSearchParams(window.location.search);
  const oauthError = params.get("oauth_error");
  if (oauthError) showErr(oauthError);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    hideErr();
    const consentEl = document.getElementById("signup-consent") as HTMLInputElement | null;
    if (consentEl && !consentEl.checked) {
      showErr(isAr ? "يجب الموافقة على الشروط والأحكام للمتابعة." : "You must agree to the Terms & Privacy Policy to continue.");
      return;
    }
    submitEl.disabled = true;
    submitEl.textContent = isAr ? "جاري الإنشاء..." : "Creating account…";
    try {
      const data = await post("/api/auth/signup", {
        name: nameEl.value.trim(),
        email: emailEl.value.trim(),
        password: passEl.value,
        role: roleEl.value,
        subjects: roleEl.value === "student" ? [...selectedSubjects] : []
      }) as { token: string; user: User };
      storeToken(data.token, data.user);
      if (data.user.role === "tutor") {
        window.location.href = "/dashboard/tutor";
      } else if (selectedSubjects.size > 0) {
        window.location.href = "/tutors";
      } else {
        window.location.href = "/onboarding";
      }
    } catch (err) {
      showErr(err instanceof Error ? err.message : (isAr ? "حدث خطأ" : "An error occurred"));
      submitEl.disabled = false;
      submitEl.textContent = isAr ? "إنشاء الحساب" : "Create account";
    }
  });
};

// ── Onboarding wizard ─────────────────────────────────────────────────────────

interface OnboardingState {
  learnerType: string;
  childAgeRange: string;
  childPreviousArabic: boolean | null;
  childPreviousQuran: boolean | null;
  parentAttendFirst: boolean | null;
  subjects: string[];
  arabicReadingLevel: string;
  recitationLevel: string;
  tajweedLevel: string;
  memorizedJuz: number;
  memorizationGoal: string;
  riwayahPreference: string;
  ijazahInterest: boolean;
  arabicGrammarLevel: string;
  arabicVocabLevel: string;
  arabicSkillGoals: string[];
  lessonsPerWeek: number | null;
  preferredDays: string[];
  preferredTimeOfDay: string[];
  tutorGenderPref: string;
  teachingLanguages: string[];
  monthlyBudgetUsd: number | null;
}

const defaultState = (): OnboardingState => ({
  learnerType: "",
  childAgeRange: "",
  childPreviousArabic: null,
  childPreviousQuran: null,
  parentAttendFirst: null,
  subjects: [],
  arabicReadingLevel: "none",
  recitationLevel: "none",
  tajweedLevel: "none",
  memorizedJuz: 0,
  memorizationGoal: "",
  riwayahPreference: "",
  ijazahInterest: false,
  arabicGrammarLevel: "none",
  arabicVocabLevel: "none",
  arabicSkillGoals: [],
  lessonsPerWeek: null,
  preferredDays: [],
  preferredTimeOfDay: [],
  tutorGenderPref: "none",
  teachingLanguages: [],
  monthlyBudgetUsd: null,
});

const initOnboarding = () => {
  const root = document.getElementById("onboarding-root");
  if (!root) return;

  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";
  const container = document.getElementById("ob-step-container") as HTMLElement;
  const backBtn = document.getElementById("ob-back") as HTMLButtonElement;
  const nextBtn = document.getElementById("ob-next") as HTMLButtonElement;
  const progressBar = document.getElementById("ob-progress-bar") as HTMLElement;
  const stepLabel = document.getElementById("ob-step-label") as HTMLElement;

  const saved = localStorage.getItem("sn_onboarding");
  const state: OnboardingState = saved ? { ...defaultState(), ...JSON.parse(saved) } : defaultState();

  let step = 0;

  const saveState = () => localStorage.setItem("sn_onboarding", JSON.stringify(state));

  const choiceBlock = (id: string, choices: Array<{ value: string; label: string; sub?: string; icon?: string }>, multi = false): string => `
    <div class="ob-choices${multi ? " ob-choices--multi" : ""}" id="${id}" role="${multi ? "group" : "radiogroup"}">
      ${choices.map(c => `
        <button class="ob-choice" data-value="${esc(c.value)}" type="button" role="${multi ? "checkbox" : "radio"}" aria-checked="false">
          ${c.icon ? iconSvg(c.icon, "ob-choice-icon") : ""}
          <span class="ob-choice-label">${esc(c.label)}</span>
          ${c.sub ? `<span class="ob-choice-sub">${esc(c.sub)}</span>` : ""}
        </button>
      `).join("")}
    </div>
  `;

  const bindChoices = (id: string, multi: boolean, onSelect: (values: string[]) => void, selected: string[]) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.querySelectorAll<HTMLButtonElement>(".ob-choice").forEach(btn => {
      const v = btn.dataset.value!;
      if (selected.includes(v)) { btn.classList.add("ob-choice--selected"); btn.setAttribute("aria-checked", "true"); }
      btn.addEventListener("click", () => {
        if (multi) {
          btn.classList.toggle("ob-choice--selected");
          const now = btn.classList.contains("ob-choice--selected");
          btn.setAttribute("aria-checked", String(now));
          const values = Array.from(el.querySelectorAll<HTMLButtonElement>(".ob-choice--selected")).map(b => b.dataset.value!);
          onSelect(values);
        } else {
          el.querySelectorAll(".ob-choice").forEach(b => { b.classList.remove("ob-choice--selected"); b.setAttribute("aria-checked", "false"); });
          btn.classList.add("ob-choice--selected");
          btn.setAttribute("aria-checked", "true");
          onSelect([v]);
        }
        saveState();
      });
    });
  };

  const buildStep1 = () => {
    const title = isAr ? "من سيتعلم؟" : "Who is learning?";
    const choices = isAr ? [
      { value: "adult", label: "أنا أتعلم لنفسي", sub: "بالغ أو مراهق (14+)", icon: "user" },
      { value: "child", label: "طفلي سيتعلم", sub: "4–13 سنة", icon: "sparkles" },
      { value: "adult_and_child", label: "أنا وطفلي معاً", icon: "users" },
    ] : [
      { value: "adult", label: "I'm learning for myself", sub: "Adult or teen (14+)", icon: "user" },
      { value: "child", label: "My child is learning", sub: "Ages 4–13", icon: "sparkles" },
      { value: "adult_and_child", label: "Both me and my child", icon: "users" },
    ];
    container.innerHTML = `<h2 class="ob-step-title">${title}</h2>${choiceBlock("ob-learner", choices)}`;
    bindChoices("ob-learner", false, ([v]) => { state.learnerType = v; }, state.learnerType ? [state.learnerType] : []);
  };

  const buildStep2 = () => {
    if (state.learnerType === "child" || state.learnerType === "adult_and_child") {
      const title = isAr ? "ما عمر طفلك؟" : "How old is your child?";
      const choices = isAr ? [
        { value: "age_4_7", label: "4–7 سنوات", icon: "sparkles" },
        { value: "age_8_12", label: "8–12 سنة", icon: "book-open" },
        { value: "age_13_plus", label: "13+ سنة", icon: "graduation" },
      ] : [
        { value: "age_4_7", label: "4–7 years", icon: "sparkles" },
        { value: "age_8_12", label: "8–12 years", icon: "book-open" },
        { value: "age_13_plus", label: "13+ years", icon: "graduation" },
      ];
      container.innerHTML = `<h2 class="ob-step-title">${title}</h2>${choiceBlock("ob-age", choices)}`;
      bindChoices("ob-age", false, ([v]) => { state.childAgeRange = v; }, state.childAgeRange ? [state.childAgeRange] : []);
    } else {
      buildStep3();
    }
  };

  const buildStep3 = () => {
    const title = isAr ? "ماذا تريد تعلمه؟" : "What do you want to learn?";
    const choices = Object.entries(SUBJECTS).map(([value, subject]) => ({
      value,
      label: lang === "ar" ? subject.ar : subject.en,
      icon: subject.icon,
    }));
    container.innerHTML = `
      <h2 class="ob-step-title">${title}</h2>
      <p class="ob-step-hint">${isAr ? "يمكنك اختيار أكثر من مادة" : "You can select more than one"}</p>
      ${choiceBlock("ob-subjects", choices, true)}
    `;
    bindChoices("ob-subjects", true, (vals) => { state.subjects = vals; }, state.subjects);
  };

  const buildStep4 = () => {
    const hasQuran = state.subjects.some(s => quranSubjectIds.includes(s));
    const hasArabic = state.subjects.some(s => arabicSubjectIds.includes(s));

    if (hasQuran) {
      const title = isAr ? "ما مستوى تلاوتك الحالي؟" : "What is your current recitation level?";
      const choices = isAr ? [
        { value: "none", label: "مبتدئ", sub: "لا أستطيع قراءة العربية" },
        { value: "beginner", label: "أبجدية", sub: "أتعرف الحروف العربية" },
        { value: "intermediate", label: "متوسط", sub: "أقرأ ببطء مع أخطاء" },
        { value: "advanced", label: "متقدم", sub: "أقرأ بطلاقة نسبياً" },
      ] : [
        { value: "none", label: "Beginner", sub: "Cannot read Arabic yet" },
        { value: "beginner", label: "Alphabet", sub: "Know the Arabic letters" },
        { value: "intermediate", label: "Intermediate", sub: "Read slowly with mistakes" },
        { value: "advanced", label: "Advanced", sub: "Read fairly fluently" },
      ];
      container.innerHTML = `<h2 class="ob-step-title">${title}</h2>${choiceBlock("ob-recitation-level", choices)}`;
      bindChoices("ob-recitation-level", false, ([v]) => { state.recitationLevel = v; }, [state.recitationLevel]);
    } else if (hasArabic) {
      const title = isAr ? "ما مستوى قواعدك في اللغة العربية؟" : "What is your Arabic grammar level?";
      const choices = isAr ? [
        { value: "none", label: "مبتدئ تماماً" },
        { value: "beginner", label: "أعرف القليل" },
        { value: "intermediate", label: "متوسط" },
        { value: "advanced", label: "متقدم" },
      ] : [
        { value: "none", label: "Complete beginner" },
        { value: "beginner", label: "Know a little" },
        { value: "intermediate", label: "Intermediate" },
        { value: "advanced", label: "Advanced" },
      ];
      container.innerHTML = `<h2 class="ob-step-title">${title}</h2>${choiceBlock("ob-arabic-level", choices)}`;
      bindChoices("ob-arabic-level", false, ([v]) => { state.arabicGrammarLevel = v; }, [state.arabicGrammarLevel]);
    } else {
      buildStep5();
    }
  };

  const buildStep5 = () => {
    const title = isAr ? "كم جلسة تريد أسبوعياً؟" : "How many sessions per week?";
    const choices = isAr ? [
      { value: "1", label: "جلسة واحدة" },
      { value: "2", label: "جلستان" },
      { value: "3", label: "3 جلسات" },
      { value: "4", label: "4 جلسات أو أكثر" },
    ] : [
      { value: "1", label: "1 session" },
      { value: "2", label: "2 sessions" },
      { value: "3", label: "3 sessions" },
      { value: "4", label: "4+ sessions" },
    ];
    container.innerHTML = `<h2 class="ob-step-title">${title}</h2>${choiceBlock("ob-freq", choices)}`;
    bindChoices("ob-freq", false, ([v]) => { state.lessonsPerWeek = Number(v); }, state.lessonsPerWeek ? [String(state.lessonsPerWeek)] : []);
  };

  const buildStep6 = () => {
    const title = isAr ? "ما هي الأيام المفضلة لديك؟" : "Which days work best for you?";
    const choices = isAr ? [
      { value: "mon", label: "الإثنين" }, { value: "tue", label: "الثلاثاء" }, { value: "wed", label: "الأربعاء" },
      { value: "thu", label: "الخميس" }, { value: "fri", label: "الجمعة" }, { value: "sat", label: "السبت" }, { value: "sun", label: "الأحد" },
    ] : [
      { value: "mon", label: "Mon" }, { value: "tue", label: "Tue" }, { value: "wed", label: "Wed" },
      { value: "thu", label: "Thu" }, { value: "fri", label: "Fri" }, { value: "sat", label: "Sat" }, { value: "sun", label: "Sun" },
    ];
    container.innerHTML = `
      <h2 class="ob-step-title">${title}</h2>
      <p class="ob-step-hint">${isAr ? "اختر يوماً أو أكثر" : "Select one or more"}</p>
      ${choiceBlock("ob-days", choices, true)}
    `;
    bindChoices("ob-days", true, (vals) => { state.preferredDays = vals; }, state.preferredDays);
  };

  const buildStep7 = () => {
    const title = isAr ? "ما لغة التدريس المفضلة؟" : "Preferred teaching language?";
    const choices = [
      { value: "en", label: "English", icon: "globe" },
      { value: "ar", label: "العربية", icon: "moon" },
      { value: "fr", label: "Français", icon: "globe" },
      { value: "ur", label: "اردو", icon: "globe" },
    ];
    container.innerHTML = `
      <h2 class="ob-step-title">${title}</h2>
      <p class="ob-step-hint">${isAr ? "يمكنك اختيار أكثر من لغة" : "You can select more than one"}</p>
      ${choiceBlock("ob-langs", choices, true)}
    `;
    bindChoices("ob-langs", true, (vals) => { state.teachingLanguages = vals; }, state.teachingLanguages);
  };

  const buildStep8 = () => {
    const title = isAr ? "ما ميزانيتك الشهرية؟ (USD)" : "What is your monthly budget? (USD)";
    container.innerHTML = `
      <h2 class="ob-step-title">${title}</h2>
      <p class="ob-step-hint">${isAr ? "هذا يساعدنا في اقتراح معلمين مناسبين" : "This helps us suggest suitable tutors"}</p>
      <div class="ob-budget-wrap">
        <div class="ob-budget-choices" id="ob-budget-choices">
          ${["50","100","150","200","300"].map(v => `
            <button class="ob-choice ${state.monthlyBudgetUsd === Number(v) ? "ob-choice--selected" : ""}" data-value="${v}" type="button">
              <span class="ob-choice-label">$${v}${v === "300" ? "+" : ""}</span>
              <span class="ob-choice-sub">${isAr ? "شهرياً" : "/month"}</span>
            </button>
          `).join("")}
          <button class="ob-choice ${!state.monthlyBudgetUsd ? "ob-choice--selected" : ""}" data-value="0" type="button">
            <span class="ob-choice-label">${isAr ? "غير محدد" : "Flexible"}</span>
          </button>
        </div>
      </div>
    `;
    document.getElementById("ob-budget-choices")?.querySelectorAll<HTMLButtonElement>(".ob-choice").forEach(btn => {
      btn.addEventListener("click", () => {
        document.querySelectorAll("#ob-budget-choices .ob-choice").forEach(b => b.classList.remove("ob-choice--selected"));
        btn.classList.add("ob-choice--selected");
        const v = Number(btn.dataset.value);
        state.monthlyBudgetUsd = v === 0 ? null : v;
        saveState();
      });
    });
  };

  type StepBuilder = () => void;
  const steps: StepBuilder[] = [buildStep1, buildStep2, buildStep3, buildStep4, buildStep5, buildStep6, buildStep7, buildStep8];
  const stepTitles = isAr
    ? ["المتعلم","العمر","المواد","المستوى","الجدول","الأيام","اللغة","الميزانية"]
    : ["Learner","Age","Subjects","Level","Schedule","Days","Language","Budget"];

  const shouldSkipStep = (): boolean => {
    if (step === 1 && state.learnerType === "adult") return true;
    if (step === 3) {
      const hasQuran = state.subjects.some(s => quranSubjectIds.includes(s));
      const hasArabic = state.subjects.some(s => arabicSubjectIds.includes(s));
      return !hasQuran && !hasArabic;
    }
    return false;
  };

  const renderStep = () => {
    steps[step]?.();
    const pct = Math.round((step / (steps.length - 1)) * 100);
    progressBar.style.width = `${pct}%`;
    stepLabel.textContent = `${stepTitles[step]} (${step + 1}/${steps.length})`;
    backBtn.hidden = step === 0;
    nextBtn.textContent = step === steps.length - 1 ? (isAr ? "اكتمل" : "Finish") : (isAr ? "التالي" : "Next");
  };

  backBtn.addEventListener("click", () => { step = Math.max(0, step - 1); renderStep(); });

  nextBtn.addEventListener("click", async () => {
    if (step < steps.length - 1) {
      step += 1;
      if (shouldSkipStep()) step += 1;
      renderStep();
    } else {
      nextBtn.disabled = true;
      nextBtn.textContent = isAr ? "جاري الحفظ..." : "Saving…";
      try {
        await post("/api/onboarding/student", state);
        localStorage.removeItem("sn_onboarding");
        window.location.href = "/tutors";
      } catch (err) {
        nextBtn.disabled = false;
        nextBtn.textContent = isAr ? "اكتمل" : "Finish";
        showSiteError(err instanceof Error ? err.message : (isAr ? "حدث خطأ" : "An error occurred"));
      }
    }
  });

  renderStep();
};

// ── Tutor listing ─────────────────────────────────────────────────────────────

const renderTutorCard = (tutor: TutorCard, lang: string): string => {
  const isAr = lang === "ar";
  const rating = tutor.averageRating !== null ? tutor.averageRating.toFixed(1) : "—";
  const rateLabel = `$${tutor.hourlyRateUsd}/hr`;
  const photoHtml = tutor.profilePhotoUrl
    ? `<img src="${esc(tutor.profilePhotoUrl)}" alt="${esc(tutor.displayName)}" class="tutor-card-photo" loading="lazy" />`
    : `<div class="tutor-card-photo-placeholder" aria-hidden="true">${iconSvg("user", "placeholder-icon")}</div>`;
  const hafizBadge = tutor.isHafiz ? `<span class="badge badge--hafiz">${isAr ? "حافظ" : "Hafiz"}</span>` : "";
  const ijazahBadge = tutor.ijazahs.length > 0 ? `<span class="badge badge--ijazah">${isAr ? "إجازة" : "Ijazah"}</span>` : "";

  let subjects: Array<{ subjectId: string }> = [];
  subjects = parseMaybeJson<Array<{ subjectId: string }>>(tutor.subjectsTaught, []);
  const subjectTags = subjects.slice(0, 3).map(s => {
    const cat = SUBJECTS[s.subjectId]?.category ?? "quran";
    return `<span class="subject-tag subject-tag--${cat}">${subjectLabel(s.subjectId, lang)}</span>`;
  }).join("");

  const matchReasonHtml = tutor.matchReason
    ? `<div class="match-reason-callout">${iconSvg("sparkles", "match-reason-icon")}<p>${esc(tutor.matchReason)}</p></div>`
    : "";

  return `
    <article class="tutor-card" data-score="${tutor.score ?? 0}">
      <a href="/tutors/${esc(tutor.userId)}${isAr ? "?lang=ar" : ""}" class="tutor-card-link" aria-label="${esc(tutor.displayName)}">
        <div class="tutor-card-photo-wrap">${photoHtml}</div>
        <div class="tutor-card-body">
          <div class="tutor-card-header">
            <h3 class="tutor-card-name">${esc(tutor.displayName)}</h3>
            <div class="tutor-card-badges">${hafizBadge}${ijazahBadge}</div>
          </div>
          <p class="tutor-card-country">${esc(tutor.country)}</p>
          <p class="tutor-card-bio">${esc(tutor.shortBio)}</p>
          <div class="tutor-card-subjects">${subjectTags}</div>
          ${matchReasonHtml}
          <div class="tutor-card-footer">
            <div class="tutor-card-stat">${iconSvg("star", "tutor-card-stat-icon")}<span>${rating}</span></div>
            <div class="tutor-card-stat">${iconSvg("graduation", "tutor-card-stat-icon")}<span>${tutor.totalSessions}</span></div>
            <div class="tutor-card-rate">${rateLabel}</div>
          </div>
        </div>
      </a>
      <a href="/book/${esc(tutor.userId)}${isAr ? "?lang=ar" : ""}" class="btn btn-primary btn-sm tutor-card-book">
        ${isAr ? "احجز" : "Book"}
      </a>
    </article>
  `;
};

const initTutorListing = () => {
  const grid = document.getElementById("tutors-grid");
  if (!grid) return;

  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";
  const loading = document.getElementById("tutors-loading") as HTMLElement;
  const empty = document.getElementById("tutors-empty") as HTMLElement;
  const countNum = document.getElementById("tutors-count-num") as HTMLElement;

  let allTutors: TutorCard[] = [];
  let filterSubject = "";
  let filterLang = "";
  let filterRateMax = 150;
  let filterHafiz = false;
  let filterIjazah = false;
  let filterDays: number[] = [];
  let sortMode = "match";

  const filterAndRender = () => {
    let list = [...allTutors];
    if (filterSubject) {
      list = list.filter(t => {
        const s = parseMaybeJson<Array<{ subjectId: string }>>(t.subjectsTaught, []);
        return s.some(x => x.subjectId === filterSubject);
      });
    }
    if (filterLang) {
      list = list.filter(t => {
        return parseMaybeJson<string[]>(t.teachingLanguages, []).includes(filterLang);
      });
    }
    list = list.filter(t => t.hourlyRateUsd <= filterRateMax);
    if (filterHafiz) list = list.filter(t => t.isHafiz);
    if (filterIjazah) list = list.filter(t => t.ijazahs.length > 0);
    if (filterDays.length > 0) {
      list = list.filter(t => filterDays.some(d => t.availabilitySlots.some(s => s.dayOfWeek === d)));
    }

    if (sortMode === "rating") list.sort((a, b) => (b.averageRating ?? 0) - (a.averageRating ?? 0));
    else if (sortMode === "rate_asc") list.sort((a, b) => a.hourlyRateUsd - b.hourlyRateUsd);
    else if (sortMode === "rate_desc") list.sort((a, b) => b.hourlyRateUsd - a.hourlyRateUsd);
    else if (sortMode === "sessions") list.sort((a, b) => b.totalSessions - a.totalSessions);

    countNum.textContent = String(list.length);
    if (list.length === 0) {
      grid.innerHTML = "";
      empty.classList.remove("hidden");
    } else {
      empty.classList.add("hidden");
      grid.innerHTML = list.map(t => renderTutorCard(t, lang)).join("");
    }
  };

  const loadTutors = async (useMatch: boolean) => {
    loading.classList.remove("hidden");
    grid.innerHTML = "";
    empty.classList.add("hidden");
    try {
      const url = useMatch ? "/api/tutors/match" : "/api/tutors";
      const data = await get(url) as { tutors?: TutorCard[]; results?: TutorCard[] } | TutorCard[];
      allTutors = Array.isArray(data) ? data : ((data as { tutors?: TutorCard[]; results?: TutorCard[] }).tutors ?? (data as { results?: TutorCard[] }).results ?? []);
    } catch {
      allTutors = [];
      try {
        const data = await get("/api/tutors") as TutorCard[] | { tutors?: TutorCard[] };
        allTutors = Array.isArray(data) ? data : ((data as { tutors?: TutorCard[] }).tutors ?? []);
      } catch { /* */ }
    }
    loading.classList.add("hidden");
    filterAndRender();
  };

  let currentMode = "match";
  document.getElementById("mode-match")?.addEventListener("click", () => {
    if (currentMode === "match") return;
    currentMode = "match";
    document.getElementById("mode-match")?.classList.add("mode-btn--active");
    document.getElementById("mode-browse")?.classList.remove("mode-btn--active");
    loadTutors(true);
  });
  document.getElementById("mode-browse")?.addEventListener("click", () => {
    if (currentMode === "browse") return;
    currentMode = "browse";
    document.getElementById("mode-browse")?.classList.add("mode-btn--active");
    document.getElementById("mode-match")?.classList.remove("mode-btn--active");
    loadTutors(false);
  });

  document.querySelectorAll<HTMLButtonElement>("#filter-subjects .chip").forEach(btn => {
    btn.addEventListener("click", () => {
      document.querySelectorAll("#filter-subjects .chip").forEach(b => b.classList.remove("chip--active"));
      btn.classList.add("chip--active");
      filterSubject = btn.dataset.value ?? "";
      filterAndRender();
    });
  });

  document.querySelectorAll<HTMLButtonElement>("#filter-lang .chip").forEach(btn => {
    btn.addEventListener("click", () => {
      document.querySelectorAll("#filter-lang .chip").forEach(b => b.classList.remove("chip--active"));
      btn.classList.add("chip--active");
      filterLang = btn.dataset.value ?? "";
      filterAndRender();
    });
  });

  const rateSlider = document.getElementById("filter-rate-max") as HTMLInputElement;
  const rateDisplay = document.getElementById("filter-rate-display") as HTMLElement;
  rateSlider?.addEventListener("input", () => {
    filterRateMax = Number(rateSlider.value);
    rateDisplay.textContent = `$${filterRateMax}`;
    filterAndRender();
  });

  (document.getElementById("filter-hafiz") as HTMLInputElement)?.addEventListener("change", (e) => {
    filterHafiz = (e.target as HTMLInputElement).checked;
    filterAndRender();
  });

  (document.getElementById("filter-ijazah") as HTMLInputElement)?.addEventListener("change", (e) => {
    filterIjazah = (e.target as HTMLInputElement).checked;
    filterAndRender();
  });

  document.querySelectorAll<HTMLButtonElement>("#filter-days .chip").forEach(btn => {
    btn.addEventListener("click", () => {
      btn.classList.toggle("chip--active");
      const dayNum = Number(btn.dataset.value);
      if (btn.classList.contains("chip--active")) {
        filterDays.push(dayNum);
      } else {
        filterDays = filterDays.filter(d => d !== dayNum);
      }
      filterAndRender();
    });
  });

  (document.getElementById("tutors-sort-select") as HTMLSelectElement)?.addEventListener("change", (e) => {
    sortMode = (e.target as HTMLSelectElement).value;
    filterAndRender();
  });

  document.getElementById("filter-reset")?.addEventListener("click", () => {
    filterSubject = "";
    filterLang = "";
    filterRateMax = 150;
    filterHafiz = false;
    filterIjazah = false;
    filterDays = [];
    if (rateSlider) rateSlider.value = "150";
    if (rateDisplay) rateDisplay.textContent = "$150";
    (document.getElementById("filter-hafiz") as HTMLInputElement).checked = false;
    (document.getElementById("filter-ijazah") as HTMLInputElement).checked = false;
    document.querySelectorAll("#filter-subjects .chip").forEach(b => b.classList.remove("chip--active"));
    document.querySelector("#filter-subjects .chip")?.classList.add("chip--active");
    document.querySelectorAll("#filter-lang .chip").forEach(b => b.classList.remove("chip--active"));
    document.querySelector("#filter-lang .chip")?.classList.add("chip--active");
    document.querySelectorAll("#filter-days .chip").forEach(b => b.classList.remove("chip--active"));
    filterAndRender();
  });

  const urlSubject = new URLSearchParams(window.location.search).get("subject") ?? "";
  if (urlSubject) {
    filterSubject = urlSubject;
    const chip = document.querySelector<HTMLButtonElement>(`#filter-subjects .chip[data-value="${urlSubject}"]`);
    if (chip) {
      document.querySelectorAll("#filter-subjects .chip").forEach(b => b.classList.remove("chip--active"));
      chip.classList.add("chip--active");
    }
  }

  loadTutors(currentMode === "match");
};

// ── Tutor profile page ────────────────────────────────────────────────────────

const setText = (id: string, text: string) => {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
};

const showSection = (id: string) => document.getElementById(id)?.classList.remove("hidden");

// Generic, self-contained modal. Returns when confirmed (onConfirm resolves true) or dismissed.
const openModal = (opts: {
  title: string; bodyHtml: string; confirmLabel: string; cancelLabel: string;
  onConfirm: (root: HTMLElement) => Promise<boolean> | boolean;
}) => {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop sn-modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal sn-modal" role="dialog" aria-modal="true">
      <h2>${opts.title}</h2>
      <div class="sn-modal-body">${opts.bodyHtml}</div>
      <div class="sn-modal-error form-error hidden" role="alert"></div>
      <div class="modal-actions">
        <button class="btn btn-ghost" data-act="cancel" type="button">${opts.cancelLabel}</button>
        <button class="btn btn-primary" data-act="confirm" type="button">${opts.confirmLabel}</button>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  document.body.classList.add("modal-open");
  const close = () => { backdrop.remove(); document.body.classList.remove("modal-open"); };
  const errEl = backdrop.querySelector(".sn-modal-error") as HTMLElement;
  backdrop.querySelector('[data-act="cancel"]')?.addEventListener("click", close);
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
  document.addEventListener("keydown", function onKey(e) {
    if (e.key === "Escape") { close(); document.removeEventListener("keydown", onKey); }
  });
  const confirmBtn = backdrop.querySelector('[data-act="confirm"]') as HTMLButtonElement;
  confirmBtn.addEventListener("click", async () => {
    errEl.classList.add("hidden");
    confirmBtn.disabled = true;
    try {
      const ok = await opts.onConfirm(backdrop);
      if (ok) close(); else confirmBtn.disabled = false;
    } catch (err) {
      errEl.textContent = err instanceof Error ? err.message : "Error";
      errEl.classList.remove("hidden");
      confirmBtn.disabled = false;
    }
  });
  const firstField = backdrop.querySelector<HTMLElement>("input, textarea, select");
  firstField?.focus();
  return { close, root: backdrop };
};

// Styled replacements for the native browser dialogs.
const confirmModal = (opts: { title: string; message: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean }): Promise<boolean> => {
  const isAr = document.documentElement.lang === "ar";
  return new Promise(resolve => {
    const backdrop = document.createElement("div");
    backdrop.className = "modal-backdrop sn-modal-backdrop";
    backdrop.innerHTML = `
      <div class="modal sn-modal sn-dialog" role="alertdialog" aria-modal="true">
        <h2>${opts.title}</h2>
        <p class="sn-dialog-message">${opts.message}</p>
        <div class="modal-actions">
          <button class="btn btn-ghost" data-act="cancel" type="button">${opts.cancelLabel ?? (isAr ? "إلغاء" : "Cancel")}</button>
          <button class="btn ${opts.danger ? "btn-danger" : "btn-primary"}" data-act="confirm" type="button">${opts.confirmLabel ?? (isAr ? "تأكيد" : "Confirm")}</button>
        </div>
      </div>`;
    document.body.appendChild(backdrop);
    document.body.classList.add("modal-open");
    const close = (result: boolean) => {
      backdrop.remove();
      document.body.classList.remove("modal-open");
      resolve(result);
    };
    backdrop.querySelector('[data-act="cancel"]')?.addEventListener("click", () => close(false));
    backdrop.querySelector('[data-act="confirm"]')?.addEventListener("click", () => close(true));
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(false); });
    document.addEventListener("keydown", function onKey(e) {
      if (e.key === "Escape") { close(false); document.removeEventListener("keydown", onKey); }
    });
    (backdrop.querySelector('[data-act="confirm"]') as HTMLButtonElement)?.focus();
  });
};

const promptModal = (opts: { title: string; label?: string; placeholder?: string; initial?: string; multiline?: boolean; optional?: boolean; confirmLabel?: string }): Promise<string | null> => {
  const isAr = document.documentElement.lang === "ar";
  return new Promise(resolve => {
    let settled = false;
    const settle = (value: string | null) => { if (!settled) { settled = true; resolve(value); } };
    const field = opts.multiline
      ? `<textarea class="form-textarea" id="sn-prompt-input" rows="3" placeholder="${esc(opts.placeholder ?? "")}">${esc(opts.initial ?? "")}</textarea>`
      : `<input type="text" class="form-input" id="sn-prompt-input" placeholder="${esc(opts.placeholder ?? "")}" value="${esc(opts.initial ?? "")}" />`;
    const modal = openModal({
      title: opts.title,
      bodyHtml: `
        <div class="form-group">
          ${opts.label ? `<label class="form-label" for="sn-prompt-input">${opts.label}</label>` : ""}
          ${field}
          ${opts.optional ? `<p class="form-hint">${isAr ? "اختياري — يمكنك تركه فارغاً." : "Optional — you can leave this empty."}</p>` : ""}
        </div>`,
      confirmLabel: opts.confirmLabel ?? (isAr ? "تم" : "Done"),
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      onConfirm: (root) => {
        settle((root.querySelector("#sn-prompt-input") as HTMLInputElement | HTMLTextAreaElement).value.trim());
        return true;
      }
    });
    // Resolve null when the dialog goes away without confirming.
    const observer = new MutationObserver(() => {
      if (!document.body.contains(modal.root)) { observer.disconnect(); settle(null); }
    });
    observer.observe(document.body, { childList: true });
  });
};

const showSiteError = (message: string) => {
  const toast = document.getElementById("site-error-toast");
  if (!toast) return;
  toast.textContent = message;
  toast.classList.remove("hidden");
  window.setTimeout(() => toast.classList.add("hidden"), 6000);
};

window.addEventListener("error", () => {
  showSiteError(document.documentElement.lang === "ar" ? "حدث خطأ. يمكنك المتابعة." : "Something went wrong. You can keep browsing.");
});

window.addEventListener("unhandledrejection", () => {
  showSiteError(document.documentElement.lang === "ar" ? "تعذر إكمال الطلب. حاول مرة أخرى." : "The request could not be completed. Try again.");
});

const initTutorProfile = () => {
  const root = document.getElementById("tutor-profile-root");
  if (!root) return;

  const tutorId = root.dataset.tutorId ?? "";
  const lang = root.dataset.lang ?? "en";
  const isAr = lang === "ar";
  const loading = document.getElementById("tutor-profile-loading") as HTMLElement;
  const content = document.getElementById("tutor-profile-content") as HTMLElement;

  const DAY_LABELS: Record<number, string> = { 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat", 7: "Sun" };
  const DAY_LABELS_AR: Record<number, string> = { 1: "الإثنين", 2: "الثلاثاء", 3: "الأربعاء", 4: "الخميس", 5: "الجمعة", 6: "السبت", 7: "الأحد" };

  const loadProfile = async () => {
    try {
      const data = await get(`/api/tutors/${encodeURIComponent(tutorId)}`) as {
        tutor: {
          displayName: string; shortBio: string; aboutFull: string; country: string;
          profilePhotoUrl: string | null; introVideoUrl: string | null;
          subjectsTaught: string; isHafiz: boolean; yearsExperience: number;
          teachingLanguages: string; ageGroupsTaught: string;
          hourlyRateUsd: number; averageRating: number | null; totalSessions: number; responseRatePercent: number;
          ijazahs: Array<{ riwayah: string; grantingSheikhName: string; verifiedByAdmin: boolean }>;
          availabilitySlots: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
        };
        packages: Array<{ id: string; name: string; lessonCount: number; priceUsd: number; discountPct: number; validityWeeks: number }>;
        reviews: Array<{ rating: number; comment: string | null; isAnonymous: boolean; studentName: string; createdAt: string }>;
        onLeave?: { until: string } | null;
        matchReason?: string;
      };

      const t = data.tutor;
      const photoEl = document.getElementById("tp-photo") as HTMLImageElement;
      const photoPlaceholder = document.getElementById("tp-photo-placeholder") as HTMLElement;
      if (t.profilePhotoUrl) { photoEl.src = t.profilePhotoUrl; photoEl.alt = t.displayName; }
      else { photoEl.classList.add("hidden"); photoPlaceholder.classList.remove("hidden"); }

      // Tutor on leave: banner now, booking links disabled after all sections
      // (including package cards) have rendered.
      const disableBookingLinks = () => {
        document.querySelectorAll<HTMLAnchorElement>('a[href^="/book/"]').forEach(a => {
          a.classList.add("btn--disabled");
          a.removeAttribute("href");
          a.setAttribute("aria-disabled", "true");
        });
      };
      if (data.onLeave) {
        const until = formatDate(data.onLeave.until, lang);
        const banner = document.createElement("div");
        banner.className = "tp-leave-banner";
        banner.innerHTML = `${iconSvg("plane", "inline-icon")} <span>${isAr
          ? `هذا المعلم في إجازة حتى ${until} — الحجوزات الجديدة متوقفة مؤقتاً وستُستأنف تلقائياً بعد عودته.`
          : `This tutor is on leave until ${until} — new bookings are paused and resume automatically when they return.`}</span>`;
        content.prepend(banner);
        disableBookingLinks();
        // Catch anchors injected by later sections in this load (packages etc.).
        setTimeout(disableBookingLinks, 0);
        setTimeout(disableBookingLinks, 500);
      }

      setText("tp-name", t.displayName);
      setText("tp-country", t.country);
      setText("tp-rating", t.averageRating !== null ? `${t.averageRating.toFixed(1)} ★` : (isAr ? "لا يوجد تقييم" : "No rating"));
      setText("tp-sessions", `${t.totalSessions} ${isAr ? "جلسة" : "sessions"}`);
      setText("tp-response-rate", `${t.responseRatePercent}% ${isAr ? "معدل الرد" : "response rate"}`);
      setText("tp-rate", `$${t.hourlyRateUsd}${isAr ? "/س" : "/hr"}`);
      setText("tp-about", t.aboutFull || t.shortBio);

      const tagsEl = document.getElementById("tp-tags") as HTMLElement;
      const tags: string[] = [];
      if (t.isHafiz) tags.push(`<span class="badge badge--hafiz">${isAr ? "حافظ" : "Hafiz"}</span>`);
      if (t.ijazahs.length > 0) tags.push(`<span class="badge badge--ijazah">${isAr ? "إجازة" : "Ijazah"}</span>`);
      if (t.yearsExperience > 0) tags.push(`<span class="badge">${t.yearsExperience} ${isAr ? "سنوات خبرة" : "yrs exp."}</span>`);
      tagsEl.innerHTML = tags.join("");

      const subjects = parseMaybeJson<Array<{ subjectId: string; levelsTaught: string[] }>>(t.subjectsTaught, []);
      const subjectsEl = document.getElementById("tp-subjects") as HTMLElement;
      subjectsEl.innerHTML = subjects.map(s => {
        const cat = SUBJECTS[s.subjectId]?.category ?? "quran";
        return `
          <div class="tutor-subject-item subject-tag--${cat}">
            <span class="subject-name">${subjectLabel(s.subjectId, lang)}</span>
            ${s.levelsTaught.length > 0 ? `<span class="subject-levels">${s.levelsTaught.join(", ")}</span>` : ""}
          </div>
        `;
      }).join("");

      const credsEl = document.getElementById("tp-credentials") as HTMLElement;
      const creds: string[] = [];
      if (t.isHafiz) creds.push(isAr ? "حافظ للقرآن الكريم" : "Hafiz/Hafiza");
      if (t.yearsExperience > 0) creds.push(`${t.yearsExperience} ${isAr ? "سنوات من الخبرة في التدريس" : "years of teaching experience"}`);
      const langs = parseMaybeJson<string[]>(t.teachingLanguages, []);
      if (langs.length > 0) creds.push(`${isAr ? "يدرّس بـ" : "Teaches in"}: ${langs.join(", ")}`);
      credsEl.innerHTML = creds.map(c => `<div class="credential-item">✓ ${esc(c)}</div>`).join("");

      if (t.ijazahs.length > 0) {
        showSection("tp-ijazah-section");
        const ijazahEl = document.getElementById("tp-ijazahs") as HTMLElement;
        ijazahEl.innerHTML = t.ijazahs.map(ij => `
          <div class="ijazah-card">
            <div class="ijazah-riwayah">${iconSvg("scroll", "inline-icon")} ${esc(ij.riwayah)}</div>
            <div class="ijazah-sheikh">${isAr ? "الشيخ: " : "Sheikh: "}${esc(ij.grantingSheikhName)}</div>
            ${ij.verifiedByAdmin ? `<span class="badge badge--verified">${isAr ? "موثق" : "Verified"}</span>` : ""}
          </div>
        `).join("");
      }

      if (t.introVideoUrl) {
        showSection("tp-video-section");
        const videoWrap = document.getElementById("tp-video-wrap") as HTMLElement;
        videoWrap.innerHTML = `<a href="${esc(t.introVideoUrl)}" target="_blank" rel="noopener" class="btn btn-outline">${isAr ? "مشاهدة الفيديو التعريفي ↗" : "Watch intro video ↗"}</a>`;
      }

      const availEl = document.getElementById("tp-availability") as HTMLElement;
      const uniqueDays = [...new Set(t.availabilitySlots.map(s => s.dayOfWeek))].sort();
      if (uniqueDays.length > 0) {
        availEl.innerHTML = uniqueDays.map(d =>
          `<span class="avail-day">${isAr ? (DAY_LABELS_AR[d] ?? d) : (DAY_LABELS[d] ?? d)}</span>`
        ).join("");
      } else {
        availEl.textContent = isAr ? "تواصل لتحديد المواعيد" : "Contact to arrange schedule";
      }

      if (data.packages.length > 0) {
        showSection("tp-packages-section");
        const pkgsEl = document.getElementById("tp-packages") as HTMLElement;
        pkgsEl.innerHTML = data.packages.map(pkg => `
          <div class="package-card">
            <h4 class="package-name">${esc(pkg.name)}</h4>
            <div class="package-details">
              <span class="package-lessons">${pkg.lessonCount} ${isAr ? "دروس" : "lessons"}</span>
              ${pkg.discountPct > 0 ? `<span class="package-discount">-${pkg.discountPct}%</span>` : ""}
              <span class="package-validity">${pkg.validityWeeks} ${isAr ? "أسابيع" : "weeks"}</span>
            </div>
            <div class="package-price">$${pkg.priceUsd}</div>
            <a href="/book/${esc(tutorId)}?package=${esc(pkg.id)}${isAr ? "&lang=ar" : ""}" class="btn btn-primary btn-sm">
              ${isAr ? "اشترك" : "Enrol"}
            </a>
          </div>
        `).join("");
      }

      const reviewsEl = document.getElementById("tp-reviews") as HTMLElement;
      if (data.reviews.length > 0) {
        reviewsEl.innerHTML = data.reviews.map(r => `
          <div class="review-card">
            <div class="review-header">
              <span class="review-stars">${"★".repeat(r.rating)}${"☆".repeat(5 - r.rating)}</span>
              <span class="review-author">${r.isAnonymous ? (isAr ? "مجهول" : "Anonymous") : esc(r.studentName)}</span>
            </div>
            ${r.comment ? `<p class="review-comment">${esc(r.comment)}</p>` : ""}
          </div>
        `).join("");
      }

      if (data.matchReason) {
        const mrEl = document.getElementById("tp-match-reason") as HTMLElement;
        const mrText = document.getElementById("tp-match-reason-text") as HTMLElement;
        mrEl.classList.remove("hidden");
        mrText.textContent = data.matchReason;
      }

      loading.classList.add("hidden");
      content.classList.remove("hidden");
    } catch (err) {
      loading.innerHTML = `<p class="error-text">${err instanceof Error ? esc(err.message) : (isAr ? "حدث خطأ" : "Error loading profile")}</p>`;
    }
  };

  document.getElementById("tp-message-btn")?.addEventListener("click", () => {
    if (!currentUser) { window.location.href = `/login${isAr ? "?lang=ar" : ""}`; return; }
    window.location.href = `/messages?to=${encodeURIComponent(tutorId)}${isAr ? "&lang=ar" : ""}`;
  });

  loadProfile();
};

// ── Booking flow ──────────────────────────────────────────────────────────────

const initBooking = () => {
  const root = document.getElementById("booking-root");
  if (!root) return;

  const tutorId = root.dataset.tutorId ?? "";
  const lang = root.dataset.lang ?? "en";
  const isAr = lang === "ar";

  let bookStep = 0;
  let selectedType = "single";
  let selectedPackageId: string | null = null;

  const backBtn = document.getElementById("bk-back") as HTMLButtonElement;
  const nextBtn = document.getElementById("bk-next") as HTMLButtonElement;
  const steps = document.querySelectorAll<HTMLElement>(".booking-step");

  const showStep = (idx: number) => {
    steps.forEach((s, i) => { s.style.display = i === idx ? "" : "none"; });
    backBtn.hidden = idx === 0;
    nextBtn.textContent = idx === steps.length - 1 ? (isAr ? "تأكيد الحجز" : "Confirm booking") : (isAr ? "التالي" : "Next");
  };

  showStep(0);

  document.querySelectorAll<HTMLInputElement>("input[name='session-type']").forEach(radio => {
    radio.addEventListener("change", () => {
      selectedType = radio.value;
      const pkgsSection = document.getElementById("bk-packages") as HTMLElement;
      pkgsSection.classList.toggle("hidden", selectedType !== "package");
    });
  });

  const loadTutorSummary = async () => {
    try {
      const data = await get(`/api/tutors/${encodeURIComponent(tutorId)}`) as {
        tutor: { displayName: string; hourlyRateUsd: number; profilePhotoUrl: string | null; shortBio: string; subjectsTaught: string };
        packages: Array<{ id: string; name: string; lessonCount: number; priceUsd: number; discountPct: number }>;
      };
      const summaryEl = document.getElementById("booking-tutor-summary") as HTMLElement;
      summaryEl.innerHTML = `
        <div class="booking-tutor-mini">
          <div class="booking-tutor-avatar">${data.tutor.profilePhotoUrl ? `<img src="${esc(data.tutor.profilePhotoUrl)}" alt="" />` : iconSvg("user", "placeholder-icon")}</div>
          <div>
            <strong>${esc(data.tutor.displayName)}</strong>
            <span>$${data.tutor.hourlyRateUsd}/hr</span>
          </div>
        </div>
      `;

      const subjectEl = document.getElementById("bk-subject") as HTMLSelectElement;
      const subjects = parseMaybeJson<Array<{ subjectId: string }>>(data.tutor.subjectsTaught, []);
      subjects.forEach(s => {
        const opt = document.createElement("option");
        opt.value = s.subjectId;
        opt.textContent = subjectLabel(s.subjectId, lang);
        subjectEl.appendChild(opt);
      });

      if (data.packages.length > 0) {
        const pkgsList = document.getElementById("bk-packages-list") as HTMLElement;
        pkgsList.innerHTML = data.packages.map(p => `
          <label class="package-choice">
            <input type="radio" name="bk-package" value="${esc(p.id)}" />
            <div class="package-card">
              <strong>${esc(p.name)}</strong>
              <span>${p.lessonCount} lessons</span>
              ${p.discountPct > 0 ? `<span class="package-discount">-${p.discountPct}%</span>` : ""}
              <span>$${p.priceUsd}</span>
            </div>
          </label>
        `).join("");
        pkgsList.querySelectorAll<HTMLInputElement>("input[name='bk-package']").forEach(r => {
          r.addEventListener("change", () => { selectedPackageId = r.value; });
        });
      } else {
        // No packages on offer: hide the whole "Lesson package" option.
        const packageRadio = document.getElementById("bk-type-package") as HTMLInputElement | null;
        packageRadio?.closest("label, .booking-type-option")?.classList.add("hidden");
        if (packageRadio?.checked) {
          const single = document.getElementById("bk-type-single") as HTMLInputElement | null;
          if (single) { single.checked = true; single.dispatchEvent(new Event("change", { bubbles: true })); }
        }
        document.getElementById("bk-packages")?.classList.add("hidden");
      }

      const minDate = new Date();
      minDate.setDate(minDate.getDate() + 1);
      (document.getElementById("bk-date") as HTMLInputElement).min = minDate.toISOString().split("T")[0];
    } catch { /* */ }
  };

  backBtn.addEventListener("click", () => { bookStep = Math.max(0, bookStep - 1); showStep(bookStep); });

  nextBtn.addEventListener("click", async () => {
    if (bookStep < steps.length - 1) {
      if (bookStep === 1) {
        const subject = (document.getElementById("bk-subject") as HTMLSelectElement).value;
        const date = (document.getElementById("bk-date") as HTMLInputElement).value;
        const time = (document.getElementById("bk-time") as HTMLInputElement).value;
        if (selectedType === "package") {
          if (!selectedPackageId) {
            showSiteError(isAr ? "يرجى اختيار الباقة" : "Please choose a package");
            return;
          }
          const summaryEl = document.getElementById("bk-summary") as HTMLElement;
          summaryEl.innerHTML = `
            <div class="booking-confirm-row"><label>${isAr ? "النوع" : "Type"}:</label><span>${isAr ? "شراء باقة" : "Package purchase"}</span></div>
          `;
        } else if (!subject || !date || !time) {
          showSiteError(isAr ? "يرجى اختيار المادة والتاريخ والوقت" : "Please select subject, date and time");
          return;
        } else {
          const summaryEl = document.getElementById("bk-summary") as HTMLElement;
          const duration = (document.getElementById("bk-duration") as HTMLSelectElement).value;
          summaryEl.innerHTML = `
            <div class="booking-confirm-row"><label>${isAr ? "نوع الجلسة" : "Type"}:</label><span>${esc(selectedType)}</span></div>
            <div class="booking-confirm-row"><label>${isAr ? "المادة" : "Subject"}:</label><span>${subjectLabel(subject, lang)}</span></div>
            <div class="booking-confirm-row"><label>${isAr ? "الموعد" : "Date"}:</label><span>${date} ${time}</span></div>
            <div class="booking-confirm-row"><label>${isAr ? "المدة" : "Duration"}:</label><span>${duration} ${isAr ? "دقيقة" : "min"}</span></div>
          `;
        }
      }
      bookStep += 1;
      showStep(bookStep);
    } else {
      nextBtn.disabled = true;
      nextBtn.textContent = isAr ? "جاري الحجز..." : "Booking…";
      try {
        const subject = (document.getElementById("bk-subject") as HTMLSelectElement).value;
        const date = (document.getElementById("bk-date") as HTMLInputElement).value;
        const time = (document.getElementById("bk-time") as HTMLInputElement).value;
        const duration = Number((document.getElementById("bk-duration") as HTMLSelectElement).value);
        const note = (document.getElementById("bk-note") as HTMLTextAreaElement).value.trim();

        if (selectedType === "package") {
          if (!selectedPackageId) throw new Error(isAr ? "يرجى اختيار الباقة" : "Please choose a package");
          const data = await post("/api/payments/checkout", { kind: "package", packageDefId: selectedPackageId }) as { checkoutUrl: string | null };
          if (!data.checkoutUrl) throw new Error("Checkout URL missing.");
          window.location.href = data.checkoutUrl;
        } else {
          const data = await post("/api/payments/checkout", {
            kind: "session",
            tutorId, subjectId: subject,
            scheduledAt: new Date(`${date}T${time}`).toISOString(),
            durationMinutes: duration,
            studentNote: note || undefined,
            isTrial: selectedType === "trial"
          }) as { checkoutUrl: string | null };
          if (!data.checkoutUrl) throw new Error("Checkout URL missing.");
          window.location.href = data.checkoutUrl;
        }
      } catch (err) {
        const errEl = document.getElementById("bk-error") as HTMLElement;
        errEl.textContent = err instanceof Error ? err.message : (isAr ? "حدث خطأ" : "An error occurred");
        errEl.classList.remove("hidden");
        nextBtn.disabled = false;
        nextBtn.textContent = isAr ? "تأكيد الحجز" : "Confirm booking";
      }
    }
  });

  loadTutorSummary();
};

// ── Dashboard helpers ─────────────────────────────────────────────────────────

const initDashTabs = (dashId: string) => {
  const dash = document.getElementById(dashId);
  if (!dash) return;

  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";

  dash.querySelectorAll<HTMLButtonElement>(".dash-tab").forEach(tab => {
    tab.addEventListener("click", () => {
      dash.querySelectorAll(".dash-tab").forEach(t => { t.classList.remove("dash-tab--active"); t.setAttribute("aria-selected", "false"); });
      tab.classList.add("dash-tab--active");
      tab.setAttribute("aria-selected", "true");
      const id = tab.dataset.tab!;
      dash.querySelectorAll<HTMLElement>(".dash-panel").forEach(p => p.classList.toggle("dash-panel--active", p.id === `tab-${id}`));
    });
  });

  document.getElementById("dash-logout")?.addEventListener("click", logout);

  loadCurrentUser().then(user => {
    updateNavAuth(user);
    if (!user) { window.location.href = `/login${isAr ? "?lang=ar" : ""}`; return; }
    setText("dash-user-name", user.name);
    const avatar = document.getElementById("dash-avatar");
    if (avatar) {
      avatar.innerHTML = user.avatarUrl
        ? `<img src="${esc(user.avatarUrl)}" alt="" />`
        : iconSvg(user.role === "admin" || user.role === "moderator" ? "shield" : "user", "dash-avatar-icon");
    }
    // Unverified email: gentle banner with a one-click resend.
    if (user.emailVerified === false) {
      const main = dash.querySelector(".dashboard-main");
      if (main && !document.getElementById("verify-email-banner")) {
        const banner = document.createElement("div");
        banner.id = "verify-email-banner";
        banner.className = "dash-verify-banner";
        banner.innerHTML = `
          ${iconSvg("mail", "inline-icon")}
          <span>${isAr ? "بريدك الإلكتروني غير مؤكد بعد — تحقق من صندوق الوارد." : "Your email isn't verified yet — check your inbox."}</span>
          <button class="btn btn-outline btn-sm" id="verify-email-resend">${isAr ? "إعادة إرسال الرابط" : "Resend link"}</button>`;
        main.prepend(banner);
        document.getElementById("verify-email-resend")?.addEventListener("click", async (e) => {
          const btn = e.currentTarget as HTMLButtonElement;
          btn.disabled = true;
          try {
            await post("/api/auth/resend-confirmation", {});
            btn.textContent = isAr ? "تم الإرسال ✓" : "Sent ✓";
          } catch (err) {
            btn.disabled = false;
            showSiteError(err instanceof Error ? err.message : "");
          }
        });
      }
    }
  });
};

// Mount the embedded messaging panel the first time a dashboard's Messages tab opens.
const wireMessagesTab = (dash: HTMLElement) => {
  let mounted = false;
  dash.querySelector<HTMLButtonElement>('.dash-tab[data-tab="messages"]')?.addEventListener("click", () => {
    if (mounted) return;
    mounted = true;
    void mountMessaging();
  });
};

const formatDate = (iso: string, lang: string): string => {
  try {
    return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  } catch { return iso; }
};

// ── Theme ─────────────────────────────────────────────────────────────────────

const applyTheme = (theme: string) => {
  const resolved = !theme || theme === "system"
    ? (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    : theme;
  if (resolved === "light") document.documentElement.removeAttribute("data-theme");
  else document.documentElement.setAttribute("data-theme", resolved);
  try { localStorage.setItem("sn_theme", theme || "system"); } catch { /* */ }
};

// ── Settings panel (shared by student & tutor dashboards) ────────────────────

const initSettingsPanel = (lang: string) => {
  const root = document.getElementById("settings-content");
  if (!root) return;
  const isAr = lang === "ar";
  const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T | null;

  const zoneOffsetLabel = (zoneId: string): string => {
    try {
      const now = new Date();
      const fmt = new Intl.DateTimeFormat("en-US", { timeZone: zoneId, timeZoneName: "shortOffset" });
      const part = fmt.formatToParts(now).find(p => p.type === "timeZoneName");
      return part?.value ?? "";
    } catch { return ""; }
  };

  const fillCities = (countryCode: string, selectedZone?: string) => {
    const citySel = $<HTMLSelectElement>("settings-tz-city");
    if (!citySel) return;
    const country = COUNTRY_TIMEZONES.find(c => c.code === countryCode);
    if (!country) {
      citySel.innerHTML = `<option value="">${isAr ? "اختر الدولة أولاً" : "Select a country first"}</option>`;
      citySel.disabled = true;
      return;
    }
    citySel.disabled = false;
    citySel.innerHTML = country.zones.map(zone => {
      const offset = zoneOffsetLabel(zone.id);
      return `<option value="${esc(zone.id)}"${zone.id === selectedZone ? " selected" : ""}>${esc(isAr ? zone.cityAr : zone.city)}${offset ? ` (${offset})` : ""}</option>`;
    }).join("");
    updateTzNow();
  };

  const updateTzNow = () => {
    const citySel = $<HTMLSelectElement>("settings-tz-city");
    const hint = $("settings-tz-now");
    if (!citySel || !hint || !citySel.value) { if (hint) hint.textContent = ""; return; }
    try {
      const now = new Date().toLocaleTimeString(isAr ? "ar" : "en-GB", { timeZone: citySel.value, hour: "2-digit", minute: "2-digit" });
      hint.textContent = isAr ? `الوقت الحالي هناك: ${now}` : `Current time there: ${now}`;
    } catch { hint.textContent = ""; }
  };

  const selectZone = (zoneId: string) => {
    const hit = findZone(zoneId);
    const countrySel = $<HTMLSelectElement>("settings-tz-country");
    if (!countrySel) return;
    if (hit) {
      countrySel.value = hit.country.code;
      fillCities(hit.country.code, hit.zone.id);
    } else {
      // Unknown zone (legacy value): keep it selectable so saving doesn't lose it.
      const citySel = $<HTMLSelectElement>("settings-tz-city");
      if (citySel) {
        citySel.disabled = false;
        citySel.innerHTML = `<option value="${esc(zoneId)}" selected>${esc(zoneId)}</option>`;
      }
    }
    updateTzNow();
  };

  const setStatus = (id: string, ok: boolean, text: string) => {
    const el = $(id);
    if (!el) return;
    el.textContent = text;
    el.classList.toggle("settings-save-status--error", !ok);
    setTimeout(() => { if (el.textContent === text) el.textContent = ""; }, 4000);
  };

  const renderAvatar = (url: string | null | undefined) => {
    const preview = $("settings-avatar-preview");
    if (preview) preview.innerHTML = url ? `<img src="${esc(url)}" alt="" />` : iconSvg("user", "settings-avatar-icon");
  };

  const markThemeSelection = (theme: string) => {
    document.querySelectorAll<HTMLButtonElement>("[data-theme-choice]").forEach(btn => {
      const active = btn.dataset.themeChoice === theme;
      btn.classList.toggle("theme-swatch--active", active);
      btn.setAttribute("aria-pressed", String(active));
    });
  };

  let savedLanguage = lang;
  let currentTheme = (() => { try { return localStorage.getItem("sn_theme") ?? "system"; } catch { return "system"; } })();

  const load = async () => {
    try {
      const data = await get("/api/account") as {
        account: { name: string; email: string; avatarUrl?: string | null; profilePhotoUrl?: string | null; timeZone?: string; language?: string; theme?: string }
      };
      const nameEl = $<HTMLInputElement>("settings-name");
      const emailEl = $<HTMLInputElement>("settings-email");
      const langSel = $<HTMLSelectElement>("settings-language");
      if (nameEl) nameEl.value = data.account.name;
      if (emailEl) emailEl.value = data.account.email;
      if (langSel) langSel.value = data.account.language ?? lang;
      savedLanguage = data.account.language ?? lang;
      // Tutors upload to their public tutor photo; show that here.
      renderAvatar(currentUser?.role === "tutor"
        ? (data.account.profilePhotoUrl ?? data.account.avatarUrl)
        : data.account.avatarUrl);
      currentTheme = data.account.theme ?? currentTheme;
      applyTheme(currentTheme);
      markThemeSelection(currentTheme);
      selectZone(data.account.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
    } catch { /* */ }
    try {
      const data = await get("/api/account/private") as { privateInfo: Record<string, string | null> | null };
      const info = data.privateInfo ?? {};
      const setVal = (id: string, value: string | null | undefined) => {
        const el = $<HTMLInputElement>(id);
        if (el) el.value = value ?? "";
      };
      setVal("private-dob", info.dateOfBirth ? String(info.dateOfBirth).slice(0, 10) : "");
      setVal("private-phone", info.phone);
      setVal("private-alt-phone", info.altPhone);
      setVal("private-contact-lang", info.preferredContactLanguage);
      setVal("private-address1", info.addressLine1);
      setVal("private-address2", info.addressLine2);
      setVal("private-city", info.city);
      setVal("private-state", info.stateRegion);
      setVal("private-postal", info.postalCode);
      const countrySel = $<HTMLSelectElement>("private-country");
      if (countrySel) countrySel.value = info.country ?? "";
    } catch { /* */ }
  };

  // Theme swatches apply instantly and persist.
  document.querySelectorAll<HTMLButtonElement>("[data-theme-choice]").forEach(btn => {
    btn.addEventListener("click", async () => {
      const theme = btn.dataset.themeChoice!;
      currentTheme = theme;
      applyTheme(theme);
      markThemeSelection(theme);
      try { await patch("/api/account", { theme }); } catch { /* offline persistence via localStorage still applies */ }
    });
  });

  $<HTMLSelectElement>("settings-tz-country")?.addEventListener("change", (e) =>
    fillCities((e.target as HTMLSelectElement).value));
  $<HTMLSelectElement>("settings-tz-city")?.addEventListener("change", updateTzNow);

  // Avatar upload
  const avatarInput = $<HTMLInputElement>("settings-avatar-input");
  $("settings-avatar-btn")?.addEventListener("click", () => avatarInput?.click());
  avatarInput?.addEventListener("change", async () => {
    const file = avatarInput.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { showSiteError(isAr ? "الصورة أكبر من 3 ميغابايت." : "Image is larger than 3 MB."); return; }
    try {
      const target = currentUser?.role === "tutor" ? "?target=tutor" : "";
      const res = await fetch(`/api/account/avatar${target}`, {
        method: "POST",
        headers: { "Content-Type": file.type },
        credentials: "same-origin",
        body: file
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
      renderAvatar((data as { url: string }).url);
      // Refresh nav + sidebar avatars without a reload.
      if (currentUser && currentUser.role !== "tutor") currentUser.avatarUrl = (data as { url: string }).url;
      const dashAvatar = document.getElementById("dash-avatar");
      if (dashAvatar && currentUser?.role !== "tutor") dashAvatar.innerHTML = `<img src="${esc((data as { url: string }).url)}" alt="" />`;
      updateNavAuth(currentUser);
    } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
    avatarInput.value = "";
  });

  $("settings-save")?.addEventListener("click", async () => {
    const btn = $<HTMLButtonElement>("settings-save")!;
    btn.disabled = true;
    try {
      const language = $<HTMLSelectElement>("settings-language")?.value ?? lang;
      await patch("/api/account", {
        name: $<HTMLInputElement>("settings-name")?.value.trim(),
        timeZone: $<HTMLSelectElement>("settings-tz-city")?.value || undefined,
        language
      });
      setStatus("settings-save-status", true, isAr ? "تم الحفظ ✓" : "Saved ✓");
      if (language !== savedLanguage) {
        // Language switch: reload the page in the chosen language.
        const url = new URL(window.location.href);
        if (language === "ar") url.searchParams.set("lang", "ar");
        else url.searchParams.delete("lang");
        window.location.href = url.toString();
        return;
      }
    } catch (err) {
      setStatus("settings-save-status", false, err instanceof Error ? err.message : (isAr ? "تعذّر الحفظ" : "Could not save"));
    }
    btn.disabled = false;
  });

  $("private-save")?.addEventListener("click", async () => {
    const btn = $<HTMLButtonElement>("private-save")!;
    btn.disabled = true;
    try {
      await put("/api/account/private", {
        dateOfBirth: $<HTMLInputElement>("private-dob")?.value ?? "",
        phone: $<HTMLInputElement>("private-phone")?.value ?? "",
        altPhone: $<HTMLInputElement>("private-alt-phone")?.value ?? "",
        preferredContactLanguage: $<HTMLInputElement>("private-contact-lang")?.value ?? "",
        addressLine1: $<HTMLInputElement>("private-address1")?.value ?? "",
        addressLine2: $<HTMLInputElement>("private-address2")?.value ?? "",
        city: $<HTMLInputElement>("private-city")?.value ?? "",
        stateRegion: $<HTMLInputElement>("private-state")?.value ?? "",
        postalCode: $<HTMLInputElement>("private-postal")?.value ?? "",
        country: $<HTMLSelectElement>("private-country")?.value ?? "",
        emergencyContactName: $<HTMLInputElement>("private-emergency-name")?.value ?? "",
        emergencyContactPhone: $<HTMLInputElement>("private-emergency-phone")?.value ?? "",
        emergencyContactRelation: $<HTMLInputElement>("private-emergency-relation")?.value ?? ""
      });
      setStatus("private-save-status", true, isAr ? "تم الحفظ ✓" : "Saved ✓");
    } catch (err) {
      setStatus("private-save-status", false, err instanceof Error ? err.message : (isAr ? "تعذّر الحفظ" : "Could not save"));
    }
    btn.disabled = false;
  });

  // Emergency contact fields load with the private info block above.
  void load().then(async () => {
    try {
      const data = await get("/api/account/private") as { privateInfo: Record<string, string | null> | null };
      const info = data.privateInfo ?? {};
      const setVal = (id: string, value: string | null | undefined) => {
        const el = $<HTMLInputElement>(id);
        if (el && !el.value) el.value = value ?? "";
      };
      setVal("private-emergency-name", info.emergencyContactName);
      setVal("private-emergency-phone", info.emergencyContactPhone);
      setVal("private-emergency-relation", info.emergencyContactRelation);
    } catch { /* */ }
  });
};

// ── Availability & leave (tutor) ──────────────────────────────────────────────

const WEEKDAYS: Array<{ iso: number; en: string; ar: string }> = [
  { iso: 1, en: "Monday", ar: "الاثنين" },
  { iso: 2, en: "Tuesday", ar: "الثلاثاء" },
  { iso: 3, en: "Wednesday", ar: "الأربعاء" },
  { iso: 4, en: "Thursday", ar: "الخميس" },
  { iso: 5, en: "Friday", ar: "الجمعة" },
  { iso: 6, en: "Saturday", ar: "السبت" },
  { iso: 7, en: "Sunday", ar: "الأحد" }
];

const initAvailabilityPanel = (lang: string) => {
  const editor = document.getElementById("availability-editor");
  if (!editor) return;
  const isAr = lang === "ar";

  type Slot = { dayOfWeek: number; startTime: string; endTime: string };
  type Reserved = { sessionId: string; scheduledAt: string; durationMinutes: number; dayOfWeek: number; startMinutes: number; studentName: string };
  let slots: Slot[] = [];
  let reserved: Reserved[] = [];

  const toMin = (t: string) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };

  const slotIsLocked = (slot: Slot): Reserved | null =>
    reserved.find(r =>
      r.dayOfWeek === slot.dayOfWeek &&
      toMin(slot.startTime) <= r.startMinutes &&
      r.startMinutes + r.durationMinutes <= toMin(slot.endTime)
    ) ?? null;

  const render = () => {
    editor.innerHTML = WEEKDAYS.map(day => {
      const daySlots = slots
        .map((s, index) => ({ ...s, index }))
        .filter(s => s.dayOfWeek === day.iso)
        .sort((a, b) => toMin(a.startTime) - toMin(b.startTime));
      return `
        <div class="availability-day">
          <div class="availability-day-head">
            <span class="availability-day-name">${isAr ? day.ar : day.en}</span>
            <button class="btn btn-ghost btn-sm avail-add" data-day="${day.iso}">${iconSvg("plus", "btn-icon")} ${isAr ? "إضافة فترة" : "Add window"}</button>
          </div>
          <div class="availability-day-slots">
            ${daySlots.length ? daySlots.map(s => {
              const lock = slotIsLocked(s);
              return `
                <div class="availability-slot${lock ? " availability-slot--locked" : ""}">
                  <input type="time" class="form-input avail-start" data-index="${s.index}" value="${esc(s.startTime)}" ${lock ? "disabled" : ""} />
                  <span class="availability-slot-sep">–</span>
                  <input type="time" class="form-input avail-end" data-index="${s.index}" value="${esc(s.endTime)}" ${lock ? "disabled" : ""} />
                  ${lock
                    ? `<span class="availability-lock" title="${esc(isAr ? `درس محجوز مع ${lock.studentName}` : `Booked lesson with ${lock.studentName}`)}">${iconSvg("lock", "availability-lock-icon")}</span>`
                    : `<button class="availability-remove avail-remove" data-index="${s.index}" aria-label="${isAr ? "حذف الفترة" : "Remove window"}">${iconSvg("trash", "availability-remove-icon")}</button>`}
                </div>`;
            }).join("") : `<p class="availability-day-empty">${isAr ? "غير متاح" : "Unavailable"}</p>`}
          </div>
        </div>`;
    }).join("");

    editor.querySelectorAll<HTMLButtonElement>(".avail-add").forEach(btn => btn.addEventListener("click", () => {
      slots.push({ dayOfWeek: Number(btn.dataset.day), startTime: "09:00", endTime: "12:00" });
      render();
    }));
    editor.querySelectorAll<HTMLButtonElement>(".avail-remove").forEach(btn => btn.addEventListener("click", () => {
      slots.splice(Number(btn.dataset.index), 1);
      render();
    }));
    editor.querySelectorAll<HTMLInputElement>(".avail-start").forEach(input => input.addEventListener("change", () => {
      slots[Number(input.dataset.index)].startTime = input.value;
    }));
    editor.querySelectorAll<HTMLInputElement>(".avail-end").forEach(input => input.addEventListener("change", () => {
      slots[Number(input.dataset.index)].endTime = input.value;
    }));
  };

  const load = async () => {
    try {
      const data = await get("/api/tutor/availability") as { slots: Slot[]; reserved: Reserved[]; timeZone: string };
      slots = data.slots.map(s => ({ dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime }));
      reserved = data.reserved;
      const hint = document.getElementById("availability-tz-hint");
      if (hint) hint.textContent = isAr
        ? `الأوقات بتوقيت ${data.timeZone}. الفترات التي تحتوي دروساً محجوزة تكون مقفلة.`
        : `Times are in ${data.timeZone}. Windows holding booked lessons are locked.`;
      render();
    } catch (err) {
      editor.innerHTML = `<p class="dash-empty">${isAr ? "تعذّر تحميل الأوقات" : "Could not load availability"}</p>`;
    }
  };

  document.getElementById("availability-save")?.addEventListener("click", async () => {
    const btn = document.getElementById("availability-save") as HTMLButtonElement;
    const status = document.getElementById("availability-save-status");
    btn.disabled = true;
    if (status) { status.textContent = ""; status.classList.remove("settings-save-status--error"); }
    try {
      const bad = slots.find(s => toMin(s.startTime) >= toMin(s.endTime));
      if (bad) throw new Error(isAr ? "كل فترة يجب أن تنتهي بعد بدايتها." : "Each window must end after it starts.");
      await put("/api/tutor/availability", { slots });
      if (status) status.textContent = isAr ? "تم الحفظ ✓" : "Saved ✓";
      await load();
    } catch (err) {
      if (status) {
        status.textContent = err instanceof Error ? err.message : (isAr ? "تعذّر الحفظ" : "Could not save");
        status.classList.add("settings-save-status--error");
      }
    }
    btn.disabled = false;
  });

  // ── Leave ──
  const leaveList = document.getElementById("leave-list");
  const loadLeaves = async () => {
    if (!leaveList) return;
    try {
      const data = await get("/api/tutor/leave") as {
        leaves: Array<{ id: string; startsAt: string; endsAt: string; reason: string | null; status: string; isActive: boolean; isUpcoming: boolean }>
      };
      const active = data.leaves.find(l => l.isActive);
      const banner = document.getElementById("leave-active-banner");
      const bannerText = document.getElementById("leave-active-banner-text");
      if (banner && bannerText) {
        banner.classList.toggle("hidden", !active);
        if (active) {
          bannerText.textContent = isAr
            ? `أنت في إجازة حتى ${formatDate(active.endsAt, lang)} — ملفك مخفي من البحث والحجوزات متوقفة مؤقتاً.`
            : `You are on leave until ${formatDate(active.endsAt, lang)} — your profile is hidden from search and new bookings are paused.`;
        }
      }
      const visible = data.leaves.filter(l => l.status !== "cancelled");
      leaveList.innerHTML = visible.length ? visible.map(l => `
        <div class="leave-item${l.isActive ? " leave-item--active" : ""}">
          <div class="leave-item-info">
            ${iconSvg("plane", "leave-item-icon")}
            <div>
              <strong>${formatDate(l.startsAt, lang)} → ${formatDate(l.endsAt, lang)}</strong>
              ${l.reason ? `<span class="leave-item-reason">${esc(l.reason)}</span>` : ""}
            </div>
          </div>
          <div class="leave-item-side">
            <span class="badge ${l.isActive ? "badge--warning" : "badge--neutral"}">${l.isActive ? (isAr ? "نشطة الآن" : "Active now") : (isAr ? "مجدولة" : "Scheduled")}</span>
            ${new Date(l.endsAt) > new Date() ? `<button class="btn btn-ghost btn-sm leave-cancel" data-id="${esc(l.id)}">${isAr ? "إلغاء" : "Cancel"}</button>` : ""}
          </div>
        </div>`).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد إجازات مجدولة" : "No leave scheduled"}</p>`;

      leaveList.querySelectorAll<HTMLButtonElement>(".leave-cancel").forEach(btn => btn.addEventListener("click", async () => {
        if (!await confirmModal({ title: isAr ? "إلغاء الإجازة" : "Cancel leave", message: isAr ? "ستعود متاحاً للحجز فوراً." : "You'll be bookable again immediately.", confirmLabel: isAr ? "إلغاء الإجازة" : "Cancel leave", danger: true })) return;
        try { await post(`/api/tutor/leave/${btn.dataset.id}/cancel`, {}); await loadLeaves(); }
        catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      }));
    } catch { /* */ }
  };

  document.getElementById("leave-submit")?.addEventListener("click", async () => {
    const btn = document.getElementById("leave-submit") as HTMLButtonElement;
    const status = document.getElementById("leave-status");
    const affectedEl = document.getElementById("leave-affected");
    const start = (document.getElementById("leave-start") as HTMLInputElement)?.value;
    const end = (document.getElementById("leave-end") as HTMLInputElement)?.value;
    const reason = (document.getElementById("leave-reason") as HTMLInputElement)?.value.trim();
    if (status) { status.textContent = ""; status.classList.remove("settings-save-status--error"); }
    if (!start || !end) {
      if (status) { status.textContent = isAr ? "حدد تاريخي البداية والنهاية." : "Pick start and end dates."; status.classList.add("settings-save-status--error"); }
      return;
    }
    btn.disabled = true;
    try {
      const data = await post("/api/tutor/leave", {
        startsAt: `${start}T00:00:00.000Z`,
        endsAt: `${end}T23:59:59.999Z`,
        reason
      }) as { affectedSessions: Array<{ id: string; scheduledAt: string; studentName: string }> };
      if (status) status.textContent = isAr ? "تمت جدولة الإجازة ✓" : "Leave scheduled ✓";
      if (affectedEl) {
        if (data.affectedSessions.length) {
          affectedEl.classList.remove("hidden");
          affectedEl.innerHTML = `
            <div class="leave-affected-head">${iconSvg("alert", "inline-icon")} ${isAr
              ? `لديك ${data.affectedSessions.length} درس محجوز خلال هذه الفترة — أعد جدولته أو ألغه من تبويب الجلسات:`
              : `You have ${data.affectedSessions.length} booked lesson(s) during this leave — rebook or cancel them from the Sessions tab:`}</div>
            ${data.affectedSessions.map(s => `<div class="leave-affected-row">${esc(s.studentName)} — ${formatDate(s.scheduledAt, lang)}</div>`).join("")}`;
        } else {
          affectedEl.classList.add("hidden");
          affectedEl.innerHTML = "";
        }
      }
      (document.getElementById("leave-start") as HTMLInputElement).value = "";
      (document.getElementById("leave-end") as HTMLInputElement).value = "";
      (document.getElementById("leave-reason") as HTMLInputElement).value = "";
      await loadLeaves();
    } catch (err) {
      if (status) {
        status.textContent = err instanceof Error ? err.message : (isAr ? "تعذّرت الجدولة" : "Could not schedule leave");
        status.classList.add("settings-save-status--error");
      }
    }
    btn.disabled = false;
  });

  void load();
  void loadLeaves();
};

// ── Payout methods (tutor earnings) ───────────────────────────────────────────

const initPayoutMethodsPanel = (lang: string) => {
  const root = document.getElementById("payout-methods");
  if (!root) return;
  const isAr = lang === "ar";

  type Method = {
    provider: string; isDefault: boolean; onboardingComplete: boolean;
    payoutsEnabled: boolean; paypalEmail: string | null; disabledReason: string | null;
  };

  const load = async () => {
    try {
      const data = await get("/api/tutor/payout-methods") as {
        providers: Array<{ id: string; label: string; labelAr: string; kind: string }>;
        methods: Method[];
      };
      const byProvider = new Map(data.methods.map(m => [m.provider, m]));
      const stripe = byProvider.get("stripe");
      const paypal = byProvider.get("paypal");

      const statusBadge = (m: Method | undefined) => !m
        ? `<span class="badge badge--neutral">${isAr ? "غير مفعّل" : "Not set up"}</span>`
        : m.payoutsEnabled
          ? `<span class="badge badge--success">${isAr ? "جاهز للتحويلات" : "Payouts enabled"}</span>`
          : `<span class="badge badge--warning">${isAr ? "بانتظار الإكمال" : "Setup incomplete"}</span>`;

      const defaultControl = (m: Method | undefined, provider: string) => !m ? "" : m.isDefault
        ? `<span class="payout-default-tag">${iconSvg("check", "btn-icon")} ${isAr ? "الطريقة الافتراضية" : "Default method"}</span>`
        : `<button class="btn btn-ghost btn-sm payout-make-default" data-provider="${provider}">${isAr ? "اجعلها الافتراضية" : "Make default"}</button>`;

      root.innerHTML = `
        <div class="payout-card${stripe?.isDefault ? " payout-card--default" : ""}">
          <div class="payout-card-head">
            <span class="payout-logo payout-logo--stripe">Stripe</span>
            ${statusBadge(stripe)}
          </div>
          <p class="payout-card-desc">${isAr ? "تحويلات بنكية آلية عبر Stripe Connect — يُنصح به." : "Automated bank payouts through Stripe Connect — recommended."}</p>
          <div class="payout-card-actions">
            <button class="btn ${stripe ? "btn-outline" : "btn-primary"} btn-sm" id="payout-stripe-btn">
              ${stripe ? (isAr ? "تحديث بيانات Stripe" : "Manage Stripe setup") : (isAr ? "ربط حساب Stripe" : "Connect Stripe")}
            </button>
            ${defaultControl(stripe, "stripe")}
            ${stripe && !stripe.isDefault ? `<button class="btn btn-ghost btn-sm payout-remove" data-provider="stripe">${isAr ? "إزالة" : "Remove"}</button>` : ""}
          </div>
        </div>

        <div class="payout-card${paypal?.isDefault ? " payout-card--default" : ""}">
          <div class="payout-card-head">
            <span class="payout-logo payout-logo--paypal">PayPal</span>
            ${statusBadge(paypal)}
          </div>
          <p class="payout-card-desc">${isAr ? "استلم أرباحك على بريد PayPal الخاص بك." : "Receive your earnings to your PayPal email."}</p>
          <div class="payout-paypal-row">
            <input type="email" class="form-input" id="payout-paypal-email" dir="ltr"
              placeholder="${isAr ? "بريد PayPal" : "PayPal email"}" value="${esc(paypal?.paypalEmail ?? "")}" />
            <button class="btn ${paypal ? "btn-outline" : "btn-primary"} btn-sm" id="payout-paypal-save">
              ${paypal ? (isAr ? "تحديث" : "Update") : (isAr ? "حفظ" : "Save")}
            </button>
          </div>
          <div class="payout-card-actions">
            ${defaultControl(paypal, "paypal")}
            ${paypal && !paypal.isDefault ? `<button class="btn btn-ghost btn-sm payout-remove" data-provider="paypal">${isAr ? "إزالة" : "Remove"}</button>` : ""}
          </div>
        </div>`;

      document.getElementById("payout-stripe-btn")?.addEventListener("click", async (e) => {
        const btn = e.currentTarget as HTMLButtonElement;
        btn.disabled = true;
        try {
          const resp = await post("/api/tutor/payout-methods/stripe", {}) as { url: string };
          window.location.href = resp.url;
        } catch (err) { showSiteError(err instanceof Error ? err.message : ""); btn.disabled = false; }
      });

      document.getElementById("payout-paypal-save")?.addEventListener("click", async (e) => {
        const btn = e.currentTarget as HTMLButtonElement;
        const email = (document.getElementById("payout-paypal-email") as HTMLInputElement).value.trim();
        btn.disabled = true;
        try {
          await post("/api/tutor/payout-methods/paypal", { email });
          await load();
        } catch (err) { showSiteError(err instanceof Error ? err.message : ""); btn.disabled = false; }
      });

      root.querySelectorAll<HTMLButtonElement>(".payout-make-default").forEach(btn => btn.addEventListener("click", async () => {
        try { await post(`/api/tutor/payout-methods/${btn.dataset.provider}/default`, {}); await load(); }
        catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      }));

      root.querySelectorAll<HTMLButtonElement>(".payout-remove").forEach(btn => btn.addEventListener("click", async () => {
        if (!await confirmModal({ title: isAr ? "إزالة طريقة الدفع" : "Remove payout method", message: isAr ? "يمكنك إعدادها من جديد في أي وقت." : "You can set it up again any time.", confirmLabel: isAr ? "إزالة" : "Remove", danger: true })) return;
        try { await api("DELETE", `/api/tutor/payout-methods/${btn.dataset.provider}`); await load(); }
        catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      }));
    } catch {
      root.innerHTML = `<p class="dash-empty">${isAr ? "تعذّر تحميل طرق الدفع" : "Could not load payout methods"}</p>`;
    }
  };

  void load();
};

// ── Session QA thread & recording ─────────────────────────────────────────────

const openQaThreadModal = (sessionId: string, opts: { lang: string; canInternal: boolean; title?: string }) => {
  const isAr = opts.lang === "ar";
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal qa-modal" role="dialog" aria-modal="true">
      <div class="modal-head">
        <h2>${opts.title ?? (isAr ? "ملاحظات الجودة" : "Quality feedback")}</h2>
        <button class="modal-close" aria-label="${isAr ? "إغلاق" : "Close"}">${iconSvg("x", "btn-icon")}</button>
      </div>
      <p class="qa-modal-hint">${isAr ? "سلسلة داخلية بين فريق المراجعة والمعلم — لا يراها الطالب." : "Internal thread between the review team and the tutor — never visible to students."}</p>
      <div class="qa-thread" id="qa-thread"><div class="spinner"></div></div>
      <div class="qa-compose">
        <textarea class="form-textarea" id="qa-input" rows="2" placeholder="${isAr ? "اكتب ملاحظة..." : "Write feedback…"}"></textarea>
        ${opts.canInternal ? `<label class="checkbox-label qa-internal-toggle"><input type="checkbox" id="qa-internal" /> ${isAr ? "ملاحظة داخلية (للفريق فقط)" : "Internal note (staff only)"}</label>` : ""}
        <button class="btn btn-primary btn-sm" id="qa-send">${iconSvg("send", "btn-icon")} ${isAr ? "إرسال" : "Send"}</button>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  const close = () => backdrop.remove();
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
  backdrop.querySelector(".modal-close")?.addEventListener("click", close);
  document.addEventListener("keydown", function onKey(e) {
    if (e.key === "Escape") { close(); document.removeEventListener("keydown", onKey); }
  });

  const threadEl = backdrop.querySelector("#qa-thread") as HTMLElement;
  const loadThread = async () => {
    try {
      const data = await get(`/api/sessions/${sessionId}/comments`) as {
        comments: Array<{ id: string; body: string; internalOnly: boolean; createdAt: string; author: { name: string; role: string } }>
      };
      threadEl.innerHTML = data.comments.length ? data.comments.map(c => `
        <div class="qa-comment${c.internalOnly ? " qa-comment--internal" : ""}">
          <div class="qa-comment-meta">
            <strong>${esc(c.author.name)}</strong>
            <span class="qa-comment-role">${esc(c.author.role)}</span>
            ${c.internalOnly ? `<span class="badge badge--warning">${isAr ? "داخلي" : "Internal"}</span>` : ""}
            <span class="qa-comment-time">${formatDate(c.createdAt, opts.lang)}</span>
          </div>
          <p>${esc(c.body)}</p>
        </div>`).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد ملاحظات بعد" : "No feedback yet"}</p>`;
      threadEl.scrollTop = threadEl.scrollHeight;
    } catch {
      threadEl.innerHTML = `<p class="dash-empty">${isAr ? "تعذّر التحميل" : "Could not load thread"}</p>`;
    }
  };

  backdrop.querySelector("#qa-send")?.addEventListener("click", async () => {
    const input = backdrop.querySelector("#qa-input") as HTMLTextAreaElement;
    const body = input.value.trim();
    if (!body) return;
    const internalOnly = (backdrop.querySelector("#qa-internal") as HTMLInputElement | null)?.checked ?? false;
    try {
      await post(`/api/sessions/${sessionId}/comments`, { body, internalOnly });
      input.value = "";
      await loadThread();
    } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
  });

  void loadThread();
};

const openRecordingModal = (sessionId: string, currentUrl: string, lang: string, onSaved: (url: string | null) => void) => {
  const isAr = lang === "ar";
  openModal({
    title: isAr ? "تسجيل الجلسة" : "Session recording",
    bodyHtml: `
      <p class="form-hint">${isAr ? "يُرفق التسجيل بالجلسة لمراجعة الجودة من قبل الإدارة." : "The recording is attached to this session for administrative quality review."}</p>
      <div class="form-group">
        <label class="form-label">${isAr ? "رابط التسجيل" : "Recording URL"}</label>
        <input type="url" class="form-input" id="rec-url" dir="ltr" value="${esc(currentUrl)}" placeholder="https://…" />
      </div>`,
    confirmLabel: isAr ? "حفظ" : "Save",
    cancelLabel: isAr ? "إلغاء" : "Cancel",
    onConfirm: async (root) => {
      const url = (root.querySelector("#rec-url") as HTMLInputElement).value.trim();
      await post(`/api/sessions/${sessionId}/recording`, { recordingUrl: url });
      onSaved(url || null);
      return true;
    }
  });
};

// ── Curriculum studio (tutor) ─────────────────────────────────────────────────

type StudioQuestion = { id: string; type: string; prompt: string; options: string[]; correctIndex: number; explanation: string | null; sortOrder: number };
type StudioLesson = { id: string; title: string; content: string; sortOrder: number; questions: StudioQuestion[] };

const curriculumStatusBadge = (status: string, isAr: boolean): string => {
  const map: Record<string, [string, string, string]> = {
    draft: ["badge--neutral", "Draft", "مسودة"],
    pending_review: ["badge--warning", "In review", "قيد المراجعة"],
    approved: ["badge--success", "Live", "منشور"],
    rejected: ["badge--danger", "Needs changes", "يحتاج تعديلات"]
  };
  const [cls, en, ar] = map[status] ?? map.draft;
  return `<span class="badge ${cls}">${isAr ? ar : en}</span>`;
};

const initCurriculumStudio = (lang: string) => {
  const listEl = document.getElementById("studio-list");
  const editorEl = document.getElementById("studio-editor");
  if (!listEl || !editorEl) return;
  const isAr = lang === "ar";

  const subjectOptions = (selected: string) => Object.keys(SUBJECTS).map(id =>
    `<option value="${id}"${id === selected ? " selected" : ""}>${subjectLabel(id, lang)}</option>`).join("");

  const showList = () => { editorEl.classList.add("hidden"); listEl.classList.remove("hidden"); void loadList(); };

  const loadList = async () => {
    try {
      const data = await get("/api/studio/curricula") as {
        curricula: Array<{ id: string; title: string; description: string | null; subjectId: string; status: string; reviewNote: string | null; lessonCount: number; questionCount: number }>
      };
      listEl.innerHTML = data.curricula.length ? data.curricula.map(c => `
        <div class="studio-item">
          <div class="studio-item-main">
            <div class="studio-item-head">
              <strong>${esc(c.title)}</strong>
              ${curriculumStatusBadge(c.status, isAr)}
            </div>
            <span class="studio-item-meta">${subjectLabel(c.subjectId, lang)} · ${c.lessonCount} ${isAr ? "درس" : "lessons"} · ${c.questionCount} ${isAr ? "سؤال" : "questions"}</span>
            ${c.status === "rejected" && c.reviewNote ? `<p class="studio-review-note">${iconSvg("alert", "btn-icon")} ${esc(c.reviewNote)}</p>` : ""}
          </div>
          <div class="studio-item-actions">
            <button class="btn btn-primary btn-sm studio-open" data-id="${esc(c.id)}">${iconSvg("pencil", "btn-icon")} ${isAr ? "فتح" : "Open"}</button>
            ${c.status === "draft" || c.status === "rejected" ? `<button class="btn btn-outline btn-sm studio-submit" data-id="${esc(c.id)}">${isAr ? "إرسال للمراجعة" : "Submit for review"}</button>` : ""}
            <button class="btn btn-ghost btn-sm studio-delete" data-id="${esc(c.id)}" aria-label="${isAr ? "حذف" : "Delete"}">${iconSvg("trash", "btn-icon")}</button>
          </div>
        </div>`).join("")
        : `<div class="dash-empty-cta">
            <p class="dash-empty">${isAr ? "لا توجد مناهج بعد" : "No curricula yet"}</p>
            <p class="form-hint">${isAr ? "ابدأ أول منهج تفاعلي لطلابك — دروس، ثم أسئلة، ثم أرسله للمراجعة." : "Start your first interactive course — write lessons, add quiz questions, then submit for review."}</p>
          </div>`;

      listEl.querySelectorAll<HTMLButtonElement>(".studio-open").forEach(btn =>
        btn.addEventListener("click", () => void openEditor(btn.dataset.id!)));
      listEl.querySelectorAll<HTMLButtonElement>(".studio-submit").forEach(btn =>
        btn.addEventListener("click", async () => {
          if (!await confirmModal({
            title: isAr ? "إرسال للمراجعة" : "Submit for review",
            message: isAr ? "سيراجع فريق المنصة الدروس والأسئلة قبل نشرها لطلابك." : "Our team will review the lessons and questions before they go live for your students.",
            confirmLabel: isAr ? "إرسال" : "Submit"
          })) return;
          try { await post(`/api/studio/curricula/${btn.dataset.id}/submit`, {}); await loadList(); }
          catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
        }));
      listEl.querySelectorAll<HTMLButtonElement>(".studio-delete").forEach(btn =>
        btn.addEventListener("click", async () => {
          if (!await confirmModal({ title: isAr ? "حذف المنهج" : "Delete curriculum", message: isAr ? "سيُحذف المنهج بكل دروسه وأسئلته نهائياً." : "The curriculum with all its lessons and questions will be permanently deleted.", confirmLabel: isAr ? "حذف" : "Delete", danger: true })) return;
          try { await api("DELETE", `/api/studio/curricula/${btn.dataset.id}`); await loadList(); }
          catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
        }));
    } catch {
      listEl.innerHTML = `<p class="dash-empty">${isAr ? "تعذّر التحميل" : "Could not load curricula"}</p>`;
    }
  };

  const openQuestionModal = (lessonId: string, existing: StudioQuestion | null, onSaved: () => void) => {
    const isTf = existing?.type === "true_false";
    const opts = existing?.options ?? ["", "", "", ""];
    openModal({
      title: existing ? (isAr ? "تعديل السؤال" : "Edit question") : (isAr ? "سؤال جديد" : "New question"),
      bodyHtml: `
        <div class="form-group">
          <label class="form-label">${isAr ? "نوع السؤال" : "Question type"}</label>
          <select class="form-select" id="q-type">
            <option value="multiple_choice"${!isTf ? " selected" : ""}>${isAr ? "اختيار من متعدد" : "Multiple choice"}</option>
            <option value="true_false"${isTf ? " selected" : ""}>${isAr ? "صح / خطأ" : "True / False"}</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">${isAr ? "السؤال" : "Prompt"}</label>
          <textarea class="form-textarea" id="q-prompt" rows="2" maxlength="500">${esc(existing?.prompt ?? "")}</textarea>
        </div>
        <div id="q-options-wrap" class="${isTf ? "hidden" : ""}">
          <label class="form-label">${isAr ? "الخيارات (حدد الإجابة الصحيحة)" : "Options (mark the correct answer)"}</label>
          ${[0, 1, 2, 3].map(i => `
            <div class="q-option-row">
              <input type="radio" name="q-correct" value="${i}"${(existing && !isTf ? existing.correctIndex === i : i === 0) ? " checked" : ""} aria-label="${isAr ? "الإجابة الصحيحة" : "Correct answer"}" />
              <input type="text" class="form-input q-option" data-index="${i}" maxlength="200" placeholder="${isAr ? `الخيار ${i + 1}` : `Option ${i + 1}`}${i > 1 ? (isAr ? " (اختياري)" : " (optional)") : ""}" value="${esc(opts[i] ?? "")}" />
            </div>`).join("")}
        </div>
        <div id="q-tf-wrap" class="${isTf ? "" : "hidden"}">
          <label class="form-label">${isAr ? "الإجابة الصحيحة" : "Correct answer"}</label>
          <div class="q-option-row"><input type="radio" name="q-tf" value="0"${(existing?.correctIndex ?? 0) === 0 ? " checked" : ""} /> <span>${isAr ? "صح" : "True"}</span></div>
          <div class="q-option-row"><input type="radio" name="q-tf" value="1"${existing?.correctIndex === 1 ? " checked" : ""} /> <span>${isAr ? "خطأ" : "False"}</span></div>
        </div>
        <div class="form-group">
          <label class="form-label">${isAr ? "شرح الإجابة (يظهر للطالب بعد المحاولة)" : "Explanation (shown to the student after answering)"}</label>
          <textarea class="form-textarea" id="q-explanation" rows="2" maxlength="500">${esc(existing?.explanation ?? "")}</textarea>
        </div>`,
      confirmLabel: existing ? (isAr ? "حفظ" : "Save") : (isAr ? "إضافة السؤال" : "Add question"),
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      onConfirm: async (root) => {
        const type = (root.querySelector("#q-type") as HTMLSelectElement).value;
        const prompt = (root.querySelector("#q-prompt") as HTMLTextAreaElement).value.trim();
        let options: string[] = [];
        let correctIndex = 0;
        if (type === "true_false") {
          correctIndex = Number((root.querySelector("input[name='q-tf']:checked") as HTMLInputElement | null)?.value ?? 0);
        } else {
          const raw = Array.from(root.querySelectorAll<HTMLInputElement>(".q-option")).map(i => i.value.trim());
          const checkedIndex = Number((root.querySelector("input[name='q-correct']:checked") as HTMLInputElement | null)?.value ?? 0);
          if (!raw[checkedIndex]) throw new Error(isAr ? "الإجابة الصحيحة لا يمكن أن تكون خياراً فارغاً." : "The correct answer can't be an empty option.");
          // Compact empty options while keeping the right correct index.
          raw.forEach((text, i) => { if (text) { if (i === checkedIndex) correctIndex = options.length; options.push(text); } });
          if (options.length < 2) throw new Error(isAr ? "أضف خيارين على الأقل." : "Add at least two options.");
        }
        const explanation = (root.querySelector("#q-explanation") as HTMLTextAreaElement).value.trim();
        const body = { type, prompt, options, correctIndex, explanation };
        if (existing) await patch(`/api/studio/questions/${existing.id}`, body);
        else await post(`/api/studio/lessons/${lessonId}/questions`, body);
        onSaved();
        return true;
      }
    });
    const typeSel = document.getElementById("q-type") as HTMLSelectElement | null;
    typeSel?.addEventListener("change", () => {
      document.getElementById("q-options-wrap")?.classList.toggle("hidden", typeSel.value === "true_false");
      document.getElementById("q-tf-wrap")?.classList.toggle("hidden", typeSel.value !== "true_false");
    });
  };

  const openEditor = async (curriculumId: string) => {
    let activeLessonId: string | null = null;
    const render = async () => {
      const data = await get(`/api/studio/curricula/${curriculumId}`) as {
        curriculum: { id: string; title: string; description: string | null; subjectId: string; status: string; reviewNote: string | null; lessons: StudioLesson[] };
      };
      const c = data.curriculum;
      if (!activeLessonId || !c.lessons.some(l => l.id === activeLessonId)) activeLessonId = c.lessons[0]?.id ?? null;
      const active = c.lessons.find(l => l.id === activeLessonId) ?? null;

      listEl.classList.add("hidden");
      editorEl.classList.remove("hidden");
      editorEl.innerHTML = `
        <div class="studio-editor-topbar">
          <button class="btn btn-ghost btn-sm" id="studio-back">← ${isAr ? "مناهجي" : "My curricula"}</button>
          <input type="text" class="studio-title-input" id="studio-title" value="${esc(c.title)}" maxlength="120" />
          <select class="form-select form-select--sm studio-subject" id="studio-subject">${subjectOptions(c.subjectId)}</select>
          ${curriculumStatusBadge(c.status, isAr)}
          ${c.status !== "pending_review" && c.status !== "approved" ? `<button class="btn btn-primary btn-sm" id="studio-editor-submit">${isAr ? "إرسال للمراجعة" : "Submit for review"}</button>` : ""}
        </div>
        ${c.status === "rejected" && c.reviewNote ? `<p class="studio-review-note">${iconSvg("alert", "btn-icon")} ${isAr ? "ملاحظات المراجعة:" : "Reviewer feedback:"} ${esc(c.reviewNote)}</p>` : ""}
        ${c.status === "approved" ? `<p class="studio-live-note">${iconSvg("check", "btn-icon")} ${isAr ? "هذا المنهج منشور — أي تعديل سيعيده إلى مسودة ويتطلب مراجعة جديدة." : "This curriculum is live — any edit sends it back to draft and requires a new review."}</p>` : ""}
        <div class="studio-workspace">
          <aside class="studio-sidebar">
            <div class="studio-sidebar-head">
              <span>${isAr ? "الدروس" : "Lessons"}</span>
              <button class="btn btn-ghost btn-sm" id="studio-add-lesson">${iconSvg("plus", "btn-icon")}</button>
            </div>
            ${c.lessons.length ? c.lessons.map((l, i) => `
              <button class="studio-lesson-link${l.id === activeLessonId ? " studio-lesson-link--active" : ""}" data-id="${esc(l.id)}">
                <span class="studio-lesson-num">${i + 1}</span>
                <span class="studio-lesson-name">${esc(l.title)}</span>
                <span class="studio-lesson-count">${l.questions.length ? `${l.questions.length} ${isAr ? "س" : "Q"}` : ""}</span>
              </button>`).join("")
            : `<p class="studio-sidebar-empty">${isAr ? "أضف أول درس" : "Add your first lesson"}</p>`}
          </aside>
          <div class="studio-main">
            ${active ? `
              <div class="studio-lesson-toolbar">
                <input type="text" class="studio-lesson-title" id="studio-lesson-title" value="${esc(active.title)}" maxlength="120" />
                <span class="settings-save-status" id="studio-save-status"></span>
                <button class="btn btn-ghost btn-sm" id="studio-delete-lesson" aria-label="${isAr ? "حذف الدرس" : "Delete lesson"}">${iconSvg("trash", "btn-icon")}</button>
              </div>
              <textarea class="studio-content" id="studio-content" placeholder="${isAr ? "اكتب محتوى الدرس هنا… فقرات بسيطة، سطر فارغ بين كل فقرة." : "Write the lesson here… simple paragraphs, blank line between each."}">${esc(active.content)}</textarea>
              <div class="studio-questions">
                <div class="studio-questions-head">
                  <h3>${iconSvg("sparkles", "btn-icon")} ${isAr ? "أسئلة الدرس" : "Lesson quiz"}</h3>
                  <button class="btn btn-outline btn-sm" id="studio-add-question">${iconSvg("plus", "btn-icon")} ${isAr ? "سؤال" : "Add question"}</button>
                </div>
                ${active.questions.length ? active.questions.map(q => `
                  <div class="studio-question-card">
                    <div class="studio-question-head">
                      <span class="badge badge--neutral">${q.type === "true_false" ? (isAr ? "صح/خطأ" : "T/F") : (isAr ? "اختياري" : "MCQ")}</span>
                      <p>${esc(q.prompt)}</p>
                      <span class="studio-question-actions">
                        <button class="btn btn-ghost btn-sm sq-edit" data-id="${esc(q.id)}">${iconSvg("pencil", "btn-icon")}</button>
                        <button class="btn btn-ghost btn-sm sq-delete" data-id="${esc(q.id)}">${iconSvg("trash", "btn-icon")}</button>
                      </span>
                    </div>
                    <ul class="studio-question-options">
                      ${q.options.map((o, i) => `<li class="${i === q.correctIndex ? "studio-option--correct" : ""}">${i === q.correctIndex ? iconSvg("check", "btn-icon") : ""} ${esc(o)}</li>`).join("")}
                    </ul>
                  </div>`).join("")
                : `<p class="form-hint">${isAr ? "أضف أسئلة قصيرة ليتدرب عليها الطالب بعد قراءة الدرس — هذا ما يجعل التعلم ممتعاً." : "Add short questions students answer after reading — this is what makes the lesson fun."}</p>`}
              </div>
            ` : `<div class="studio-main-empty">${iconSvg("book-open", "messages-empty-icon")}<p>${isAr ? "أنشئ درساً من الشريط الجانبي للبدء." : "Create a lesson from the sidebar to get started."}</p></div>`}
          </div>
        </div>`;

      // ── wiring ──
      document.getElementById("studio-back")?.addEventListener("click", showList);
      const saveStatus = (ok: boolean, text: string) => {
        const el = document.getElementById("studio-save-status");
        if (!el) return;
        el.textContent = text;
        el.classList.toggle("settings-save-status--error", !ok);
        setTimeout(() => { if (el.textContent === text) el.textContent = ""; }, 2500);
      };

      const titleInput = document.getElementById("studio-title") as HTMLInputElement | null;
      titleInput?.addEventListener("change", async () => {
        try { await patch(`/api/studio/curricula/${curriculumId}`, { title: titleInput.value.trim() }); void render(); }
        catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      });
      (document.getElementById("studio-subject") as HTMLSelectElement | null)?.addEventListener("change", async (e) => {
        try { await patch(`/api/studio/curricula/${curriculumId}`, { subjectId: (e.target as HTMLSelectElement).value }); void render(); }
        catch { /* */ }
      });
      document.getElementById("studio-editor-submit")?.addEventListener("click", async () => {
        try { await post(`/api/studio/curricula/${curriculumId}/submit`, {}); void render(); }
        catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      });
      document.getElementById("studio-add-lesson")?.addEventListener("click", async () => {
        const title = await promptModal({ title: isAr ? "درس جديد" : "New lesson", label: isAr ? "عنوان الدرس" : "Lesson title", confirmLabel: isAr ? "إنشاء" : "Create" });
        if (!title) return;
        try {
          const resp = await post(`/api/studio/curricula/${curriculumId}/lessons`, { title }) as { lesson: { id: string } };
          activeLessonId = resp.lesson.id;
          void render();
        } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      });
      editorEl.querySelectorAll<HTMLButtonElement>(".studio-lesson-link").forEach(btn =>
        btn.addEventListener("click", () => { activeLessonId = btn.dataset.id!; void render(); }));

      if (active) {
        const lessonTitle = document.getElementById("studio-lesson-title") as HTMLInputElement;
        lessonTitle.addEventListener("change", async () => {
          try { await patch(`/api/studio/lessons/${active.id}`, { title: lessonTitle.value.trim() }); saveStatus(true, isAr ? "تم الحفظ ✓" : "Saved ✓"); void render(); }
          catch (err) { saveStatus(false, isAr ? "تعذّر الحفظ" : "Save failed"); }
        });
        const content = document.getElementById("studio-content") as HTMLTextAreaElement;
        let lastSaved = content.value;
        const saveContent = async () => {
          if (content.value === lastSaved) return;
          try {
            await patch(`/api/studio/lessons/${active.id}`, { content: content.value });
            lastSaved = content.value;
            saveStatus(true, isAr ? "تم الحفظ ✓" : "Saved ✓");
          } catch { saveStatus(false, isAr ? "تعذّر الحفظ" : "Save failed"); }
        };
        content.addEventListener("blur", saveContent);
        content.addEventListener("input", () => {
          const el = document.getElementById("studio-save-status");
          if (el) el.textContent = isAr ? "غير محفوظ…" : "Unsaved…";
        });
        window.setTimeout(function autosave() {
          if (document.getElementById("studio-content") === content) {
            void saveContent();
            window.setTimeout(autosave, 8000);
          }
        }, 8000);

        document.getElementById("studio-delete-lesson")?.addEventListener("click", async () => {
          if (!await confirmModal({ title: isAr ? "حذف الدرس" : "Delete lesson", message: isAr ? "سيُحذف الدرس وأسئلته." : "The lesson and its questions will be deleted.", confirmLabel: isAr ? "حذف" : "Delete", danger: true })) return;
          try { await api("DELETE", `/api/studio/lessons/${active.id}`); activeLessonId = null; void render(); }
          catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
        });
        document.getElementById("studio-add-question")?.addEventListener("click", async () => {
          await saveContent();
          openQuestionModal(active.id, null, () => void render());
        });
        editorEl.querySelectorAll<HTMLButtonElement>(".sq-edit").forEach(btn => btn.addEventListener("click", () => {
          const q = active.questions.find(x => x.id === btn.dataset.id);
          if (q) openQuestionModal(active.id, q, () => void render());
        }));
        editorEl.querySelectorAll<HTMLButtonElement>(".sq-delete").forEach(btn => btn.addEventListener("click", async () => {
          if (!await confirmModal({ title: isAr ? "حذف السؤال" : "Delete question", message: isAr ? "لا يمكن التراجع عن هذا." : "This cannot be undone.", confirmLabel: isAr ? "حذف" : "Delete", danger: true })) return;
          try { await api("DELETE", `/api/studio/questions/${btn.dataset.id}`); void render(); }
          catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
        }));
      }
    };
    try { await render(); } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
  };

  document.getElementById("studio-new-btn")?.addEventListener("click", async () => {
    const title = await promptModal({ title: isAr ? "منهج جديد" : "New curriculum", label: isAr ? "اسم المنهج" : "Curriculum title", placeholder: isAr ? "مثال: أساسيات التجويد" : "e.g. Tajweed foundations", confirmLabel: isAr ? "إنشاء" : "Create" });
    if (!title) return;
    try {
      const resp = await post("/api/studio/curricula", { title }) as { curriculum: { id: string } };
      await openEditor(resp.curriculum.id);
    } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
  });

  void loadList();
};

// ── Interactive lessons (student) ─────────────────────────────────────────────

const initLearnPanel = (lang: string) => {
  const root = document.getElementById("learn-root");
  if (!root) return;
  const isAr = lang === "ar";

  const loadCurricula = async () => {
    try {
      const data = await get("/api/learn/curricula") as {
        curricula: Array<{ id: string; title: string; description: string | null; subjectId: string; tutor: { name: string }; lessonCount: number; questionCount: number; completedLessons: number }>
      };
      root.innerHTML = data.curricula.length ? `<div class="learn-grid">${data.curricula.map(c => {
        const pct = c.lessonCount ? Math.round((c.completedLessons / c.lessonCount) * 100) : 0;
        return `
        <button class="learn-card" data-id="${esc(c.id)}">
          <div class="learn-card-head">
            <strong>${esc(c.title)}</strong>
            <span class="badge badge--neutral">${subjectLabel(c.subjectId, lang)}</span>
          </div>
          <span class="learn-card-tutor">${iconSvg("graduation", "btn-icon")} ${esc(c.tutor.name)}</span>
          ${c.description ? `<p class="learn-card-desc">${esc(c.description)}</p>` : ""}
          <div class="learn-progress"><div class="learn-progress-bar" style="width:${pct}%"></div></div>
          <span class="learn-card-meta">${c.completedLessons}/${c.lessonCount} ${isAr ? "درس" : "lessons"} · ${c.questionCount} ${isAr ? "سؤال" : "questions"}</span>
        </button>`;
      }).join("")}</div>`
      : `<div class="dash-empty-cta">
          <p class="dash-empty">${isAr ? "لا توجد دروس تفاعلية بعد" : "No interactive lessons yet"}</p>
          <p class="form-hint">${isAr ? "بعد أول حجز مع معلم، ستظهر مناهجه المعتمدة هنا." : "Once you book with a tutor, their approved courses appear here."}</p>
        </div>`;
      root.querySelectorAll<HTMLButtonElement>(".learn-card").forEach(btn =>
        btn.addEventListener("click", () => void openCurriculum(btn.dataset.id!)));
    } catch {
      root.innerHTML = `<p class="dash-empty">${isAr ? "تعذّر التحميل" : "Could not load lessons"}</p>`;
    }
  };

  const openCurriculum = async (id: string) => {
    try {
      const data = await get(`/api/learn/curricula/${id}`) as {
        curriculum: { id: string; title: string; description: string | null; tutor: { name: string }; lessons: Array<{ id: string; title: string; questionCount: number; best: { score: number; total: number } | null }> };
      };
      const c = data.curriculum;
      root.innerHTML = `
        <button class="btn btn-ghost btn-sm" id="learn-back">← ${isAr ? "كل المناهج" : "All courses"}</button>
        <h3 class="learn-course-title">${esc(c.title)} <span class="learn-card-tutor">· ${esc(c.tutor.name)}</span></h3>
        ${c.description ? `<p class="learn-card-desc">${esc(c.description)}</p>` : ""}
        <div class="learn-lesson-list">
          ${c.lessons.map((l, i) => `
            <button class="learn-lesson" data-id="${esc(l.id)}">
              <span class="studio-lesson-num">${i + 1}</span>
              <span class="learn-lesson-title">${esc(l.title)}</span>
              ${l.best
                ? `<span class="learn-lesson-score${l.best.score === l.best.total ? " learn-lesson-score--perfect" : ""}">${l.best.score === l.best.total ? "⭐" : ""} ${l.best.score}/${l.best.total}</span>`
                : l.questionCount ? `<span class="learn-lesson-score learn-lesson-score--new">${l.questionCount} ${isAr ? "سؤال" : "Q"}</span>` : ""}
            </button>`).join("")}
        </div>`;
      document.getElementById("learn-back")?.addEventListener("click", () => void loadCurricula());
      root.querySelectorAll<HTMLButtonElement>(".learn-lesson").forEach(btn =>
        btn.addEventListener("click", () => void openLesson(btn.dataset.id!, () => void openCurriculum(id))));
    } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
  };

  const openLesson = async (lessonId: string, onClose: () => void) => {
    try {
      const data = await get(`/api/learn/lessons/${lessonId}`) as {
        lesson: { id: string; title: string; content: string; curriculumTitle: string; questions: Array<{ id: string; type: string; prompt: string; options: string[] }> };
      };
      const l = data.lesson;
      const backdrop = document.createElement("div");
      backdrop.className = "modal-backdrop";
      document.body.appendChild(backdrop);
      const close = () => { backdrop.remove(); onClose(); };

      const renderReading = () => {
        backdrop.innerHTML = `
          <div class="modal learn-modal" role="dialog" aria-modal="true">
            <div class="modal-head">
              <h2>${esc(l.title)}</h2>
              <button class="modal-close" aria-label="close">${iconSvg("x", "btn-icon")}</button>
            </div>
            <p class="qa-modal-hint">${esc(l.curriculumTitle)}</p>
            <div class="learn-content">${l.content.split(/\n{2,}/).map(p => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("")}</div>
            <div class="modal-actions">
              ${l.questions.length
                ? `<button class="btn btn-primary" id="learn-start-quiz">${iconSvg("sparkles", "btn-icon")} ${isAr ? `ابدأ الاختبار (${l.questions.length} أسئلة)` : `Start quiz (${l.questions.length} questions)`}</button>`
                : `<button class="btn btn-primary" id="learn-done">${isAr ? "تم" : "Done"}</button>`}
            </div>
          </div>`;
        backdrop.querySelector(".modal-close")?.addEventListener("click", close);
        backdrop.querySelector("#learn-done")?.addEventListener("click", close);
        backdrop.querySelector("#learn-start-quiz")?.addEventListener("click", () => renderQuiz(0, 0, []));
      };

      const answers: Array<{ questionId: string; answerIndex: number }> = [];
      const renderQuiz = (index: number, correctSoFar: number, _history: unknown[]) => {
        const q = l.questions[index];
        if (!q) { void finishQuiz(correctSoFar); return; }
        const pct = Math.round((index / l.questions.length) * 100);
        backdrop.innerHTML = `
          <div class="modal learn-modal quiz-modal" role="dialog" aria-modal="true">
            <div class="modal-head">
              <div class="quiz-progress"><div class="quiz-progress-bar" style="width:${pct}%"></div></div>
              <button class="modal-close" aria-label="close">${iconSvg("x", "btn-icon")}</button>
            </div>
            <p class="quiz-counter">${isAr ? `سؤال ${index + 1} من ${l.questions.length}` : `Question ${index + 1} of ${l.questions.length}`}</p>
            <h2 class="quiz-prompt">${esc(q.prompt)}</h2>
            <div class="quiz-options">
              ${q.options.map((o, i) => `<button class="quiz-option" data-index="${i}">${esc(o)}</button>`).join("")}
            </div>
            <div class="quiz-feedback hidden" id="quiz-feedback"></div>
            <div class="modal-actions">
              <button class="btn btn-primary hidden" id="quiz-next">${index + 1 === l.questions.length ? (isAr ? "النتيجة" : "See result") : (isAr ? "التالي" : "Next")}</button>
            </div>
          </div>`;
        backdrop.querySelector(".modal-close")?.addEventListener("click", close);
        let answered = false;
        let gotItRight = false;
        backdrop.querySelectorAll<HTMLButtonElement>(".quiz-option").forEach(btn => btn.addEventListener("click", async () => {
          if (answered) return;
          answered = true;
          const answerIndex = Number(btn.dataset.index);
          answers.push({ questionId: q.id, answerIndex });
          try {
            const verdict = await post(`/api/learn/questions/${q.id}/check`, { answerIndex }) as { correct: boolean; correctIndex: number; explanation: string | null };
            gotItRight = verdict.correct;
            backdrop.querySelectorAll<HTMLButtonElement>(".quiz-option").forEach(o => {
              const i = Number(o.dataset.index);
              o.disabled = true;
              if (i === verdict.correctIndex) o.classList.add("quiz-option--correct");
              if (i === answerIndex && !verdict.correct) o.classList.add("quiz-option--wrong");
            });
            const fb = backdrop.querySelector("#quiz-feedback") as HTMLElement;
            fb.classList.remove("hidden");
            fb.classList.toggle("quiz-feedback--correct", verdict.correct);
            fb.innerHTML = verdict.correct
              ? `<strong>${isAr ? "أحسنت! ✨" : "Nice one! ✨"}</strong>${verdict.explanation ? `<span>${esc(verdict.explanation)}</span>` : ""}`
              : `<strong>${isAr ? "ليست صحيحة" : "Not quite"}</strong><span>${isAr ? "الإجابة الصحيحة:" : "Correct answer:"} ${esc(q.options[verdict.correctIndex] ?? "")}</span>${verdict.explanation ? `<span>${esc(verdict.explanation)}</span>` : ""}`;
            (backdrop.querySelector("#quiz-next") as HTMLButtonElement).classList.remove("hidden");
          } catch (err) { showSiteError(err instanceof Error ? err.message : ""); answered = false; }
        }));
        backdrop.querySelector("#quiz-next")?.addEventListener("click", () =>
          renderQuiz(index + 1, correctSoFar + (gotItRight ? 1 : 0), []));
      };

      const finishQuiz = async (clientScore: number) => {
        let score = clientScore;
        let total = l.questions.length;
        try {
          const resp = await post(`/api/learn/lessons/${l.id}/attempt`, { answers }) as { attempt: { score: number; total: number } };
          score = resp.attempt.score;
          total = resp.attempt.total;
        } catch { /* attempt record failed — still show local result */ }
        const perfect = score === total;
        backdrop.innerHTML = `
          <div class="modal learn-modal quiz-modal quiz-result" role="dialog" aria-modal="true">
            <div class="quiz-result-emoji">${perfect ? "🏆" : score / total >= 0.6 ? "🌟" : "💪"}</div>
            <h2>${perfect ? (isAr ? "علامة كاملة!" : "Perfect score!") : (isAr ? "أكملت الاختبار" : "Quiz complete")}</h2>
            <p class="quiz-result-score">${score} / ${total}</p>
            <p class="qa-modal-hint">${perfect
              ? (isAr ? "ما شاء الله — أتقنت هذا الدرس." : "Masha'Allah — you've mastered this lesson.")
              : (isAr ? "أعد المحاولة لتحسين نتيجتك — أفضل نتيجة تُحفظ." : "Try again to beat your score — your best result is saved.")}</p>
            <div class="modal-actions">
              <button class="btn btn-outline" id="quiz-retry">${isAr ? "إعادة المحاولة" : "Retry"}</button>
              <button class="btn btn-primary" id="quiz-finish">${isAr ? "تم" : "Done"}</button>
            </div>
          </div>`;
        backdrop.querySelector("#quiz-retry")?.addEventListener("click", () => { answers.length = 0; renderQuiz(0, 0, []); });
        backdrop.querySelector("#quiz-finish")?.addEventListener("click", close);
      };

      renderReading();
    } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
  };

  void loadCurricula();
};

// ── Student dashboard ─────────────────────────────────────────────────────────

const initStudentDashboard = () => {
  const dash = document.getElementById("student-dashboard");
  if (!dash) return;

  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";

  initDashTabs("student-dashboard");

  const loadOverview = async () => {
    try {
      const [sessionsData, notifsData, hwData, pkgsData] = await Promise.all([
        get("/api/sessions/me") as Promise<{ sessions: Array<{ id: string; scheduledAt: string; tutor: { name: string }; subjectId: string; status: string }> }>,
        get("/api/notifications/me") as Promise<{ notifications: Array<{ id: string; title: string; body: string; isRead: boolean }> }>,
        get("/api/homework/me") as Promise<{ homework: Array<{ id: string; title: string; dueDate: string; status: string }> }>,
        get("/api/packages/me") as Promise<{ enrolments: Array<{ id: string; lessonsTotal: number; lessonsUsed: number; status: string }> }>,
      ]);

      const statsEl = document.getElementById("dash-stats") as HTMLElement;
      const upcoming = sessionsData.sessions.filter(s => s.status === "scheduled").length;
      const activePackages = pkgsData.enrolments.filter(p => p.status === "active").length;
      const unreadNotifs = notifsData.notifications.filter(n => !n.isRead).length;
      statsEl.innerHTML = `
        <div class="dash-stat-card"><span class="dash-stat-num">${upcoming}</span><span class="dash-stat-label">${isAr ? "جلسات قادمة" : "Upcoming sessions"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">${activePackages}</span><span class="dash-stat-label">${isAr ? "باقات نشطة" : "Active packages"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">${unreadNotifs}</span><span class="dash-stat-label">${isAr ? "إشعارات جديدة" : "New notifications"}</span></div>
      `;

      const upcomingEl = document.getElementById("dash-upcoming-sessions") as HTMLElement;
      const upcomingSessions = sessionsData.sessions.filter(s => s.status === "scheduled").slice(0, 3);
      upcomingEl.innerHTML = upcomingSessions.length > 0
        ? upcomingSessions.map(s => `
            <div class="dash-quick-item">
              <span class="dash-quick-item-title">${subjectLabel(s.subjectId, lang)}</span>
              <span class="dash-quick-item-sub">${esc(s.tutor.name)} · ${formatDate(s.scheduledAt, lang)}</span>
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد جلسات قادمة" : "No upcoming sessions"}</p>`;

      const hwEl = document.getElementById("dash-pending-homework") as HTMLElement;
      const pendingHw = hwData.homework.filter(h => h.status === "assigned").slice(0, 3);
      hwEl.innerHTML = pendingHw.length > 0
        ? pendingHw.map(h => `<div class="dash-quick-item"><span>${esc(h.title)}</span><span class="dash-quick-item-sub">${formatDate(h.dueDate, lang)}</span></div>`).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد واجبات معلقة" : "No pending homework"}</p>`;

      const notifEl = document.getElementById("dash-notifications") as HTMLElement;
      notifEl.innerHTML = notifsData.notifications.slice(0, 5).map(n => `
        <div class="dash-quick-item${n.isRead ? "" : " dash-quick-item--unread"}">
          <span>${esc(n.title)}</span>
          <span class="dash-quick-item-sub">${esc(n.body)}</span>
        </div>
      `).join("");
    } catch { /* non-fatal */ }
  };

  type StudentSession = {
    id: string; scheduledAt: string; tutor: { id: string; name: string }; subjectId: string;
    status: string; durationMinutes: number; videoCallUrl: string | null; hasReview?: boolean; isTrial?: boolean;
  };

  const openReviewModal = (session: StudentSession, onDone: () => void) => {
    const starRow = [1, 2, 3, 4, 5].map(n =>
      `<button type="button" class="review-star" data-val="${n}" aria-label="${n}">${iconSvg("star", "review-star-icon")}</button>`
    ).join("");
    let rating = 5;
    const { root } = openModal({
      title: isAr ? "قيّم جلستك" : "Rate your lesson",
      confirmLabel: isAr ? "إرسال التقييم" : "Submit review",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      bodyHtml: `
        <p class="review-target">${esc(session.tutor.name)} · ${subjectLabel(session.subjectId, lang)}</p>
        <div class="review-stars" id="review-stars">${starRow}</div>
        <textarea class="form-textarea" id="review-comment" rows="3" maxlength="600" placeholder="${isAr ? "شارك تجربتك (اختياري)" : "Share your experience (optional)"}"></textarea>
        <label class="checkbox-label"><input type="checkbox" id="review-recommend" checked /> ${isAr ? "أنصح بهذا المعلم" : "I would continue with this tutor"}</label>
        <label class="checkbox-label"><input type="checkbox" id="review-anon" /> ${isAr ? "نشر التقييم باسم مجهول" : "Post anonymously"}</label>
      `,
      onConfirm: async () => {
        await post("/api/reviews", {
          sessionId: session.id,
          rating,
          comment: (root.querySelector("#review-comment") as HTMLTextAreaElement).value.trim(),
          wouldContinue: (root.querySelector("#review-recommend") as HTMLInputElement).checked,
          isAnonymous: (root.querySelector("#review-anon") as HTMLInputElement).checked,
        });
        onDone();
        return true;
      }
    });
    const paint = () => root.querySelectorAll<HTMLButtonElement>(".review-star").forEach(s =>
      s.classList.toggle("review-star--on", Number(s.dataset.val) <= rating));
    root.querySelectorAll<HTMLButtonElement>(".review-star").forEach(s =>
      s.addEventListener("click", () => { rating = Number(s.dataset.val); paint(); }));
    paint();
  };

  const loadSessions = async () => {
    try {
      const data = await get("/api/sessions/me") as { sessions: StudentSession[] };
      const listEl = document.getElementById("dash-sessions-list") as HTMLElement;
      let currentFilter = "upcoming";

      const render = () => {
        const filtered = data.sessions.filter(s => {
          if (currentFilter === "upcoming") return s.status === "scheduled";
          if (currentFilter === "completed") return s.status === "completed";
          return s.status === "cancelled";
        });
        listEl.innerHTML = filtered.length > 0
          ? filtered.map(s => {
              const actions: string[] = [];
              if (s.status === "scheduled" && s.videoCallUrl) actions.push(`<a class="btn btn-primary btn-sm" href="${esc(s.videoCallUrl)}" target="_blank" rel="noopener">${isAr ? "انضمام" : "Join"}</a>`);
              if (s.status === "scheduled") actions.push(`<button class="btn btn-outline btn-sm sess-cancel" data-id="${esc(s.id)}">${isAr ? "إلغاء" : "Cancel"}</button>`);
              if (s.status === "completed" && !s.hasReview) actions.push(`<button class="btn btn-outline btn-sm sess-review" data-id="${esc(s.id)}">${isAr ? "أضف تقييماً" : "Leave a review"}</button>`);
              if (s.status === "completed" && s.hasReview) actions.push(`<span class="session-reviewed">${isAr ? "تم التقييم ✓" : "Reviewed ✓"}</span>`);
              return `
              <div class="session-card">
                <div class="session-card-info">
                  <span class="session-subject">${subjectLabel(s.subjectId, lang)}${s.isTrial ? ` · ${isAr ? "تجريبية" : "Trial"}` : ""}</span>
                  <span class="session-tutor">${esc(s.tutor.name)}</span>
                  <span class="session-date">${formatDate(s.scheduledAt, lang)}</span>
                  <span class="session-duration">${s.durationMinutes} ${isAr ? "د" : "min"}</span>
                </div>
                <div class="session-card-side">
                  <span class="session-status session-status--${s.status}">${s.status}</span>
                  ${actions.length ? `<div class="session-actions">${actions.join("")}</div>` : ""}
                </div>
              </div>`;
            }).join("")
          : `<p class="dash-empty">${isAr ? "لا توجد جلسات" : "No sessions"}</p>`;

        listEl.querySelectorAll<HTMLButtonElement>(".sess-cancel").forEach(btn => {
          btn.addEventListener("click", async () => {
            if (!await confirmModal({ title: isAr ? "إلغاء الجلسة" : "Cancel session", message: isAr ? "سيتم إشعار الطرف الآخر بالإلغاء." : "The other party will be notified.", confirmLabel: isAr ? "إلغاء الجلسة" : "Cancel session", danger: true })) return;
            try {
              await post(`/api/sessions/${btn.dataset.id}/cancel`, {});
              const s = data.sessions.find(x => x.id === btn.dataset.id); if (s) s.status = "cancelled";
              render();
            } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
          });
        });
        listEl.querySelectorAll<HTMLButtonElement>(".sess-review").forEach(btn => {
          btn.addEventListener("click", () => {
            const s = data.sessions.find(x => x.id === btn.dataset.id);
            if (s) openReviewModal(s, () => { s.hasReview = true; render(); });
          });
        });
      };

      render();
      document.querySelectorAll<HTMLButtonElement>("#tab-sessions .chip").forEach(btn => {
        btn.addEventListener("click", () => {
          document.querySelectorAll("#tab-sessions .chip").forEach(b => b.classList.remove("chip--active"));
          btn.classList.add("chip--active");
          currentFilter = btn.dataset.filter!;
          render();
        });
      });
    } catch { /* */ }
  };

  const loadProgress = async () => {
    try {
      const data = await get("/api/progress/my") as { progress: Array<{ subjectId: string; fluencyLevel: number; overallNotes: string | null }> };
      const tabsEl = document.getElementById("progress-subject-tabs") as HTMLElement;
      const contentEl = document.getElementById("dash-progress-content") as HTMLElement;

      if (data.progress.length === 0) {
        contentEl.innerHTML = `<p class="dash-empty">${isAr ? "لا يوجد تقدم مسجّل بعد" : "No progress recorded yet"}</p>`;
        return;
      }

      tabsEl.innerHTML = data.progress.map((p, i) => `
        <button class="chip${i === 0 ? " chip--active" : ""}" data-idx="${i}">${subjectLabel(p.subjectId, lang)}</button>
      `).join("");

      const showProgress = (p: typeof data.progress[0]) => {
        contentEl.innerHTML = `
          <div class="progress-item">
            <h3>${subjectLabel(p.subjectId, lang)}</h3>
            <div class="progress-bar-wrap">
              <div class="progress-bar-fill" style="width:${Math.min(100, p.fluencyLevel)}%"></div>
            </div>
            <p>${isAr ? "مستوى الطلاقة:" : "Fluency level:"} ${p.fluencyLevel}/100</p>
            ${p.overallNotes ? `<p class="progress-notes">${esc(p.overallNotes)}</p>` : ""}
          </div>
        `;
      };

      showProgress(data.progress[0]);
      tabsEl.querySelectorAll<HTMLButtonElement>(".chip").forEach(btn => {
        btn.addEventListener("click", () => {
          tabsEl.querySelectorAll(".chip").forEach(b => b.classList.remove("chip--active"));
          btn.classList.add("chip--active");
          showProgress(data.progress[Number(btn.dataset.idx)]);
        });
      });
    } catch { /* */ }
  };

  const loadHomework = async () => {
    try {
      const data = await get("/api/homework/me") as { homework: Array<{ id: string; title: string; description: string; dueDate: string; status: string; tutorFeedback: string | null }> };
      const listEl = document.getElementById("dash-homework-list") as HTMLElement;
      let currentFilter = "assigned";

      const render = () => {
        const filtered = data.homework.filter(h => h.status === currentFilter);
        listEl.innerHTML = filtered.length > 0
          ? filtered.map(h => `
              <div class="homework-card">
                <h4>${esc(h.title)}</h4>
                <p>${esc(h.description)}</p>
                <span class="homework-due">${isAr ? "التسليم:" : "Due:"} ${formatDate(h.dueDate, lang)}</span>
                ${h.tutorFeedback ? `<div class="homework-feedback"><strong>${isAr ? "تقييم المعلم:" : "Tutor feedback:"}</strong> ${esc(h.tutorFeedback)}</div>` : ""}
                ${currentFilter === "assigned" ? `<button class="btn btn-outline btn-sm hw-submit-btn" data-id="${esc(h.id)}">${isAr ? "تسليم" : "Submit"}</button>` : ""}
              </div>
            `).join("")
          : `<p class="dash-empty">${isAr ? "لا توجد واجبات" : "No homework"}</p>`;

        listEl.querySelectorAll<HTMLButtonElement>(".hw-submit-btn").forEach(btn => {
          btn.addEventListener("click", async () => {
            const text = await promptModal({ title: isAr ? "تسليم الواجب" : "Submit homework", label: isAr ? "إجابتك" : "Your answer", multiline: true, confirmLabel: isAr ? "تسليم" : "Submit" });
            if (!text) return;
            try {
              await patch(`/api/homework/${btn.dataset.id}/submit`, { submissionText: text });
              location.reload();
            } catch { showSiteError(isAr ? "حدث خطأ" : "Error"); }
          });
        });
      };

      render();
      document.querySelectorAll<HTMLButtonElement>("#tab-homework .chip").forEach(btn => {
        btn.addEventListener("click", () => {
          document.querySelectorAll("#tab-homework .chip").forEach(b => b.classList.remove("chip--active"));
          btn.classList.add("chip--active");
          currentFilter = btn.dataset.filter!;
          render();
        });
      });
    } catch { /* */ }
  };

  const loadCurriculum = async () => {
    try {
      const data = await get("/api/curriculum/my") as {
        assignments: Array<{ id: string; completedAt: string; tutorNote: string | null; item: { title: string; subjectId: string; description: string | null }; tutor: { name: string } }>
      };
      const listEl = document.getElementById("dash-student-curriculum-list") as HTMLElement | null;
      if (!listEl) return;
      listEl.innerHTML = data.assignments.length > 0
        ? data.assignments.map(a => `
            <div class="curriculum-card">
              <div>
                <strong>${esc(a.item.title)}</strong>
                <span>${subjectLabel(a.item.subjectId, lang)} · ${esc(a.tutor.name)} · ${formatDate(a.completedAt, lang)}</span>
                ${a.tutorNote ? `<p>${esc(a.tutorNote)}</p>` : ""}
              </div>
              <span class="badge badge--verified">${isAr ? "مكتمل" : "Completed"}</span>
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا يوجد منهج مكتمل بعد" : "No completed curriculum yet"}</p>`;
    } catch { /* */ }
  };

  const loadMyPackages = async () => {
    const listEl = document.getElementById("dash-packages-list") as HTMLElement | null;
    if (!listEl) return;
    try {
      const data = await get("/api/packages/me") as {
        enrolments: Array<{ id: string; lessonsTotal: number; lessonsUsed: number; status: string; expiresAt: string; packageDef: { name: string; priceUsd: string | number } }>
      };
      listEl.innerHTML = data.enrolments.length > 0
        ? data.enrolments.map(p => `
            <div class="package-card package-card--outline">
              <div>
                <h3>${esc(p.packageDef.name)}</h3>
                <p>${p.lessonsUsed}/${p.lessonsTotal} ${isAr ? "دروس مستخدمة" : "lessons used"} · <span class="badge ${p.status === "active" ? "badge--success" : "badge--neutral"}">${esc(p.status)}</span></p>
                <p class="form-hint">${isAr ? "تنتهي" : "Expires"} ${formatDate(p.expiresAt, lang)}</p>
              </div>
              <strong>$${Number(p.packageDef.priceUsd).toFixed(0)}</strong>
            </div>
          `).join("")
        : `
          <div class="dash-empty-cta">
            <p class="dash-empty">${isAr ? "لا توجد باقات بعد" : "No packages yet"}</p>
            <p class="form-hint">${isAr ? "وفّر بشراء باقة دروس مخفضة من معلمك." : "Save by buying a discounted lesson bundle from your tutor."}</p>
            <a href="/tutors${isAr ? "?lang=ar" : ""}" class="btn btn-outline btn-sm">${isAr ? "تصفح المعلمين" : "Browse tutors"}</a>
          </div>`;
    } catch { /* */ }
  };

  wireMessagesTab(dash);
  initSettingsPanel(lang);
  initLearnPanel(lang);
  Promise.all([loadOverview(), loadSessions(), loadProgress(), loadHomework(), loadCurriculum(), loadMyPackages()]);
};

// ── Tutor dashboard ───────────────────────────────────────────────────────────

const initTutorDashboard = () => {
  const dash = document.getElementById("tutor-dashboard");
  if (!dash) return;

  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";

  initDashTabs("tutor-dashboard");

  const loadOverview = async () => {
    try {
      const [sessionsData, notifsData, profileData] = await Promise.all([
        get("/api/sessions/me") as Promise<{ sessions: Array<{ id: string; scheduledAt: string; student: { name: string }; subjectId: string; status: string }> }>,
        get("/api/notifications/me") as Promise<{ notifications: Array<{ id: string; title: string; body: string; isRead: boolean }> }>,
        get("/api/tutor/profile/me") as Promise<{ profile: { approved: boolean; totalStudents: number; totalSessions: number; averageRating: number | null } }>,
      ]);

      if (!profileData.profile.approved) {
        document.getElementById("tutor-approval-banner")?.classList.remove("hidden");
        document.getElementById("tutor-application-workflow")?.classList.remove("hidden");
        dash.classList.add("tutor-dashboard--pending");
      }

      const statsEl = document.getElementById("dash-stats") as HTMLElement;
      statsEl.innerHTML = `
        <div class="dash-stat-card"><span class="dash-stat-num">${profileData.profile.totalStudents}</span><span class="dash-stat-label">${isAr ? "طلاب" : "Students"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">${profileData.profile.totalSessions}</span><span class="dash-stat-label">${isAr ? "جلسات" : "Sessions"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">${profileData.profile.averageRating?.toFixed(1) ?? "—"}</span><span class="dash-stat-label">${isAr ? "التقييم" : "Rating"}</span></div>
      `;

      const upcomingEl = document.getElementById("dash-upcoming-sessions") as HTMLElement;
      const upcoming = sessionsData.sessions.filter(s => s.status === "scheduled").slice(0, 3);
      upcomingEl.innerHTML = upcoming.length > 0
        ? upcoming.map(s => `
            <div class="dash-quick-item">
              <span>${subjectLabel(s.subjectId, lang)}</span>
              <span class="dash-quick-item-sub">${esc(s.student.name)} · ${formatDate(s.scheduledAt, lang)}</span>
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد جلسات قادمة" : "No upcoming sessions"}</p>`;

      const notifEl = document.getElementById("dash-notifications") as HTMLElement;
      notifEl.innerHTML = notifsData.notifications.slice(0, 5).map(n => `
        <div class="dash-quick-item${n.isRead ? "" : " dash-quick-item--unread"}">
          <span>${esc(n.title)}</span><span class="dash-quick-item-sub">${esc(n.body)}</span>
        </div>
      `).join("");
    } catch { /* */ }
  };

  const populateSubjectSelect = (select: HTMLSelectElement | null) => {
    if (!select) return;
    select.innerHTML = Object.keys(SUBJECTS).map(id => `<option value="${id}">${subjectLabel(id, lang)}</option>`).join("");
  };

  const loadStudents = async () => {
    try {
      const data = await get("/api/tutor/students") as {
        students: Array<{ id: string; name: string; email: string; avatarUrl: string | null; totalSessions: number; completedSessions: number; nextSessionAt: string | null }>
      };
      const listEl = document.getElementById("dash-students-list") as HTMLElement | null;
      const studentOptions = data.students.map(s => `<option value="${esc(s.id)}">${esc(s.name)}</option>`).join("");
      const hwSelect = document.getElementById("hw-student") as HTMLSelectElement | null;
      const curSelect = document.getElementById("cur-student") as HTMLSelectElement | null;
      if (hwSelect) hwSelect.innerHTML = studentOptions;
      if (curSelect) curSelect.innerHTML = studentOptions;
      if (listEl) {
        listEl.innerHTML = data.students.length > 0
          ? data.students.map(s => `
              <div class="student-card">
                <div class="student-card-main">
                  <div class="student-avatar">${s.avatarUrl ? `<img src="${esc(s.avatarUrl)}" alt="" />` : iconSvg("user", "dash-avatar-icon")}</div>
                  <div>
                    <strong>${esc(s.name)}</strong>
                    <span>${s.completedSessions}/${s.totalSessions} ${isAr ? "جلسات مكتملة" : "sessions completed"}</span>
                    <span>${s.nextSessionAt ? `${isAr ? "التالي:" : "Next:"} ${formatDate(s.nextSessionAt, lang)}` : (isAr ? "لا توجد جلسة قادمة" : "No upcoming session")}</span>
                  </div>
                </div>
                <div class="student-card-actions">
                  <button class="btn btn-primary btn-sm student-message" data-id="${esc(s.id)}">${iconSvg("message", "btn-icon")} ${isAr ? "مراسلة" : "Message"}</button>
                  <button class="btn btn-outline btn-sm student-request" data-id="${esc(s.id)}" data-type="higher_level">${isAr ? "طلب مستوى أعلى" : "Request higher"}</button>
                  <button class="btn btn-outline btn-sm student-request" data-id="${esc(s.id)}" data-type="time_change">${isAr ? "طلب تغيير الموعد" : "Request time change"}</button>
                </div>
              </div>
            `).join("")
          : `<p class="dash-empty">${isAr ? "لا يوجد طلاب بعد" : "No approved students yet"}</p>`;
        listEl.querySelectorAll<HTMLButtonElement>(".student-message").forEach(btn => {
          btn.addEventListener("click", () => {
            // Jump to the Messages tab with this student's conversation open.
            (document.querySelector('.dash-tab[data-tab="messages"]') as HTMLButtonElement | null)?.click();
            void mountMessaging({ preselectUserId: btn.dataset.id });
          });
        });
        listEl.querySelectorAll<HTMLButtonElement>(".student-request").forEach(btn => {
          btn.addEventListener("click", async () => {
            const message = await promptModal({ title: isAr ? "إرسال طلب" : "Send request", label: isAr ? "ملاحظة" : "Note", optional: true, confirmLabel: isAr ? "إرسال" : "Send" });
            if (message === null) return;
            try {
              await post(`/api/tutor/students/${btn.dataset.id}/requests`, { type: btn.dataset.type, message });
              btn.textContent = isAr ? "تم الإرسال" : "Requested";
              btn.disabled = true;
            } catch (err) { showSiteError(err instanceof Error ? err.message : "Error"); }
          });
        });
      }
    } catch { /* */ }
  };

  const loadProfile = async () => {
    try {
      const data = await get("/api/tutor/profile/me") as {
        profile: {
          displayName: string; shortBio: string; aboutFull: string; country: string;
          introVideoUrl: string | null; isHafiz: boolean; memorizedJuz: number;
          hasTachingSanad: boolean; yearsExperience: number; hourlyRateUsd: number;
          subjectsTaught: string; cvDocumentUrl?: string | null; sampleSyllabusUrl?: string | null;
          ijazahs?: Array<{ id: string; riwayah: string; grantingSheikhName: string }>;
        };
        ijazahs?: Array<{ id: string; riwayah: string; grantingSheikhName: string }>;
      };

      const p = data.profile;
      (document.getElementById("prof-display-name") as HTMLInputElement).value = p.displayName;
      (document.getElementById("prof-short-bio") as HTMLInputElement).value = p.shortBio;
      (document.getElementById("prof-about-full") as HTMLTextAreaElement).value = p.aboutFull;
      (document.getElementById("prof-country") as HTMLInputElement).value = p.country;
      (document.getElementById("prof-intro-video") as HTMLInputElement).value = p.introVideoUrl ?? "";
      (document.getElementById("prof-is-hafiz") as HTMLInputElement).checked = p.isHafiz;
      (document.getElementById("prof-juz") as HTMLInputElement).value = String(p.memorizedJuz);
      (document.getElementById("prof-has-sanad") as HTMLInputElement).checked = p.hasTachingSanad;
      (document.getElementById("prof-years-exp") as HTMLInputElement).value = String(p.yearsExperience);
      (document.getElementById("prof-rate") as HTMLInputElement).value = String(p.hourlyRateUsd);

      const subjects = parseMaybeJson<Array<{ subjectId: string; levelsTaught: string[] }>>(p.subjectsTaught, []);
      const appSubjectsEditor = document.getElementById("app-subjects-editor") as HTMLElement | null;
      if (appSubjectsEditor) {
        appSubjectsEditor.innerHTML = Object.keys(SUBJECTS).map(sid => `
          <label class="checkbox-label">
            <input type="checkbox" class="app-subject-check" data-subject="${sid}" ${subjects.some(s => s.subjectId === sid) ? "checked" : ""} />
            ${subjectLabel(sid, lang)}
          </label>
        `).join("");
        (document.getElementById("app-display-name") as HTMLInputElement | null)?.setAttribute("value", p.displayName);
        (document.getElementById("app-short-bio") as HTMLInputElement | null)?.setAttribute("value", p.shortBio);
        const appAbout = document.getElementById("app-about-full") as HTMLTextAreaElement | null;
        if (appAbout) appAbout.value = p.aboutFull;
        const appCountry = document.getElementById("app-country") as HTMLSelectElement | null;
        if (appCountry) appCountry.value = p.country;
        (document.getElementById("app-years-exp") as HTMLInputElement | null)?.setAttribute("value", String(p.yearsExperience));
        (document.getElementById("app-cv-url") as HTMLInputElement | null)?.setAttribute("value", p.cvDocumentUrl ?? "");
        (document.getElementById("app-syllabus-url") as HTMLInputElement | null)?.setAttribute("value", p.sampleSyllabusUrl ?? "");
      }
      const subjectsEditor = document.getElementById("prof-subjects-editor") as HTMLElement;
      subjectsEditor.innerHTML = Object.keys(SUBJECTS).map(sid => {
        const hasSubject = subjects.some(s => s.subjectId === sid);
        return `
          <label class="checkbox-label">
            <input type="checkbox" class="prof-subject-check" data-subject="${sid}" ${hasSubject ? "checked" : ""} />
            ${subjectLabel(sid, lang)}
          </label>
        `;
      }).join("");

      const ijazahsList = document.getElementById("prof-ijazahs-list") as HTMLElement;
      const ijazahs = data.ijazahs ?? p.ijazahs ?? [];
      if (ijazahs.length > 0) {
        ijazahsList.innerHTML = ijazahs.map(ij => `
          <div class="ijazah-editor-item">
            <span>${esc(ij.riwayah)} — ${esc(ij.grantingSheikhName)}</span>
          </div>
        `).join("");
      }
    } catch { /* */ }
  };

  document.getElementById("prof-save")?.addEventListener("click", async () => {
    const msgEl = document.getElementById("prof-message") as HTMLElement;
    try {
      const subjects: Array<{ subjectId: string; levelsTaught: string[] }> = [];
      document.querySelectorAll<HTMLInputElement>(".prof-subject-check:checked").forEach(cb => {
        subjects.push({ subjectId: cb.dataset.subject!, levelsTaught: [] });
      });
      await put("/api/tutor/profile", {
        displayName: (document.getElementById("prof-display-name") as HTMLInputElement).value.trim(),
        shortBio: (document.getElementById("prof-short-bio") as HTMLInputElement).value.trim(),
        aboutFull: (document.getElementById("prof-about-full") as HTMLTextAreaElement).value.trim(),
        country: (document.getElementById("prof-country") as HTMLInputElement).value.trim(),
        introVideoUrl: (document.getElementById("prof-intro-video") as HTMLInputElement).value.trim() || null,
        isHafiz: (document.getElementById("prof-is-hafiz") as HTMLInputElement).checked,
        memorizedJuz: Number((document.getElementById("prof-juz") as HTMLInputElement).value),
        hasTachingSanad: (document.getElementById("prof-has-sanad") as HTMLInputElement).checked,
        yearsExperience: Number((document.getElementById("prof-years-exp") as HTMLInputElement).value),
        hourlyRateUsd: Number((document.getElementById("prof-rate") as HTMLInputElement).value),
        subjectsTaught: subjects,
      });
      msgEl.textContent = isAr ? "تم الحفظ ✓" : "Saved ✓";
      msgEl.classList.remove("hidden");
      setTimeout(() => msgEl.classList.add("hidden"), 3000);
    } catch (err) {
      msgEl.textContent = err instanceof Error ? err.message : (isAr ? "حدث خطأ" : "Error");
      msgEl.classList.remove("hidden");
    }
  });

  document.getElementById("prof-apply-btn")?.addEventListener("click", async () => {
    try {
      await post("/api/tutor/apply", {});
      showSiteError(isAr ? "تم تقديم الطلب. سيتم مراجعته قريباً." : "Application submitted. It will be reviewed shortly.");
    } catch (err) {
      showSiteError(err instanceof Error ? err.message : (isAr ? "حدث خطأ" : "Error"));
    }
  });

  const getAppFormValues = () => {
    const subjects: Array<{ subjectId: string; levelsTaught: string[] }> = [];
    document.querySelectorAll<HTMLInputElement>(".app-subject-check:checked").forEach(cb => {
      subjects.push({ subjectId: cb.dataset.subject!, levelsTaught: [] });
    });
    return {
      displayName: (document.getElementById("app-display-name") as HTMLInputElement).value.trim(),
      shortBio: (document.getElementById("app-short-bio") as HTMLInputElement).value.trim(),
      aboutFull: (document.getElementById("app-about-full") as HTMLTextAreaElement).value.trim(),
      country: (document.getElementById("app-country") as HTMLSelectElement).value.trim(),
      yearsExperience: Number((document.getElementById("app-years-exp") as HTMLInputElement).value),
      cvDocumentUrl: (document.getElementById("app-cv-url") as HTMLInputElement).value.trim() || null,
      sampleSyllabusUrl: (document.getElementById("app-syllabus-url") as HTMLInputElement).value.trim() || null,
      hourlyRateUsd: 0,
      subjectsTaught: subjects,
    };
  };

  document.getElementById("app-preview-btn")?.addEventListener("click", () => {
    const vals = getAppFormValues();
    if (!vals.displayName || !vals.shortBio || !vals.country) {
      showSiteError(isAr ? "يرجى ملء الاسم والنبذة والدولة على الأقل." : "Please fill in display name, short bio, and country at minimum.");
      return;
    }
    const previewContent = document.getElementById("app-preview-content") as HTMLElement;
    const subjectNames = vals.subjectsTaught.map(s => subjectLabel(s.subjectId, lang)).join(", ") || (isAr ? "غير محدد" : "None selected");
    previewContent.innerHTML = `
      <div class="preview-row"><label>${isAr ? "الاسم المعروض:" : "Display name:"}</label><span>${esc(vals.displayName)}</span></div>
      <div class="preview-row"><label>${isAr ? "النبذة القصيرة:" : "Short bio:"}</label><span>${esc(vals.shortBio)}</span></div>
      <div class="preview-row"><label>${isAr ? "خلفية التدريس:" : "Teaching background:"}</label><span>${esc(vals.aboutFull || "—")}</span></div>
      <div class="preview-row"><label>${isAr ? "الدولة:" : "Country:"}</label><span>${esc(vals.country)}</span></div>
      <div class="preview-row"><label>${isAr ? "سنوات الخبرة:" : "Years of experience:"}</label><span>${vals.yearsExperience}</span></div>
      <div class="preview-row"><label>${isAr ? "المواد:" : "Subjects:"}</label><span>${esc(subjectNames)}</span></div>
      ${vals.cvDocumentUrl ? `<div class="preview-row"><label>${isAr ? "رابط السيرة الذاتية:" : "CV link:"}</label><a href="${esc(vals.cvDocumentUrl)}" target="_blank" rel="noopener">${isAr ? "عرض" : "View"}</a></div>` : ""}
      ${vals.sampleSyllabusUrl ? `<div class="preview-row"><label>${isAr ? "رابط المنهج:" : "Sample syllabus:"}</label><a href="${esc(vals.sampleSyllabusUrl)}" target="_blank" rel="noopener">${isAr ? "عرض" : "View"}</a></div>` : ""}
    `;
    document.getElementById("tutor-application-workflow")?.classList.add("hidden");
    document.getElementById("app-preview-panel")?.classList.remove("hidden");
  });

  document.getElementById("app-preview-back")?.addEventListener("click", () => {
    document.getElementById("app-preview-panel")?.classList.add("hidden");
    document.getElementById("tutor-application-workflow")?.classList.remove("hidden");
  });

  document.getElementById("app-submit-approval")?.addEventListener("click", async () => {
    const submitBtn = document.getElementById("app-submit-approval") as HTMLButtonElement;
    submitBtn.disabled = true;
    submitBtn.textContent = isAr ? "جاري الإرسال..." : "Submitting…";
    try {
      const vals = getAppFormValues();
      await put("/api/tutor/profile", vals);
      await post("/api/tutor/apply", {});
      document.getElementById("app-preview-panel")?.classList.add("hidden");
      document.getElementById("app-success-panel")?.classList.remove("hidden");
      document.getElementById("tutor-approval-banner")?.classList.remove("hidden");
    } catch (err) {
      submitBtn.disabled = false;
      submitBtn.textContent = isAr ? "إرسال الطلب" : "Submit application";
      showSiteError(err instanceof Error ? err.message : "Error");
    }
  });

  const loadTutorHomework = async () => {
    try {
      const data = await get("/api/homework/me") as { homework: Array<{ id: string; title: string; description: string; dueDate: string; status: string; submissionText: string | null; tutorFeedback: string | null }> };
      const listEl = document.getElementById("dash-homework-list") as HTMLElement | null;
      if (!listEl) return;
      listEl.innerHTML = data.homework.length > 0
        ? data.homework.map(h => `
            <div class="homework-card">
              <h4>${esc(h.title)}</h4>
              <p>${esc(h.description)}</p>
              <span class="homework-due">${isAr ? "التسليم:" : "Due:"} ${formatDate(h.dueDate, lang)} · ${esc(h.status)}</span>
              ${h.submissionText ? `<div class="homework-submission"><strong>${isAr ? "حل الطالب:" : "Student submission:"}</strong> ${esc(h.submissionText)}</div>` : ""}
              ${h.status === "submitted" ? `<button class="btn btn-outline btn-sm hw-review-btn" data-id="${esc(h.id)}">${isAr ? "مراجعة الحل" : "Review solution"}</button>` : ""}
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد واجبات بعد" : "No homework yet"}</p>`;
      listEl.querySelectorAll<HTMLButtonElement>(".hw-review-btn").forEach(btn => {
        btn.addEventListener("click", async () => {
          const tutorFeedback = await promptModal({ title: isAr ? "مراجعة الحل" : "Review solution", label: isAr ? "تعليقك على الحل" : "Feedback for the student", multiline: true, confirmLabel: isAr ? "إرسال المراجعة" : "Send review" });
          if (!tutorFeedback) return;
          try {
            await patch(`/api/homework/${btn.dataset.id}/review`, { tutorFeedback });
            loadTutorHomework();
          } catch (err) { showSiteError(err instanceof Error ? err.message : "Error"); }
        });
      });
    } catch { /* */ }
  };

  const initHomeworkCreation = () => {
    const modal = document.getElementById("hw-modal") as HTMLElement | null;
    document.getElementById("hw-new-btn")?.addEventListener("click", () => modal?.classList.remove("hidden"));
    document.getElementById("hw-cancel")?.addEventListener("click", () => modal?.classList.add("hidden"));
    document.getElementById("hw-submit")?.addEventListener("click", async () => {
      try {
        await post("/api/homework", {
          studentId: (document.getElementById("hw-student") as HTMLSelectElement).value,
          title: (document.getElementById("hw-title") as HTMLInputElement).value.trim(),
          description: (document.getElementById("hw-desc") as HTMLTextAreaElement).value.trim(),
          dueDate: (document.getElementById("hw-due") as HTMLInputElement).value
        });
        modal?.classList.add("hidden");
        loadTutorHomework();
      } catch (err) { showSiteError(err instanceof Error ? err.message : "Error"); }
    });
  };

  const loadPackages = async () => {
    try {
      const data = await get("/api/packages/me") as { enrolments: Array<{ id: string; lessonsTotal: number; lessonsUsed: number; status: string; packageDef: { name: string; priceUsd: string | number; validityWeeks: number } }> };
      const listEl = document.getElementById("dash-packages-list") as HTMLElement | null;
      if (!listEl) return;
      listEl.innerHTML = data.enrolments.length > 0
        ? data.enrolments.map(p => `
            <div class="package-card package-card--outline">
              <div>
                <h3>${esc(p.packageDef.name)}</h3>
                <p>${p.lessonsUsed}/${p.lessonsTotal} ${isAr ? "دروس مستخدمة" : "lessons used"} · ${esc(p.status)}</p>
              </div>
              <strong>$${Number(p.packageDef.priceUsd).toFixed(0)}</strong>
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد باقات مباعة بعد" : "No package enrolments yet"}</p>`;
    } catch { /* */ }
  };

  type PackageDef = {
    id: string; name: string; lessonCount: number; discountPct: number;
    validityWeeks: number; priceUsd: number; isActive: boolean;
    enrolmentCount: number; activeEnrolments: number;
  };

  const loadPackageDefs = async () => {
    const listEl = document.getElementById("pkg-defs-list") as HTMLElement | null;
    if (!listEl) return;
    try {
      const data = await get("/api/tutor/packages") as { packages: PackageDef[] };
      listEl.innerHTML = data.packages.length ? data.packages.map(p => `
        <div class="pkg-def-card${p.isActive ? "" : " pkg-def-card--inactive"}">
          <div class="pkg-def-head">
            <h3>${esc(p.name)}</h3>
            <span class="badge ${p.isActive ? "badge--success" : "badge--neutral"}">${p.isActive ? (isAr ? "معروضة" : "Active") : (isAr ? "موقوفة" : "Inactive")}</span>
          </div>
          <div class="pkg-def-price">
            <strong>$${p.priceUsd.toFixed(0)}</strong>
            <span>${isAr ? `≈ $${(p.priceUsd / Math.max(1, p.lessonCount)).toFixed(2)} للدرس` : `≈ $${(p.priceUsd / Math.max(1, p.lessonCount)).toFixed(2)} / lesson`}</span>
          </div>
          <ul class="pkg-def-meta">
            <li>${p.lessonCount} ${isAr ? "درس" : "lessons"}</li>
            <li>${p.discountPct}% ${isAr ? "خصم" : "discount"}</li>
            <li>${isAr ? `صالحة ${p.validityWeeks} أسبوع` : `Valid ${p.validityWeeks} wks`}</li>
            <li>${p.enrolmentCount} ${isAr ? "اشتراك" : "sold"}${p.activeEnrolments ? ` (${p.activeEnrolments} ${isAr ? "نشط" : "active"})` : ""}</li>
          </ul>
          <div class="pkg-def-actions">
            <button class="btn btn-outline btn-sm pkg-edit" data-id="${esc(p.id)}">${iconSvg("pencil", "btn-icon")} ${isAr ? "تعديل" : "Edit"}</button>
            <button class="btn btn-ghost btn-sm pkg-toggle" data-id="${esc(p.id)}" data-active="${p.isActive}">${p.isActive ? (isAr ? "إيقاف العرض" : "Deactivate") : (isAr ? "إعادة العرض" : "Activate")}</button>
          </div>
        </div>`).join("")
        : `<p class="dash-empty">${isAr ? "لم تنشئ أي باقات بعد — أنشئ أول باقة ليشتريها طلابك." : "No packages yet — create your first bundle for students to buy."}</p>`;

      listEl.querySelectorAll<HTMLButtonElement>(".pkg-toggle").forEach(btn => btn.addEventListener("click", async () => {
        try {
          await patch(`/api/tutor/packages/${btn.dataset.id}`, { isActive: btn.dataset.active !== "true" });
          loadPackageDefs();
        } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      }));
      listEl.querySelectorAll<HTMLButtonElement>(".pkg-edit").forEach(btn => btn.addEventListener("click", () => {
        const pkg = data.packages.find(x => x.id === btn.dataset.id);
        if (pkg) openPackageModal(pkg);
      }));
    } catch {
      listEl.innerHTML = `<p class="dash-empty">${isAr ? "تعذّر تحميل الباقات" : "Could not load packages"}</p>`;
    }
  };

  const openPackageModal = (existing?: PackageDef) => {
    openModal({
      title: existing ? (isAr ? "تعديل الباقة" : "Edit package") : (isAr ? "باقة جديدة" : "New package"),
      bodyHtml: `
        <div class="form-group">
          <label class="form-label">${isAr ? "اسم الباقة" : "Package name"}</label>
          <input type="text" class="form-input" id="pkg-name" maxlength="60" value="${esc(existing?.name ?? "")}" placeholder="${isAr ? "مثال: باقة التأسيس" : "e.g. Starter bundle"}" />
        </div>
        <div class="form-grid-2">
          <div class="form-group">
            <label class="form-label">${isAr ? "عدد الدروس" : "Number of lessons"}</label>
            <input type="number" class="form-input" id="pkg-lessons" min="1" max="100" value="${existing?.lessonCount ?? 8}" />
          </div>
          <div class="form-group">
            <label class="form-label">${isAr ? "السعر الإجمالي (دولار)" : "Total price (USD)"}</label>
            <input type="number" class="form-input" id="pkg-price" min="1" step="1" value="${existing?.priceUsd ?? 100}" />
          </div>
          <div class="form-group">
            <label class="form-label">${isAr ? "نسبة الخصم %" : "Discount %"}</label>
            <input type="number" class="form-input" id="pkg-discount" min="0" max="50" value="${existing?.discountPct ?? 5}" />
          </div>
          <div class="form-group">
            <label class="form-label">${isAr ? "الصلاحية (أسابيع)" : "Validity (weeks)"}</label>
            <input type="number" class="form-input" id="pkg-validity" min="1" max="52" value="${existing?.validityWeeks ?? 8}" />
          </div>
        </div>
        <p class="form-hint" id="pkg-per-lesson"></p>`,
      confirmLabel: existing ? (isAr ? "حفظ التعديلات" : "Save changes") : (isAr ? "إنشاء الباقة" : "Create package"),
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      onConfirm: async (root) => {
        const name = (root.querySelector("#pkg-name") as HTMLInputElement).value.trim();
        const lessonCount = Number((root.querySelector("#pkg-lessons") as HTMLInputElement).value);
        const priceUsd = Number((root.querySelector("#pkg-price") as HTMLInputElement).value);
        const discountPct = Number((root.querySelector("#pkg-discount") as HTMLInputElement).value);
        const validityWeeks = Number((root.querySelector("#pkg-validity") as HTMLInputElement).value);
        if (name.length < 2) throw new Error(isAr ? "أدخل اسماً للباقة." : "Give the package a name.");
        if (!Number.isFinite(lessonCount) || lessonCount < 1) throw new Error(isAr ? "عدد الدروس غير صالح." : "Lesson count is invalid.");
        if (!Number.isFinite(priceUsd) || priceUsd < 1) throw new Error(isAr ? "السعر غير صالح." : "Price is invalid.");
        const body = { name, lessonCount, priceUsd, discountPct, validityWeeks };
        if (existing) await patch(`/api/tutor/packages/${existing.id}`, body);
        else await post("/api/tutor/packages", body);
        loadPackageDefs();
        return true;
      }
    });
    // Live per-lesson price hint while typing.
    const update = () => {
      const lessons = Number((document.getElementById("pkg-lessons") as HTMLInputElement | null)?.value ?? 0);
      const price = Number((document.getElementById("pkg-price") as HTMLInputElement | null)?.value ?? 0);
      const hint = document.getElementById("pkg-per-lesson");
      if (hint && lessons > 0 && price > 0) {
        hint.textContent = isAr
          ? `≈ ${(price / lessons).toFixed(2)}$ للدرس الواحد`
          : `≈ $${(price / lessons).toFixed(2)} per lesson`;
      }
    };
    document.getElementById("pkg-lessons")?.addEventListener("input", update);
    document.getElementById("pkg-price")?.addEventListener("input", update);
    update();
  };

  document.getElementById("pkg-new-btn")?.addEventListener("click", () => openPackageModal());

  const initCurriculum = () => {
    populateSubjectSelect(document.getElementById("cur-subject") as HTMLSelectElement | null);
    const loadCurriculum = async () => {
      try {
        const data = await get("/api/tutor/curriculum") as { assignments: Array<{ id: string; completedAt: string | null; tutorNote: string | null; student: { name: string }; item: { title: string; subjectId: string } }> };
        const listEl = document.getElementById("dash-curriculum-list") as HTMLElement | null;
        if (!listEl) return;
        listEl.innerHTML = data.assignments.length > 0
          ? data.assignments.map(a => `<div class="curriculum-card"><div><strong>${esc(a.item.title)}</strong><span>${esc(a.student.name)} · ${subjectLabel(a.item.subjectId, lang)}</span>${a.tutorNote ? `<p>${esc(a.tutorNote)}</p>` : ""}</div><span class="badge badge--verified">${isAr ? "ظاهر للطالب" : "Visible to student"}</span></div>`).join("")
          : `<p class="dash-empty">${isAr ? "لا يوجد منهج مدفوع بعد" : "No curriculum pushed yet"}</p>`;
      } catch { /* */ }
    };
    document.getElementById("cur-push")?.addEventListener("click", async () => {
      try {
        await post("/api/tutor/curriculum/push", {
          studentId: (document.getElementById("cur-student") as HTMLSelectElement).value,
          subjectId: (document.getElementById("cur-subject") as HTMLSelectElement).value,
          title: (document.getElementById("cur-title") as HTMLInputElement).value.trim(),
          tutorNote: (document.getElementById("cur-note") as HTMLTextAreaElement).value.trim()
        });
        loadCurriculum();
      } catch (err) { showSiteError(err instanceof Error ? err.message : "Error"); }
    });
    loadCurriculum();
  };

  const openReportModal = (session: { id: string; studentName: string; subjectId: string }, onDone: () => void) => {
    const { root } = openModal({
      title: isAr ? "تقرير الجلسة" : "Session report",
      confirmLabel: isAr ? "حفظ التقرير" : "Save report",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      bodyHtml: `
        <p class="review-target">${esc(session.studentName)} · ${subjectLabel(session.subjectId, lang)}</p>
        <label class="form-label">${isAr ? "المواضيع المغطّاة (مفصولة بفاصلة)" : "Topics covered (comma-separated)"}</label>
        <input type="text" class="form-input" id="rpt-topics" maxlength="400" />
        <label class="form-label">${isAr ? "تقييم جهد الطالب" : "Student effort"}</label>
        <select class="form-select" id="rpt-effort">${[5, 4, 3, 2, 1].map(n => `<option value="${n}">${n} / 5</option>`).join("")}</select>
        <label class="form-label">${isAr ? "الواجب المطلوب" : "Homework assigned"}</label>
        <input type="text" class="form-input" id="rpt-hw" maxlength="400" />
        <label class="form-label">${isAr ? "ملاحظات للطالب" : "Notes for the student"}</label>
        <textarea class="form-textarea" id="rpt-notes" rows="3" maxlength="1200"></textarea>
        <label class="form-label">${isAr ? "الخطوات التالية" : "Next steps"}</label>
        <input type="text" class="form-input" id="rpt-next" maxlength="400" />
      `,
      onConfirm: async () => {
        const topics = (root.querySelector("#rpt-topics") as HTMLInputElement).value;
        await post(`/api/sessions/${session.id}/report`, {
          topicsCovered: topics.split(",").map(x => x.trim()).filter(Boolean),
          studentEffortRating: Number((root.querySelector("#rpt-effort") as HTMLSelectElement).value),
          homeworkAssigned: (root.querySelector("#rpt-hw") as HTMLInputElement).value.trim() || undefined,
          tutorNotes: (root.querySelector("#rpt-notes") as HTMLTextAreaElement).value.trim() || undefined,
          nextSteps: (root.querySelector("#rpt-next") as HTMLInputElement).value.trim() || undefined,
        });
        onDone();
        return true;
      }
    });
  };

  const openLinkModal = (sessionId: string, currentUrl: string, onDone: (url: string | null) => void) => {
    openModal({
      title: isAr ? "رابط اللقاء" : "Meeting link",
      confirmLabel: isAr ? "حفظ" : "Save",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      bodyHtml: `
        <label class="form-label">${isAr ? "رابط الفيديو (Zoom / Google Meet)" : "Video meeting URL (Zoom / Google Meet)"}</label>
        <input type="url" class="form-input" id="sess-link-input" placeholder="https://..." value="${esc(currentUrl)}" />
        <p class="form-hint">${isAr ? "سيراه الطالب كزر «انضمام» قبل الجلسة." : "Students see this as a “Join” button before the lesson."}</p>
      `,
      onConfirm: async (root) => {
        const url = (root.querySelector("#sess-link-input") as HTMLInputElement).value.trim();
        await post(`/api/sessions/${sessionId}/link`, { videoCallUrl: url });
        onDone(url || null);
        return true;
      }
    });
  };

  type TutorSession = {
    id: string; scheduledAt: string; student: { name: string }; subjectId: string;
    status: string; durationMinutes: number; videoCallUrl: string | null;
    meetingProvider?: string | null; recordingUrl?: string | null;
    sessionReport: { submittedAt: string | null } | null;
  };

  const loadTutorSessions = async () => {
    try {
      const data = await get("/api/sessions/me") as { sessions: TutorSession[] };
      const listEl = document.getElementById("dash-sessions-list") as HTMLElement | null;
      if (!listEl) return;
      let filter = "scheduled";

      const render = () => {
        const filtered = data.sessions.filter(s =>
          filter === "scheduled" ? s.status === "scheduled" :
          filter === "completed" ? s.status === "completed" : s.status === "cancelled");
        listEl.innerHTML = filtered.length ? filtered.map(s => {
          const actions: string[] = [];
          if (s.status === "scheduled") {
            if (s.videoCallUrl) actions.push(`<a class="btn btn-primary btn-sm" href="${esc(s.videoCallUrl)}" target="_blank" rel="noopener">${iconSvg("video", "btn-icon")} ${isAr ? "بدء الدرس" : "Start lesson"}</a>`);
            actions.push(`<button class="btn btn-outline btn-sm sess-regen" data-id="${esc(s.id)}">${s.videoCallUrl ? (isAr ? "تجديد الرابط" : "New link") : (isAr ? "إنشاء رابط" : "Generate link")}</button>`);
            actions.push(`<button class="btn btn-ghost btn-sm sess-link" data-id="${esc(s.id)}" data-url="${esc(s.videoCallUrl ?? "")}">${isAr ? "رابط يدوي" : "Manual link"}</button>`);
            actions.push(`<button class="btn btn-primary btn-sm sess-complete" data-id="${esc(s.id)}">${isAr ? "إنهاء الجلسة" : "Mark done"}</button>`);
          }
          if (s.status === "completed") {
            actions.push(`<button class="btn btn-ghost btn-sm sess-recording" data-id="${esc(s.id)}" data-url="${esc(s.recordingUrl ?? "")}">${iconSvg("camera", "btn-icon")} ${s.recordingUrl ? (isAr ? "التسجيل" : "Recording") : (isAr ? "إرفاق تسجيل" : "Attach recording")}</button>`);
          }
          actions.push(`<button class="btn btn-ghost btn-sm sess-qa" data-id="${esc(s.id)}">${iconSvg("clipboard", "btn-icon")} ${isAr ? "ملاحظات الجودة" : "QA feedback"}</button>`);
          if (s.status === "completed" && !s.sessionReport?.submittedAt) actions.push(`<button class="btn btn-outline btn-sm sess-report" data-id="${esc(s.id)}" data-name="${esc(s.student.name)}" data-subject="${esc(s.subjectId)}">${isAr ? "اكتب التقرير" : "Write report"}</button>`);
          const meetingMeta = s.videoCallUrl && s.status === "scheduled"
            ? `<span class="session-meeting-chip">${iconSvg("link", "session-meeting-icon")} ${s.meetingProvider === "zoom" ? "Zoom" : s.meetingProvider === "manual" ? (isAr ? "رابط يدوي" : "Manual link") : (isAr ? "غرفة المنصة" : "Platform room")}</span>`
            : "";
          return `
            <div class="session-card">
              <div class="session-card-info">
                <span class="session-subject">${subjectLabel(s.subjectId, lang)}</span>
                <span class="session-tutor">${esc(s.student.name)}</span>
                <span class="session-date">${formatDate(s.scheduledAt, lang)}</span>
                <span class="session-duration">${s.durationMinutes} ${isAr ? "د" : "min"}</span>
                ${meetingMeta}
              </div>
              <div class="session-card-side">
                <span class="session-status session-status--${s.status}">${s.status}</span>
                ${actions.length ? `<div class="session-actions">${actions.join("")}</div>` : ""}
              </div>
            </div>`;
        }).join("") : `<p class="dash-empty">${isAr ? "لا توجد جلسات" : "No sessions"}</p>`;

        listEl.querySelectorAll<HTMLButtonElement>(".sess-regen").forEach(btn => btn.addEventListener("click", async () => {
          btn.disabled = true;
          try {
            const resp = await post(`/api/sessions/${btn.dataset.id}/meeting/regenerate`, {}) as { session: { videoCallUrl: string | null; meetingProvider?: string | null } };
            const s = data.sessions.find(x => x.id === btn.dataset.id);
            if (s) { s.videoCallUrl = resp.session.videoCallUrl; s.meetingProvider = resp.session.meetingProvider; }
            render();
          } catch (err) { showSiteError(err instanceof Error ? err.message : ""); btn.disabled = false; }
        }));
        listEl.querySelectorAll<HTMLButtonElement>(".sess-recording").forEach(btn => btn.addEventListener("click", () => {
          openRecordingModal(btn.dataset.id!, btn.dataset.url ?? "", lang, (url) => {
            const s = data.sessions.find(x => x.id === btn.dataset.id);
            if (s) s.recordingUrl = url;
            render();
          });
        }));
        listEl.querySelectorAll<HTMLButtonElement>(".sess-qa").forEach(btn => btn.addEventListener("click", () =>
          openQaThreadModal(btn.dataset.id!, { lang, canInternal: false })));

        listEl.querySelectorAll<HTMLButtonElement>(".sess-complete").forEach(btn => btn.addEventListener("click", async () => {
          if (!await confirmModal({ title: isAr ? "إنهاء الجلسة" : "Complete session", message: isAr ? "سيبدأ احتساب أرباح هذا الدرس بعد انتهاء فترة الاعتراض." : "Earnings for this lesson start releasing after the dispute window.", confirmLabel: isAr ? "إنهاء" : "Mark done" })) return;
          try {
            await post(`/api/sessions/${btn.dataset.id}/complete`, {});
            const s = data.sessions.find(x => x.id === btn.dataset.id); if (s) s.status = "completed";
            render(); loadSessionReports(); loadEarnings(); loadOverview();
          } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
        }));
        listEl.querySelectorAll<HTMLButtonElement>(".sess-link").forEach(btn => btn.addEventListener("click", () => {
          openLinkModal(btn.dataset.id!, btn.dataset.url ?? "", (url) => {
            const s = data.sessions.find(x => x.id === btn.dataset.id); if (s) s.videoCallUrl = url; render();
          });
        }));
        listEl.querySelectorAll<HTMLButtonElement>(".sess-report").forEach(btn => btn.addEventListener("click", () =>
          openReportModal({ id: btn.dataset.id!, studentName: btn.dataset.name!, subjectId: btn.dataset.subject! }, () => { loadSessionReports(); render(); })));
      };

      render();
      document.querySelectorAll<HTMLButtonElement>("#tab-sessions .chip").forEach(b => b.addEventListener("click", () => {
        document.querySelectorAll("#tab-sessions .chip").forEach(x => x.classList.remove("chip--active"));
        b.classList.add("chip--active"); filter = b.dataset.filter!; render();
      }));
    } catch { /* */ }
  };

  const loadSessionReports = async () => {
    try {
      const data = await get("/api/sessions/me") as {
        sessions: Array<{
          id: string; scheduledAt: string; student: { name: string; email: string; avatarUrl: string | null };
          subjectId: string; status: string;
          sessionReport: { submittedAt: string | null } | null;
        }>
      };
      const pending = data.sessions.filter(s => s.status === "completed" && !s.sessionReport?.submittedAt);
      const reportsEl = document.getElementById("dash-reports-list") as HTMLElement;
      reportsEl.innerHTML = pending.length > 0
        ? pending.map(s => `
            <div class="report-card">
              <div class="student-card-main">
                <div class="student-avatar">${s.student.avatarUrl ? `<img src="${esc(s.student.avatarUrl)}" alt="" />` : iconSvg("user", "dash-avatar-icon")}</div>
                <div>
                  <strong>${esc(s.student.name)}</strong>
                  <span>${subjectLabel(s.subjectId, lang)} · ${formatDate(s.scheduledAt, lang)}</span>
                  <span>${esc(s.student.email)}</span>
                </div>
              </div>
              <button class="btn btn-outline btn-sm rpt-btn" data-id="${esc(s.id)}" data-name="${esc(s.student.name)}" data-subject="${esc(s.subjectId)}">
                ${isAr ? "اكتب التقرير" : "Write report"}
              </button>
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد تقارير معلقة" : "No pending reports"}</p>`;

      reportsEl.querySelectorAll<HTMLButtonElement>(".rpt-btn").forEach(btn => {
        btn.addEventListener("click", () => openReportModal(
          { id: btn.dataset.id!, studentName: btn.dataset.name!, subjectId: btn.dataset.subject! },
          () => loadSessionReports()
        ));
      });
    } catch { /* */ }
  };

  const loadEarnings = async () => {
    try {
      const data = await get("/api/tutor/earnings") as {
        commissionPct: number;
        payoutAccount?: { onboardingComplete: boolean; payoutsEnabled: boolean } | null;
        summary: { pendingNet: number; availableNet: number; releasedNet: number; lifetimeNet: number; monthNet: number; completedCount: number; monthCount: number };
        lines: Array<{ id: string; studentName: string; subjectId: string; scheduledAt: string; gross: number; net: number; commission: number; status?: string }>;
      };
      const sumEl = document.getElementById("earnings-summary") as HTMLElement | null;
      if (sumEl) sumEl.innerHTML = `
        <div class="dash-stat-card"><span class="dash-stat-num">$${data.summary.pendingNet.toFixed(2)}</span><span class="dash-stat-label">${isAr ? "قيد الانتظار" : "Pending"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">$${data.summary.availableNet.toFixed(2)}</span><span class="dash-stat-label">${isAr ? "جاهز للتحويل" : "Available"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">$${data.summary.releasedNet.toFixed(2)}</span><span class="dash-stat-label">${isAr ? "تم تحويله" : "Released"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">${data.commissionPct}%</span><span class="dash-stat-label">${isAr ? "عمولة المنصة" : "Platform fee"}</span></div>`;
      const listEl = document.getElementById("earnings-list") as HTMLElement | null;
      if (listEl) listEl.innerHTML = data.lines.length ? `
        <div class="earnings-table">
          <div class="earnings-row earnings-row--head">
            <span>${isAr ? "الطالب" : "Student"}</span><span>${isAr ? "التاريخ" : "Date"}</span>
            <span>${isAr ? "الحالة" : "Status"}</span><span>${isAr ? "صافي" : "Net"}</span>
          </div>
          ${data.lines.map(l => `<div class="earnings-row"><span>${esc(l.studentName)}</span><span>${formatDate(l.scheduledAt, lang)}</span><span>${esc(l.status ?? "")}</span><span class="earnings-net">$${l.net.toFixed(2)}</span></div>`).join("")}
        </div>` : `<p class="dash-empty">${isAr ? "لا توجد أرباح بعد. أكمل جلساتك لرؤية أرباحك هنا." : "No earnings yet. Complete sessions to see your payouts here."}</p>`;
    } catch { /* */ }
  };
  wireMessagesTab(dash);
  initHomeworkCreation();
  initCurriculum();
  initCurriculumStudio(lang);
  initSettingsPanel(lang);
  initAvailabilityPanel(lang);
  initPayoutMethodsPanel(lang);
  Promise.all([loadOverview(), loadProfile(), loadStudents(), loadTutorSessions(), loadTutorHomework(), loadSessionReports(), loadPackages(), loadPackageDefs(), loadEarnings()]);
};

// ── Admin dashboard ───────────────────────────────────────────────────────────

const initAdminDashboard = () => {
  const dash = document.getElementById("admin-dashboard");
  if (!dash) return;

  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";

  initDashTabs("admin-dashboard");

  const loadApplications = async () => {
    try {
      const data = await get("/api/admin/applications?status=pending") as {
        applications: Array<{
          id: string; submittedAt: string;
          tutorProfile: { displayName: string; shortBio: string; userId: string };
        }>
      };
      const listEl = document.getElementById("admin-applications-list") as HTMLElement;

      const render = (apps: typeof data.applications) => {
        listEl.innerHTML = apps.length > 0
          ? apps.map(app => `
              <div class="application-card">
                <div class="application-info">
                  <strong>${esc(app.tutorProfile.displayName)}</strong>
                  <p>${esc(app.tutorProfile.shortBio)}</p>
                  <span>${formatDate(app.submittedAt, lang)}</span>
                </div>
                <div class="application-actions">
                  <a href="/tutors/${esc(app.tutorProfile.userId)}" target="_blank" class="btn btn-ghost btn-sm">${isAr ? "عرض الملف" : "View profile"}</a>
                  <button class="btn btn-primary btn-sm app-approve" data-id="${esc(app.id)}">${isAr ? "قبول" : "Approve"}</button>
                  <button class="btn btn-outline btn-sm app-reject" data-id="${esc(app.id)}">${isAr ? "رفض" : "Reject"}</button>
                </div>
              </div>
            `).join("")
          : `<p class="dash-empty">${isAr ? "لا توجد طلبات" : "No applications"}</p>`;

        listEl.querySelectorAll<HTMLButtonElement>(".app-approve").forEach(btn => {
          btn.addEventListener("click", async () => {
            const note = await promptModal({ title: isAr ? "اعتماد المعلم" : "Approve tutor", label: isAr ? "ملاحظة" : "Note", optional: true, confirmLabel: isAr ? "اعتماد" : "Approve" });
            if (note === null) return;
            try {
              await post(`/api/admin/applications/${btn.dataset.id}/review`, { status: "approved", reviewNote: note });
              loadApplications();
            } catch (err) { showSiteError(err instanceof Error ? err.message : "Error"); }
          });
        });

        listEl.querySelectorAll<HTMLButtonElement>(".app-reject").forEach(btn => {
          btn.addEventListener("click", async () => {
            const note = await promptModal({ title: isAr ? "رفض الطلب" : "Reject application", label: isAr ? "سبب الرفض" : "Rejection reason", multiline: true, confirmLabel: isAr ? "رفض" : "Reject" });
            if (!note) return;
            try {
              await post(`/api/admin/applications/${btn.dataset.id}/review`, { status: "rejected", reviewNote: note });
              loadApplications();
            } catch (err) { showSiteError(err instanceof Error ? err.message : "Error"); }
          });
        });
      };

      render(data.applications);

      document.querySelectorAll<HTMLButtonElement>("#tab-applications .chip").forEach(btn => {
        btn.addEventListener("click", async () => {
          document.querySelectorAll("#tab-applications .chip").forEach(b => b.classList.remove("chip--active"));
          btn.classList.add("chip--active");
          try {
            const d = await get(`/api/admin/applications?status=${btn.dataset.filter}`) as { applications: typeof data.applications };
            render(d.applications);
          } catch { /* */ }
        });
      });
    } catch { /* */ }
  };

  const loadDisputes = async () => {
    try {
      const data = await get("/api/admin/disputes?status=open") as {
        disputes: Array<{
          id: string; reason: string; description: string | null; status: string; createdAt: string;
          session: { tutor: { name: string }; student: { name: string } };
        }>
      };
      const listEl = document.getElementById("admin-disputes-list") as HTMLElement;

      const render = (disputes: typeof data.disputes) => {
        listEl.innerHTML = disputes.length > 0
          ? disputes.map(d => `
              <div class="dispute-card">
                <div class="dispute-info">
                  <strong>${esc(d.reason)}</strong>
                  <p>${esc(d.description ?? "")}</p>
                  <span>${esc(d.session.tutor.name)} ↔ ${esc(d.session.student.name)}</span>
                  <span>${formatDate(d.createdAt, lang)}</span>
                </div>
                <div class="dispute-actions">
                  <button class="btn btn-primary btn-sm disp-resolve" data-id="${esc(d.id)}">${isAr ? "حسم" : "Resolve"}</button>
                </div>
              </div>
            `).join("")
          : `<p class="dash-empty">${isAr ? "لا توجد نزاعات" : "No disputes"}</p>`;

        listEl.querySelectorAll<HTMLButtonElement>(".disp-resolve").forEach(btn => {
          btn.addEventListener("click", async () => {
            openModal({
              title: isAr ? "حسم النزاع" : "Resolve dispute",
              bodyHtml: `
                <div class="form-group">
                  <label class="form-label">${isAr ? "القرار" : "Resolution"}</label>
                  <select class="form-select" id="disp-status">
                    <option value="resolved_student">${isAr ? "لصالح الطالب" : "In favour of the student"}</option>
                    <option value="resolved_tutor">${isAr ? "لصالح المعلم" : "In favour of the tutor"}</option>
                    <option value="resolved_no_action">${isAr ? "بدون إجراء" : "No action needed"}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">${isAr ? "ملاحظة" : "Note"}</label>
                  <textarea class="form-textarea" id="disp-note" rows="3"></textarea>
                </div>`,
              confirmLabel: isAr ? "حسم" : "Resolve",
              cancelLabel: isAr ? "إلغاء" : "Cancel",
              onConfirm: async (root) => {
                await post(`/api/admin/disputes/${btn.dataset.id}/resolve`, {
                  status: (root.querySelector("#disp-status") as HTMLSelectElement).value,
                  resolutionNote: (root.querySelector("#disp-note") as HTMLTextAreaElement).value.trim()
                });
                loadDisputes();
                return true;
              }
            });
          });
        });
      };

      render(data.disputes);
    } catch { /* */ }
  };

  const loadStats = async () => {
    try {
      const data = await get("/api/admin/stats") as { users: number; tutors: number; students: number; sessions: number };
      const statsEl = document.getElementById("dash-stats") as HTMLElement;
      statsEl.innerHTML = `
        <div class="dash-stat-card"><span class="dash-stat-num">${data.users}</span><span class="dash-stat-label">${isAr ? "مستخدمون" : "Users"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">${data.tutors}</span><span class="dash-stat-label">${isAr ? "معلمون" : "Tutors"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">${data.students}</span><span class="dash-stat-label">${isAr ? "طلاب" : "Students"}</span></div>
        <div class="dash-stat-card"><span class="dash-stat-num">${data.sessions}</span><span class="dash-stat-label">${isAr ? "جلسات" : "Sessions"}</span></div>
      `;
    } catch { /* */ }
  };

  const loadOverviewQuickLists = async () => {
    try {
      const [appsData, disputesData] = await Promise.all([
        get("/api/admin/applications?status=pending") as Promise<{ applications: Array<{ id: string; submittedAt: string; tutorProfile: { displayName: string; userId: string } }> }>,
        get("/api/admin/disputes?status=open") as Promise<{ disputes: Array<{ id: string; reason: string; session: { tutor: { name: string }; student: { name: string } } }> }>
      ]);

      const pendingEl = document.getElementById("admin-pending-apps") as HTMLElement;
      pendingEl.innerHTML = appsData.applications.length > 0
        ? appsData.applications.slice(0, 5).map(a => `
            <div class="dash-quick-item">
              <span class="dash-quick-item-title">${esc(a.tutorProfile.displayName)}</span>
              <span class="dash-quick-item-sub">${formatDate(a.submittedAt, lang)}</span>
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد طلبات" : "No pending applications"}</p>`;

      const disputesEl = document.getElementById("admin-open-disputes") as HTMLElement;
      disputesEl.innerHTML = disputesData.disputes.length > 0
        ? disputesData.disputes.slice(0, 5).map(d => `
            <div class="dash-quick-item">
              <span class="dash-quick-item-title">${esc(d.reason)}</span>
              <span class="dash-quick-item-sub">${esc(d.session.tutor.name)} ↔ ${esc(d.session.student.name)}</span>
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد نزاعات" : "No open disputes"}</p>`;
    } catch { /* */ }
  };

  const loadUsers = async (q = "") => {
    const listEl = document.getElementById("admin-users-list") as HTMLElement;
    try {
      const data = await get(`/api/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`) as { users: Array<{ id: string; name: string; email: string; role: string }> };
      listEl.innerHTML = data.users.length > 0
        ? data.users.map(u => `
            <div class="user-card">
              <div class="user-info">
                <strong>${esc(u.name)}</strong>
                <span class="user-email">${esc(u.email)}</span>
              </div>
              <div class="user-card-actions">
                <span class="badge">${esc(u.role)}</span>
                <select class="form-select form-select--sm user-role-select" data-user-id="${esc(u.id)}" data-current="${esc(u.role)}">
                  <option value="student" ${u.role === "student" ? "selected" : ""}>student</option>
                  <option value="tutor" ${u.role === "tutor" ? "selected" : ""}>tutor</option>
                  <option value="moderator" ${u.role === "moderator" ? "selected" : ""}>moderator</option>
                  <option value="admin" ${u.role === "admin" ? "selected" : ""}>admin</option>
                </select>
                <button class="btn btn-outline btn-sm user-detail-btn" data-user-id="${esc(u.id)}">${iconSvg("eye", "btn-icon")} ${isAr ? "التفاصيل" : "Details"}</button>
              </div>
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد نتائج" : "No users found"}</p>`;

      listEl.querySelectorAll<HTMLButtonElement>(".user-detail-btn").forEach(btn =>
        btn.addEventListener("click", () => openUserDetailDrawer(btn.dataset.userId!)));

      listEl.querySelectorAll<HTMLSelectElement>(".user-role-select").forEach(sel => {
        sel.addEventListener("change", async () => {
          const userId = sel.dataset.userId!;
          const role = sel.value;
          if (role === sel.dataset.current) return;
          try {
            await patch(`/api/admin/users/${userId}/role`, { role });
            sel.dataset.current = role;
            const badge = sel.closest(".user-card")?.querySelector(".badge") as HTMLElement;
            if (badge) badge.textContent = role;
          } catch (err) {
            showSiteError(err instanceof Error ? err.message : "Error");
            sel.value = sel.dataset.current!;
          }
        });
      });
    } catch { /* */ }
  };

  const loadFlaggedMessages = async () => {
    const listEl = document.getElementById("admin-flagged-messages") as HTMLElement | null;
    if (!listEl) return;
    try {
      const data = await get("/api/admin/messages/flagged") as {
        messages: Array<{ id: string; conversationId?: string | null; content: string; sentAt: string; from: { name: string; role: string }; to: { name: string; role: string } }>
      };
      listEl.innerHTML = data.messages.length > 0
        ? data.messages.map(m => `
            <div class="flagged-msg-card">
              <div class="flagged-msg-head">
                <span>${esc(m.from.name)} <small>(${esc(m.from.role)})</small> → ${esc(m.to.name)} <small>(${esc(m.to.role)})</small></span>
                <span class="flagged-msg-time">${formatDate(m.sentAt, lang)}</span>
              </div>
              <p class="flagged-msg-body">${esc(m.content)}</p>
              <div class="flagged-msg-actions">
                <button class="btn btn-ghost btn-sm flagged-clear" data-id="${esc(m.id)}">${isAr ? "إزالة العلامة" : "Clear flag"}</button>
                ${m.conversationId ? `<button class="btn btn-outline btn-sm flagged-block" data-id="${esc(m.conversationId)}">${isAr ? "حظر المحادثة" : "Block conversation"}</button>` : ""}
              </div>
            </div>
          `).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد رسائل مُبلَّغ عنها" : "No flagged messages"}</p>`;
      listEl.querySelectorAll<HTMLButtonElement>(".flagged-clear").forEach(btn => {
        btn.addEventListener("click", async () => {
          try { await post(`/api/admin/messages/${btn.dataset.id}/clear`, {}); loadFlaggedMessages(); }
          catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
        });
      });
      listEl.querySelectorAll<HTMLButtonElement>(".flagged-block").forEach(btn => {
        btn.addEventListener("click", async () => {
          if (!await confirmModal({ title: isAr ? "حظر المحادثة" : "Block conversation", message: isAr ? "لن يتمكن الطرفان من تبادل الرسائل بعد الآن." : "The two participants will no longer be able to message each other.", confirmLabel: isAr ? "حظر" : "Block", danger: true })) return;
          try { await post(`/api/admin/messages/conversations/${btn.dataset.id}/block`, {}); loadFlaggedMessages(); }
          catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
        });
      });
    } catch { /* */ }
  };

  // ── User detail drawer (private administrative information) ──
  const openUserDetailDrawer = async (userId: string) => {
    const backdrop = document.createElement("div");
    backdrop.className = "modal-backdrop";
    backdrop.innerHTML = `<div class="modal user-detail-modal" role="dialog" aria-modal="true"><div class="spinner"></div></div>`;
    document.body.appendChild(backdrop);
    const close = () => backdrop.remove();
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    document.addEventListener("keydown", function onKey(e) {
      if (e.key === "Escape") { close(); document.removeEventListener("keydown", onKey); }
    });

    const row = (label: string, value: string | null | undefined) =>
      `<div class="preview-row"><span>${label}</span><strong>${value ? esc(String(value)) : `<em class="detail-empty">${isAr ? "غير مُدخل" : "Not provided"}</em>`}</strong></div>`;

    try {
      const data = await get(`/api/admin/users/${userId}/detail`) as {
        user: { id: string; name: string; email: string; role: string; avatarUrl: string | null; timeZone: string; language: string; createdAt: string };
        privateInfo: Record<string, string | null> | null;
        tutorProfile: { displayName: string; country: string; approved: boolean; yearsExperience: number; hourlyRateUsd: number; totalSessions: number } | null;
        studentProfile: { learnerType: string; onboardingComplete: boolean } | null;
        activity: { sessionCount: number; lastSessionAt: string | null };
        payoutMethods: Array<{ provider: string; isDefault: boolean; payoutsEnabled: boolean; paypalEmail: string | null }>;
        upcomingLeaves: Array<{ startsAt: string; endsAt: string; reason: string | null }>;
      };
      const info = data.privateInfo ?? {};
      const modal = backdrop.querySelector(".user-detail-modal") as HTMLElement;
      modal.innerHTML = `
        <div class="modal-head">
          <h2>${esc(data.user.name)} <span class="badge">${esc(data.user.role)}</span></h2>
          <button class="modal-close" aria-label="${isAr ? "إغلاق" : "Close"}">${iconSvg("x", "btn-icon")}</button>
        </div>
        <div class="user-detail-grid">
          <section>
            <h3>${iconSvg("user", "detail-section-icon")} ${isAr ? "الحساب" : "Account"}</h3>
            ${row(isAr ? "البريد" : "Email", data.user.email)}
            ${row(isAr ? "المنطقة الزمنية" : "Time zone", data.user.timeZone)}
            ${row(isAr ? "اللغة" : "Language", data.user.language)}
            ${row(isAr ? "انضم في" : "Joined", formatDate(data.user.createdAt, lang))}
            ${row(isAr ? "عدد الجلسات" : "Sessions", String(data.activity.sessionCount))}
            ${row(isAr ? "آخر جلسة" : "Last session", data.activity.lastSessionAt ? formatDate(data.activity.lastSessionAt, lang) : null)}
          </section>
          <section>
            <h3>${iconSvg("lock", "detail-section-icon")} ${isAr ? "المعلومات الخاصة" : "Private information"}</h3>
            ${row(isAr ? "تاريخ الميلاد" : "Date of birth", info.dateOfBirth ? String(info.dateOfBirth).slice(0, 10) : null)}
            ${row(isAr ? "الهاتف" : "Phone", info.phone)}
            ${row(isAr ? "هاتف بديل" : "Alt phone", info.altPhone)}
            ${row(isAr ? "العنوان" : "Address", [info.addressLine1, info.addressLine2, info.city, info.stateRegion, info.postalCode, info.country].filter(Boolean).join(", ") || null)}
            ${row(isAr ? "جهة اتصال الطوارئ" : "Emergency contact", [info.emergencyContactName, info.emergencyContactPhone, info.emergencyContactRelation].filter(Boolean).join(" — ") || null)}
            ${row(isAr ? "لغة التواصل" : "Contact language", info.preferredContactLanguage)}
          </section>
          ${data.tutorProfile ? `
          <section>
            <h3>${iconSvg("graduation", "detail-section-icon")} ${isAr ? "ملف المعلم" : "Tutor profile"}</h3>
            ${row(isAr ? "الاسم المعروض" : "Display name", data.tutorProfile.displayName)}
            ${row(isAr ? "الدولة" : "Country", data.tutorProfile.country)}
            ${row(isAr ? "معتمد" : "Approved", data.tutorProfile.approved ? "✓" : "✗")}
            ${row(isAr ? "الخبرة" : "Experience", `${data.tutorProfile.yearsExperience} ${isAr ? "سنة" : "yrs"}`)}
            ${row(isAr ? "السعر بالساعة" : "Hourly rate", `$${data.tutorProfile.hourlyRateUsd}`)}
            ${data.payoutMethods.length ? row(isAr ? "طرق الدفع" : "Payout methods", data.payoutMethods.map(m => `${m.provider}${m.isDefault ? " (default)" : ""}`).join(", ")) : ""}
            ${data.upcomingLeaves.length ? row(isAr ? "إجازات قادمة" : "Upcoming leave", data.upcomingLeaves.map(l => `${String(l.startsAt).slice(0, 10)} → ${String(l.endsAt).slice(0, 10)}`).join("; ")) : ""}
          </section>` : ""}
          <section class="user-detail-notes">
            <h3>${iconSvg("pencil", "detail-section-icon")} ${isAr ? "ملاحظات داخلية (للفريق فقط)" : "Internal notes (staff only)"}</h3>
            <textarea class="form-textarea" id="detail-internal-notes" rows="3">${esc(info.internalNotes ?? "")}</textarea>
            <div class="form-actions">
              <button class="btn btn-primary btn-sm" id="detail-notes-save">${isAr ? "حفظ الملاحظات" : "Save notes"}</button>
              <span class="settings-save-status" id="detail-notes-status"></span>
            </div>
          </section>
        </div>`;
      modal.querySelector(".modal-close")?.addEventListener("click", close);
      modal.querySelector("#detail-notes-save")?.addEventListener("click", async () => {
        const status = modal.querySelector("#detail-notes-status") as HTMLElement;
        try {
          await patch(`/api/admin/users/${userId}/private`, {
            internalNotes: (modal.querySelector("#detail-internal-notes") as HTMLTextAreaElement).value
          });
          status.textContent = isAr ? "تم الحفظ ✓" : "Saved ✓";
          setTimeout(() => { status.textContent = ""; }, 3000);
        } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      });
    } catch (err) {
      close();
      showSiteError(err instanceof Error ? err.message : "");
    }
  };

  // ── Sessions (meetings, recordings, QA) ──
  let adminSessionFilter = "all";
  const loadAdminSessions = async () => {
    const listEl = document.getElementById("admin-sessions-list") as HTMLElement | null;
    if (!listEl) return;
    try {
      const data = await get(`/api/admin/sessions${adminSessionFilter !== "all" ? `?filter=${adminSessionFilter}` : ""}`) as {
        sessions: Array<{
          id: string; scheduledAt: string; durationMinutes: number; subjectId: string; status: string;
          videoCallUrl: string | null; meetingProvider: string | null; recordingUrl: string | null;
          qaCommentCount: number; tutor: { name: string }; student: { name: string };
        }>
      };
      listEl.innerHTML = data.sessions.length ? data.sessions.map(s => `
        <div class="session-card">
          <div class="session-card-info">
            <span class="session-subject">${subjectLabel(s.subjectId, lang)}</span>
            <span class="session-tutor">${esc(s.tutor.name)} ⇄ ${esc(s.student.name)}</span>
            <span class="session-date">${formatDate(s.scheduledAt, lang)} · ${s.durationMinutes} ${isAr ? "د" : "min"}</span>
            ${s.meetingProvider ? `<span class="session-meeting-chip">${iconSvg("link", "session-meeting-icon")} ${esc(s.meetingProvider)}</span>` : ""}
          </div>
          <div class="session-card-side">
            <span class="session-status session-status--${s.status}">${s.status}</span>
            <div class="session-actions">
              ${s.videoCallUrl ? `<a class="btn btn-ghost btn-sm" href="${esc(s.videoCallUrl)}" target="_blank" rel="noopener">${iconSvg("video", "btn-icon")} ${isAr ? "الرابط" : "Link"}</a>` : ""}
              ${s.recordingUrl ? `<a class="btn btn-outline btn-sm" href="${esc(s.recordingUrl)}" target="_blank" rel="noopener">${iconSvg("camera", "btn-icon")} ${isAr ? "التسجيل" : "Recording"}</a>` : ""}
              <button class="btn btn-outline btn-sm adm-sess-qa" data-id="${esc(s.id)}">${iconSvg("clipboard", "btn-icon")} ${isAr ? "الجودة" : "QA"}${s.qaCommentCount ? ` (${s.qaCommentCount})` : ""}</button>
            </div>
          </div>
        </div>`).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد جلسات" : "No sessions"}</p>`;
      listEl.querySelectorAll<HTMLButtonElement>(".adm-sess-qa").forEach(btn =>
        btn.addEventListener("click", () => openQaThreadModal(btn.dataset.id!, { lang, canInternal: true })));
    } catch { /* */ }
  };
  document.querySelectorAll<HTMLButtonElement>("#tab-sessions .chip").forEach(chip => chip.addEventListener("click", () => {
    document.querySelectorAll("#tab-sessions .chip").forEach(c => c.classList.remove("chip--active"));
    chip.classList.add("chip--active");
    adminSessionFilter = chip.dataset.filter!;
    loadAdminSessions();
  }));

  // ── Curriculum review (moderation gate for tutor-authored content) ──
  let adminCurriculaFilter = "pending_review";
  const loadAdminCurricula = async () => {
    const listEl = document.getElementById("admin-curricula-list") as HTMLElement | null;
    if (!listEl) return;
    try {
      const data = await get(`/api/admin/curricula?status=${adminCurriculaFilter}`) as {
        curricula: Array<{ id: string; title: string; subjectId: string; status: string; submittedAt: string | null; reviewNote: string | null; lessonCount: number; questionCount: number; tutor: { name: string; email: string } }>
      };
      listEl.innerHTML = data.curricula.length ? data.curricula.map(c => `
        <div class="studio-item">
          <div class="studio-item-main">
            <div class="studio-item-head">
              <strong>${esc(c.title)}</strong>
              ${curriculumStatusBadge(c.status, isAr)}
            </div>
            <span class="studio-item-meta">${esc(c.tutor.name)} (${esc(c.tutor.email)}) · ${subjectLabel(c.subjectId, lang)} · ${c.lessonCount} ${isAr ? "درس" : "lessons"} · ${c.questionCount} ${isAr ? "سؤال" : "questions"}${c.submittedAt ? ` · ${isAr ? "أُرسل" : "submitted"} ${formatDate(c.submittedAt, lang)}` : ""}</span>
          </div>
          <div class="studio-item-actions">
            <button class="btn btn-primary btn-sm adm-cur-review" data-id="${esc(c.id)}">${iconSvg("eye", "btn-icon")} ${isAr ? "مراجعة" : "Review"}</button>
          </div>
        </div>`).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد مناهج في هذه الحالة" : "No curricula in this state"}</p>`;
      listEl.querySelectorAll<HTMLButtonElement>(".adm-cur-review").forEach(btn =>
        btn.addEventListener("click", () => void openCurriculumReview(btn.dataset.id!)));
    } catch { /* */ }
  };

  const openCurriculumReview = async (id: string) => {
    try {
      const data = await get(`/api/admin/curricula/${id}`) as {
        curriculum: {
          id: string; title: string; description: string | null; subjectId: string; status: string;
          tutor: { name: string; email: string };
          lessons: Array<{ id: string; title: string; content: string; questions: Array<{ prompt: string; type: string; options: string[]; correctIndex: number; explanation: string | null }> }>;
        };
      };
      const c = data.curriculum;
      const backdrop = document.createElement("div");
      backdrop.className = "modal-backdrop";
      backdrop.innerHTML = `
        <div class="modal learn-modal review-modal" role="dialog" aria-modal="true">
          <div class="modal-head">
            <h2>${esc(c.title)} ${curriculumStatusBadge(c.status, isAr)}</h2>
            <button class="modal-close" aria-label="close">${iconSvg("x", "btn-icon")}</button>
          </div>
          <p class="qa-modal-hint">${esc(c.tutor.name)} (${esc(c.tutor.email)}) · ${subjectLabel(c.subjectId, lang)}${c.description ? ` — ${esc(c.description)}` : ""}</p>
          <div class="review-lessons">
            ${c.lessons.map((l, i) => `
              <details class="review-lesson"${i === 0 ? " open" : ""}>
                <summary><span class="studio-lesson-num">${i + 1}</span> ${esc(l.title)} <span class="studio-item-meta">(${l.questions.length} ${isAr ? "سؤال" : "questions"})</span></summary>
                <div class="learn-content">${l.content.split(/\n{2,}/).map(p => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join("")}</div>
                ${l.questions.map(q => `
                  <div class="studio-question-card">
                    <div class="studio-question-head"><span class="badge badge--neutral">${q.type === "true_false" ? "T/F" : "MCQ"}</span><p>${esc(q.prompt)}</p></div>
                    <ul class="studio-question-options">
                      ${q.options.map((o, oi) => `<li class="${oi === q.correctIndex ? "studio-option--correct" : ""}">${oi === q.correctIndex ? iconSvg("check", "btn-icon") : ""} ${esc(o)}</li>`).join("")}
                    </ul>
                    ${q.explanation ? `<p class="form-hint">${esc(q.explanation)}</p>` : ""}
                  </div>`).join("")}
              </details>`).join("")}
          </div>
          ${c.status === "pending_review" ? `
          <div class="modal-actions">
            <button class="btn btn-outline" id="rev-reject">${isAr ? "إرجاع بملاحظات" : "Return with feedback"}</button>
            <button class="btn btn-primary" id="rev-approve">${iconSvg("check", "btn-icon")} ${isAr ? "اعتماد ونشر" : "Approve & publish"}</button>
          </div>` : ""}
        </div>`;
      document.body.appendChild(backdrop);
      const close = () => backdrop.remove();
      backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
      backdrop.querySelector(".modal-close")?.addEventListener("click", close);
      backdrop.querySelector("#rev-approve")?.addEventListener("click", async () => {
        const note = await promptModal({ title: isAr ? "اعتماد المنهج" : "Approve curriculum", label: isAr ? "ملاحظة للمعلم" : "Note to the tutor", optional: true, confirmLabel: isAr ? "اعتماد" : "Approve" });
        if (note === null) return;
        try { await post(`/api/admin/curricula/${id}/review`, { decision: "approve", note }); close(); loadAdminCurricula(); }
        catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      });
      backdrop.querySelector("#rev-reject")?.addEventListener("click", async () => {
        const note = await promptModal({ title: isAr ? "إرجاع المنهج" : "Return curriculum", label: isAr ? "ما الذي يجب تعديله؟" : "What needs to change?", multiline: true, confirmLabel: isAr ? "إرجاع" : "Return" });
        if (!note) return;
        try { await post(`/api/admin/curricula/${id}/review`, { decision: "reject", note }); close(); loadAdminCurricula(); }
        catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
      });
    } catch (err) { showSiteError(err instanceof Error ? err.message : ""); }
  };

  document.querySelectorAll<HTMLButtonElement>("#tab-curricula .chip").forEach(chip => chip.addEventListener("click", () => {
    document.querySelectorAll("#tab-curricula .chip").forEach(x => x.classList.remove("chip--active"));
    chip.classList.add("chip--active");
    adminCurriculaFilter = chip.dataset.filter!;
    loadAdminCurricula();
  }));

  // ── Tutor leave overview ──
  const loadAdminLeaves = async () => {
    const listEl = document.getElementById("admin-leaves-list") as HTMLElement | null;
    if (!listEl) return;
    try {
      const data = await get("/api/admin/leaves") as {
        leaves: Array<{ id: string; startsAt: string; endsAt: string; reason: string | null; isActive: boolean; tutor: { name: string; email: string } }>
      };
      listEl.innerHTML = data.leaves.length ? data.leaves.map(l => `
        <div class="leave-item${l.isActive ? " leave-item--active" : ""}">
          <div class="leave-item-info">
            ${iconSvg("plane", "leave-item-icon")}
            <div>
              <strong>${esc(l.tutor.name)}</strong>
              <span class="leave-item-reason">${esc(l.tutor.email)}</span>
              <span class="leave-item-reason">${formatDate(l.startsAt, lang)} → ${formatDate(l.endsAt, lang)}${l.reason ? ` · ${esc(l.reason)}` : ""}</span>
            </div>
          </div>
          <span class="badge ${l.isActive ? "badge--warning" : "badge--neutral"}">${l.isActive ? (isAr ? "في إجازة الآن" : "On leave now") : (isAr ? "مجدولة" : "Scheduled")}</span>
        </div>`).join("")
        : `<p class="dash-empty">${isAr ? "لا توجد إجازات قادمة" : "No upcoming leave"}</p>`;
    } catch { /* */ }
  };

  document.getElementById("users-search")?.addEventListener("input", (e) => {
    loadUsers((e.target as HTMLInputElement).value.trim());
  });

  document.querySelectorAll<HTMLButtonElement>(".dash-tab").forEach(tab => {
    if (tab.dataset.tab === "users") {
      tab.addEventListener("click", () => { loadUsers(); }, { once: true });
    }
    if (tab.dataset.tab === "messages") {
      tab.addEventListener("click", () => { loadFlaggedMessages(); }, { once: true });
    }
    if (tab.dataset.tab === "sessions") {
      tab.addEventListener("click", () => { loadAdminSessions(); }, { once: true });
    }
    if (tab.dataset.tab === "leaves") {
      tab.addEventListener("click", () => { loadAdminLeaves(); }, { once: true });
    }
    if (tab.dataset.tab === "curricula") {
      tab.addEventListener("click", () => { loadAdminCurricula(); }, { once: true });
    }
  });

  Promise.all([loadStats(), loadApplications(), loadDisputes(), loadOverviewQuickLists()]);
};

// ── Messages ──────────────────────────────────────────────────────────────────

const messageSafetyNote = (isAr: boolean): string => isAr
  ? "حفاظًا على سلامتك ووفقًا لشروط الاستخدام، أبقِ كل التواصل والدفع داخل سلام نتورك. لا تشارك أرقام الهاتف أو البريد أو الروابط الخارجية."
  : "For your safety and per our Terms, keep all communication and payments on Salam Network. Never share phone numbers, emails, or external links.";

const flagReasonLabel = (reason: string, isAr: boolean): string => {
  const map: Record<string, [string, string]> = {
    phone: ["a phone number", "رقم هاتف"],
    email: ["an email address", "بريد إلكتروني"],
    link: ["an external link", "رابط خارجي"],
    "off-platform contact": ["off-platform contact details", "وسيلة تواصل خارجية"],
  };
  const entry = map[reason] ?? [reason, reason];
  return isAr ? entry[1] : entry[0];
};

let activeMessagingPoll: ReturnType<typeof setInterval> | null = null;
let activeThreadsPoll: number | null = null;

const mountMessaging = async (opts?: { preselectUserId?: string | null }) => {
  const threadsEl = document.getElementById("messages-threads");
  const convEl = document.getElementById("messages-conversation");
  if (!threadsEl || !convEl) return;

  const lang = document.documentElement.lang === "ar" ? "ar" : "en";
  const isAr = lang === "ar";
  if (!currentUser) await loadCurrentUser();

  let selectedUserId: string | null = opts?.preselectUserId ?? null;
  let threadSearch = "";
  let cachedThreads: Array<{ userId: string; name: string; role?: string; lastMessage: string; unreadCount: number }> = [];
  if (activeMessagingPoll) { clearInterval(activeMessagingPoll); activeMessagingPoll = null; }
  if (activeThreadsPoll) { clearInterval(activeThreadsPoll); activeThreadsPoll = null; }

  const renderThreads = () => {
    const q = threadSearch.trim().toLowerCase();
    const visible = q ? cachedThreads.filter(t => t.name.toLowerCase().includes(q)) : cachedThreads;
    const roleLabel = (role?: string) => !role ? "" :
      role === "tutor" ? (isAr ? "معلم" : "Tutor") :
      role === "student" ? (isAr ? "طالب" : "Student") :
      (isAr ? "فريق المنصة" : "Support");
    threadsEl.innerHTML = `
      <div class="messages-search">
        ${iconSvg("search", "messages-search-icon")}
        <input type="search" id="threads-search" class="messages-search-input" placeholder="${isAr ? "ابحث في المحادثات..." : "Search conversations…"}" value="${esc(threadSearch)}" />
      </div>
      <div class="messages-thread-list">
      ${cachedThreads.length === 0
        ? `<div class="messages-empty-state">${iconSvg("message", "messages-empty-icon")}<strong>${isAr ? "لا توجد محادثات بعد" : "No conversations yet"}</strong><span>${isAr ? "بمجرد حجز درس، سيظهر معلمك أو طالبك هنا تلقائياً." : "As soon as a lesson is booked, your tutor or student appears here automatically."}</span></div>`
        : visible.length === 0
          ? `<div class="messages-empty-state"><span>${isAr ? "لا نتائج مطابقة" : "No matches"}</span></div>`
          : visible.map(t => `
            <button class="message-thread${t.userId === selectedUserId ? " message-thread--active" : ""}" data-user="${esc(t.userId)}">
              <span class="thread-avatar">${esc(t.name.slice(0, 1).toUpperCase())}</span>
              <span class="thread-main">
                <span class="thread-name">${esc(t.name)}${t.role ? `<span class="thread-role">${roleLabel(t.role)}</span>` : ""}</span>
                <span class="thread-last">${t.lastMessage ? esc(t.lastMessage) : `<em>${isAr ? "لا رسائل بعد — رحّب!" : "No messages yet — say salam!"}</em>`}</span>
              </span>
              ${t.unreadCount > 0 ? `<span class="thread-badge">${t.unreadCount}</span>` : ""}
            </button>`).join("")}
      </div>`;

    const searchInput = document.getElementById("threads-search") as HTMLInputElement | null;
    searchInput?.addEventListener("input", () => {
      threadSearch = searchInput.value;
      const pos = searchInput.selectionStart;
      renderThreads();
      const again = document.getElementById("threads-search") as HTMLInputElement | null;
      if (again) { again.focus(); if (pos !== null) again.setSelectionRange(pos, pos); }
    });
    threadsEl.querySelectorAll<HTMLButtonElement>(".message-thread").forEach(btn => {
      btn.addEventListener("click", () => { selectedUserId = btn.dataset.user!; loadConversation(selectedUserId); void loadThreads(); });
    });
  };

  const loadThreads = async () => {
    try {
      const data = await get("/api/messages/threads") as { threads: Array<{ userId: string; name: string; role?: string; lastMessage: string; unreadCount: number }> };
      cachedThreads = data.threads;
      renderThreads();
    } catch { /* */ }
  };

  const showFlagWarning = (reasons: string[]) => {
    const note = document.getElementById("conv-flag-warning");
    if (!note) return;
    note.textContent = (isAr ? "تم إخفاء جزء من رسالتك لاحتوائها على " : "Part of your message was hidden because it contained ")
      + reasons.map(r => flagReasonLabel(r, isAr)).join(isAr ? "، " : ", ") + ".";
    note.classList.remove("hidden");
    setTimeout(() => note?.classList.add("hidden"), 7000);
  };

  const QUICK_EMOJI = ["😊", "🤲", "👍", "❤️", "🎉", "📖", "🕌", "⭐", "✅", "🌙"];
  let lastConvSignature = "";

  const loadConversation = async (userId: string, opts?: { force?: boolean }) => {
    try {
      const data = await get(`/api/messages?with=${encodeURIComponent(userId)}`) as {
        messages: Array<{ id: string; content: string; fromUserId: string; sentAt: string; isRead?: boolean; isFlagged?: boolean }>;
        otherUser: { id: string; name: string; role?: string; avatarUrl?: string | null };
      };

      // Skip the re-render entirely when nothing changed — keeps scroll
      // position, focus, and selection stable while polling.
      const signature = `${userId}:` + data.messages.map(m => m.id + (m.isRead ? "r" : "")).join("|");
      if (!opts?.force && convEl.dataset.convUser === userId && signature === lastConvSignature) return;
      lastConvSignature = signature;

      const prevList = document.getElementById("conv-messages-list");
      const nearBottom = !prevList || (prevList.scrollHeight - prevList.scrollTop - prevList.clientHeight < 80);
      const prevScroll = prevList?.scrollTop ?? 0;
      const prevInput = document.getElementById("conv-input") as HTMLTextAreaElement | null;
      const draft = prevInput?.value ?? "";
      const hadFocus = prevInput === document.activeElement;

      const roleLabel = data.otherUser.role === "tutor" ? (isAr ? "معلم" : "Tutor")
        : data.otherUser.role === "student" ? (isAr ? "طالب" : "Student")
        : (isAr ? "فريق المنصة" : "Support");
      const profileLink = data.otherUser.role === "tutor"
        ? `<a class="conv-header-link" href="/tutors/${esc(data.otherUser.id)}${isAr ? "?lang=ar" : ""}">${isAr ? "عرض الملف" : "View profile"}</a>`
        : "";

      const GROUP_WINDOW_MS = 5 * 60 * 1000;
      const messagesHtml = data.messages.length > 0 ? (() => {
        let lastDay = "";
        let prevFrom = "";
        let prevAt = 0;
        return data.messages.map(m => {
          const mine = m.fromUserId === currentUser?.id;
          const at = new Date(m.sentAt).getTime();
          const day = new Date(m.sentAt).toDateString();
          const separator = day !== lastDay
            ? `<div class="conv-day-sep"><span>${new Date(m.sentAt).toLocaleDateString(isAr ? "ar-SA" : "en-GB", { weekday: "long", day: "numeric", month: "long" })}</span></div>`
            : "";
          const grouped = !separator && m.fromUserId === prevFrom && at - prevAt < GROUP_WINDOW_MS;
          lastDay = day; prevFrom = m.fromUserId; prevAt = at;
          const time = new Date(m.sentAt).toLocaleTimeString(isAr ? "ar" : "en-GB", { hour: "2-digit", minute: "2-digit" });
          const receipt = mine
            ? `<span class="conv-receipt${m.isRead ? " conv-receipt--read" : ""}" title="${m.isRead ? (isAr ? "تمت القراءة" : "Read") : (isAr ? "أُرسلت" : "Sent")}">${m.isRead ? "✓✓" : "✓"}</span>`
            : "";
          return `${separator}
          <div class="conv-msg${mine ? " conv-msg--mine" : ""}${grouped ? " conv-msg--grouped" : ""}${m.isFlagged ? " conv-msg--flagged" : ""}">
            <p>${esc(m.content).replace(/\n/g, "<br>")}</p>
            <span class="conv-msg-meta">
              <span class="conv-msg-time">${time}</span>
              ${receipt}
              ${!mine ? `<button class="conv-msg-report" data-id="${esc(m.id)}" title="${isAr ? "إبلاغ" : "Report"}" aria-label="${isAr ? "إبلاغ عن الرسالة" : "Report message"}">${iconSvg("flag", "conv-report-icon")}</button>` : ""}
            </span>
          </div>`;
        }).join("");
      })() : `<div class="messages-empty-state messages-empty-state--conversation">${iconSvg("message", "messages-empty-icon")}<strong>${isAr ? "ابدأ المحادثة" : "Start the conversation"}</strong><span>${isAr ? "اكتب أول رسالة بالأسفل." : "Write the first message below."}</span></div>`;

      convEl.dataset.convUser = userId;
      convEl.innerHTML = `
        <div class="conv-header">
          <span class="thread-avatar">${data.otherUser.avatarUrl ? `<img src="${esc(data.otherUser.avatarUrl)}" alt="" />` : esc(data.otherUser.name.slice(0, 1).toUpperCase())}</span>
          <div class="conv-header-main">
            <strong>${esc(data.otherUser.name)}</strong>
            <span class="conv-header-role">${roleLabel}</span>
          </div>
          ${profileLink}
        </div>
        <div class="conv-safety">${iconSvg("shield", "conv-safety-icon")}<span>${messageSafetyNote(isAr)}</span></div>
        <div class="conv-messages" id="conv-messages-list">${messagesHtml}</div>
        <div class="conv-flag-warning hidden" id="conv-flag-warning" role="alert"></div>
        <div class="conv-emoji-strip hidden" id="conv-emoji-strip">
          ${QUICK_EMOJI.map(e => `<button type="button" class="conv-emoji" data-emoji="${e}">${e}</button>`).join("")}
        </div>
        <form class="conv-form conv-form--rich" id="conv-form">
          <button type="button" class="conv-emoji-toggle" id="conv-emoji-toggle" aria-label="Emoji">🙂</button>
          <textarea id="conv-input" class="conv-textarea" rows="1" maxlength="2000"
            placeholder="${isAr ? "اكتب رسالة... (Enter للإرسال، Shift+Enter لسطر جديد)" : "Write a message… (Enter to send, Shift+Enter for a new line)"}"></textarea>
          <button type="submit" class="btn btn-primary conv-send" aria-label="${isAr ? "إرسال" : "Send"}">${iconSvg("send", "btn-icon")}</button>
        </form>
      `;

      const list = document.getElementById("conv-messages-list");
      if (list) list.scrollTop = nearBottom ? list.scrollHeight : prevScroll;

      const input = document.getElementById("conv-input") as HTMLTextAreaElement;
      if (draft) input.value = draft;
      if (hadFocus) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }

      const autoGrow = () => {
        input.style.height = "auto";
        input.style.height = `${Math.min(input.scrollHeight, 132)}px`;
      };
      input.addEventListener("input", autoGrow);
      autoGrow();
      // Enter sends; Shift+Enter inserts a newline.
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          document.getElementById("conv-form")?.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
        }
      });

      document.getElementById("conv-emoji-toggle")?.addEventListener("click", () =>
        document.getElementById("conv-emoji-strip")?.classList.toggle("hidden"));
      convEl.querySelectorAll<HTMLButtonElement>(".conv-emoji").forEach(btn => btn.addEventListener("click", () => {
        const start = input.selectionStart ?? input.value.length;
        input.value = input.value.slice(0, start) + btn.dataset.emoji + input.value.slice(input.selectionEnd ?? start);
        input.focus();
        const pos = start + (btn.dataset.emoji?.length ?? 0);
        input.setSelectionRange(pos, pos);
        autoGrow();
      }));

      convEl.querySelectorAll<HTMLButtonElement>(".conv-msg-report").forEach(btn => {
        btn.addEventListener("click", async () => {
          if (!await confirmModal({ title: isAr ? "إبلاغ عن رسالة" : "Report message", message: isAr ? "سيراجع فريق الإشراف هذه الرسالة." : "The moderation team will review this message.", confirmLabel: isAr ? "إبلاغ" : "Report" })) return;
          try {
            await post(`/api/messages/${btn.dataset.id}/report`, {});
            btn.disabled = true;
            btn.classList.add("conv-msg-report--done");
          } catch { /* */ }
        });
      });

      document.getElementById("conv-form")?.addEventListener("submit", async (e) => {
        e.preventDefault();
        const text = input.value.trim();
        if (!text) return;
        const sendBtn = convEl.querySelector(".conv-send") as HTMLButtonElement | null;
        if (sendBtn) sendBtn.disabled = true;
        try {
          const resp = await post("/api/messages", { toUserId: userId, content: text }) as { warning?: { reasons: string[] } | null };
          input.value = "";
          await loadConversation(userId, { force: true });
          if (resp.warning) showFlagWarning(resp.warning.reasons);
          void loadThreads();
        } catch (err) {
          if (sendBtn) sendBtn.disabled = false;
          showSiteError(err instanceof Error ? err.message : (isAr ? "تعذّر إرسال الرسالة." : "Could not send message."));
        }
      });

      if (activeMessagingPoll) clearInterval(activeMessagingPoll);
      activeMessagingPoll = setInterval(() => {
        if (selectedUserId !== userId) return;
        loadConversation(userId);
      }, 10000);
    } catch { /* */ }
  };

  await loadThreads();
  // Keep the thread list fresh (new bookings / incoming messages).
  activeThreadsPoll = window.setInterval(() => {
    const search = document.getElementById("threads-search") as HTMLInputElement | null;
    if (search && document.activeElement === search) return;
    void loadThreads();
  }, 30000);
  if (selectedUserId) await loadConversation(selectedUserId);
};

const initMessages = async () => {
  if (!currentUser) await loadCurrentUser();
  updateNavAuth(currentUser);
  if (!currentUser) { window.location.href = `/login${document.documentElement.lang === "ar" ? "?lang=ar" : ""}`; return; }
  const params = new URLSearchParams(window.location.search);
  await mountMessaging({ preselectUserId: params.get("to") });
};

// Decorate the nav "Messages" link with an unread badge for signed-in users.
const decorateNavUnread = async () => {
  if (!currentUser) return;
  try {
    const data = await get("/api/messages/unread/count") as { count: number };
    if (!data.count) return;
    document.querySelectorAll<HTMLAnchorElement>('.nav-link[href^="/messages"]').forEach(link => {
      if (link.querySelector(".nav-unread-badge")) return;
      const badge = document.createElement("span");
      badge.className = "nav-unread-badge";
      badge.textContent = data.count > 9 ? "9+" : String(data.count);
      link.appendChild(badge);
    });
  } catch { /* */ }
};

// ── Boot ──────────────────────────────────────────────────────────────────────

const boot = async () => {
  const hideLoading = () => document.getElementById("app-loading-screen")?.classList.add("app-loading-screen--hidden");
  try {
    initNav();

    const page = document.body.dataset.page;

    // Every page shows the signed-in state in the nav (help, terms, home included).
    await loadCurrentUser();
    updateNavAuth(currentUser);
    // Server-side theme preference wins over the local cache (syncs devices).
    if (currentUser?.theme) applyTheme(currentUser.theme);

    switch (page) {
      case "home":              await initHome(); break;
      case "login":             initLogin(); break;
      case "signup":            initSignup(); break;
      case "forgot-password":   initForgotPassword(); break;
      case "reset-password":    initResetPassword(); break;
      case "onboarding":        initOnboarding(); break;
      case "tutor-listing":     initTutorListing(); break;
      case "tutor-profile":     initTutorProfile(); break;
      case "booking":           initBooking(); break;
      case "student-dashboard": initStudentDashboard(); break;
      case "tutor-dashboard":   initTutorDashboard(); break;
      case "admin-dashboard":   initAdminDashboard(); break;
      case "messages":          await initMessages(); break;
    }
    void decorateNavUnread();
  } catch (err) {
    console.error(err);
    showSiteError(document.documentElement.lang === "ar" ? "حدث خطأ أثناء تحميل الصفحة." : "There was a problem loading this page.");
  } finally {
    window.setTimeout(hideLoading, 180);
  }
};

document.addEventListener("DOMContentLoaded", boot);
